import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { type LocaleCode, isLocaleCode, DEFAULT_LOCALE } from './locales';

// Local storage key for language persistence
const STORAGE_KEY = 'lang';

const savedLang = ((): LocaleCode => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (isLocaleCode(v)) return v;
    return DEFAULT_LOCALE;
  } catch {
    return DEFAULT_LOCALE;
  }
})();

// Minimal bootstrap resources; full dictionaries live in separate files and are merged here
const resources = {
  en: {
    howItWorks: {
      title: 'How to Use OurScooter (For Staff)',
      subtitle: 'Simple steps, clear rules, and useful tips to do your work properly and efficiently.',
      categories: 'Categories',
      whyTitle: 'Why OurScooter: purpose and benefits',
      whyDescription: 'OurScooter helps staff run daily scooter rental operations efficiently: fewer clicks, fewer mistakes, and clear records.',
      whySpeed: 'Speed: create contracts in seconds, reuse details through duplication.',
      whyClarity: 'Clarity: statuses show where each rental stands at a glance.',
      whyControl: 'Control: filter, search, and switch views (table/kanban) to fit the task.',
      whyAccuracy: 'Accuracy: guardrails prevent invalid moves and overlapping rentals.',
      signingInTitle: 'Signing in and staying signed in',
      signingInToken: 'Username + password → the server verifies and returns access + refresh tokens.',
      signingInAutoRefresh: 'Access tokens are short-lived; we refresh them silently using the refresh token.',
      signingInRoles: 'Roles: admin and staff. The org context is selected in the sidebar.',
      signingInSession: 'Session length: You stay signed in for up to 1 day of inactivity. The app uses short 15-minute access tokens and renews them in the background. After 1 day of not using the app (or if cookies are cleared), you will be asked to sign in again.',
      scootersTitle: 'Scooters: adding and editing',
      scootersOpen: 'Open "Add Scooter" to register a new vehicle. Keep the details short and accurate.',
      scootersRequired: 'Required: brand/model, status.',
      scootersOptional: 'Optional: purchase cost, fuel/battery details, notes.',
      scootersTip: 'Tip: the "maintenance" status removes a scooter from availability until it is returned.',
      clientsTitle: 'Clients: adding and editing',
      clientsOpen: 'Open "New Client". Capture what helps you find them later.',
      clientsRequired: 'Required: full name.',
      clientsOptional: 'Optional: phone, email, ID/passport, notes.',
      contractsTitle: 'Contracts: creating and managing',
      contractsWaysTitle: 'Ways to create a contract (4 options)',
      contractsFromContracts: 'From Contracts: press "Add Contract".',
      contractsFromScooters: 'From Scooters: open a scooter → "New Contract".',
      contractsFromClients: 'From Clients: open a client → "New Contract".',
      contractsDuplicate: 'Duplicate: open an existing contract → "Duplicate" to safely reuse the details.',
      contractsRequiredTitle: 'Required fields',
      contractsRequiredClient: 'Client and Scooter.',
      contractsRequiredDate: 'Start date/time.',
      contractsRequiredRate: 'Daily rate. (Deposit/payment method are optional.)',
      contractsStatusTitle: 'Statuses and workflow (what is allowed)',
      contractsStatusFlow: 'Draft → Active → Closed → Archived.',
      contractsStatusActivate: 'You can only activate when the required fields are set and the scooter is available.',
      contractsStatusClosed: 'From Closed you can only move to Archived (you cannot go back to Active).',
      contractsStatusForbidden: 'Not allowed: overlapping Active contracts for the same scooter.',
      contractsStatusArchived: 'Archived contracts disappear from default views. Toggle "Include archived" to see them.',
      contractsAutoTitle: 'Automatic scooter status updates',
      contractsAutoCreate: 'Creating a contract does not change the scooter status (regardless of contract status).',
      contractsAutoActive: 'When a contract becomes Active → the scooter automatically becomes "rented".',
      contractsAutoClosed: 'When an Active contract is Closed/Archived → the scooter automatically becomes "available".',
      contractsAutoDraft: 'When an Active contract is reverted to Draft → the scooter automatically becomes "available".',
      contractsAutoPrevent: 'This prevents double-bookings and keeps scooter availability accurate.',
      contractsTimeTitle: 'Time tracking for Active contracts',
      contractsTimeRemaining: 'Active contracts show the time remaining until the end date (e.g., "2h 30m left").',
      contractsTimeOverdue: 'Overdue contracts show the elapsed time (e.g., "Overdue 1d 5h").',
      contractsTimeColors: 'Colors indicate urgency: Green (4+ hours), Yellow (1-4 hours), Orange (<1 hour), Red (overdue).',
      contractsTimeKanban: 'In Kanban view: only a timer is shown for Active contracts.',
      contractsTimeList: 'In List view: both the status badge and timer are shown for Active contracts.',
      contractsViewsTitle: 'Contracts: views',
      contractsViewsTable: 'Table view: quickly scan, filter, and export rows.',
      contractsViewsKanban: 'Kanban board: visual workflow by status (Draft, Active, Closed, Archived).',
      contractsViewsToggle: 'Use the view toggle in the Contracts toolbar to switch views.',
      contractsKanbanTitle: 'Working on the Kanban board',
      contractsKanbanDrag: 'Drag & drop a card to move it to the next allowed status.',
      contractsKanbanEdit: 'Open a card and press Edit to change the details or status directly.',
      contractsKanbanRules: 'Rules apply: you cannot move from Closed back to Active; Archived is final.',
      contractsKanbanBlocked: 'Unavailable moves are blocked (e.g., the scooter is already Active elsewhere).',
      contractsKanbanFilters: 'Use the filters to focus on one client or include Archived items when needed.',
      findingTitle: 'Quick finding (filter & search)',
      findingScooters: 'Scooters: filter by status, brand, model. Use the search box for quick text matches.',
      findingClients: 'Clients/Users: search by name/email; adjust the page size to see more rows.',
      findingContracts: 'Contracts: filter by status; optionally include archived.',
      rowsTitle: 'How many rows do we show?',
      rowsScooters: 'Scooters: load in pages and fetch more when needed (up to 100 per load).',
      rowsClients: 'Clients/Users: you can pick 10/20/30/40/50 rows per page.',
      rowsContracts: 'Contracts: show current org contracts; include archived to expand the list.',
      themesTitle: 'Themes and defaults',
      themesChange: 'Change the theme (Dark/Light/Blue/Wood) in the bottom-left sidebar.',
      themesAdmin: 'Admins can configure organization defaults in Management (e.g., currency, rates).',
      usersTitle: 'Users & Management (admins)',
      usersStaff: 'Staff: daily operations, view and create contracts, manage scooters/clients.',
      usersAdmin: 'Admin: everything staff can do, plus org-level settings and deletions.',
      tipsTitle: 'Tips & troubleshooting',
      tipsActivate: 'Cannot activate? Check the scooter status (must be available) and the required fields.',
      tipsContract: 'Cannot see a contract? It may be archived; enable "Include archived".',
      tipsSession: 'Session ended? If it has been more than a day, just sign in again.'
    },
    booking: {
      orgNotFound: 'Organization not found',
      orgNotFoundDesc: "The booking page you're looking for doesn't exist. This could mean:",
      orgNotFoundReason1: 'The organization slug is incorrect',
      orgNotFoundReason2: "The organization hasn't set up a booking page yet",
      orgNotFoundReason3: 'The booking page has been removed',
      orgNotFoundOwner: "If you're the organization owner, make sure you've configured your organization slug in Management → Technical Details.",
      requestReceived: 'Request Received!',
      requestReceivedDesc: 'Your booking request has been received. This is not a confirmed booking.',
      willContact: '{{business}} will contact you shortly to confirm your rental.',
      requestIdLabel: 'Request ID:',
      contactLabel: 'Contact:',
      showroomBadge: 'Scooter Showroom',
      bookBadge: 'Book a Scooter',
      showroomIntro: "Browse our scooters, pick the one you like, and send a booking request. We'll confirm with you shortly.",
      bookIntro: "Submit your booking request below and we'll contact you shortly to confirm.",
      yourDetailsStep: '3. Your Details',
      yourInformation: 'Your Information',
      fullName: 'Full Name',
      fullNamePlaceholder: 'Enter full name',
      email: 'Email',
      emailPlaceholder: 'Enter email address',
      localPhone: 'Local Phone Number',
      phonePlaceholder: 'Phone number',
      messengerNumber: 'Messenger Number (WhatsApp, etc.)',
      messengerPlaceholder: 'WhatsApp number (optional)',
      sameAsPhone: 'Same as phone',
      lineId: 'LINE ID',
      lineIdPlaceholder: 'LINE ID (optional)',
      passportNumber: 'Passport Number',
      passportPlaceholder: 'Passport number (optional)',
      nationality: 'Nationality',
      nationalityPlaceholder: 'Nationality (optional)',
      localAddress: 'Local Address',
      localAddressPlaceholder: 'Local address in the country (optional)',
      rentalDatesStep: '1. Rental Dates',
      bookingDetails: 'Booking Details',
      startDate: 'Start Date',
      duration: 'Duration',
      endDate: 'End Date',
      dayUnit: 'day',
      daysUnit: 'days',
      weekUnit: 'week',
      weeksUnit: 'weeks',
      monthUnit: 'month',
      monthsUnit: 'months',
      scooterUnit: 'scooter',
      scootersUnit: 'scooters',
      pricingSummary: 'Pricing Summary',
      lineDailyRate: '{{count}} {{unit}} × Daily Rate:',
      lineWeeklyRate: '{{count}} {{unit}} × Weekly Rate:',
      lineDailyFromWeekly: '{{count}} {{unit}} × Daily Rate (from weekly):',
      lineMonthlyRate: '{{count}} {{unit}} × Monthly Rate:',
      lineDailyFromMonthly: '{{count}} {{unit}} × Daily Rate (from monthly):',
      subtotal: 'Subtotal:',
      deposit: 'Deposit:',
      total: 'Total:',
      selectToSeePricingShowroom: 'Select dates and a scooter to see pricing',
      selectToSeePricingGroup: 'Select dates and pricing group to see pricing',
      selectPricingGroup: 'Select Pricing Group',
      perDay: '/day',
      perWeek: '/week',
      perMonth: '/month',
      specialPromotions: 'Special Promotions:',
      scooterPreference: 'Scooter Preference',
      noPreference: 'No preference',
      scooterFallback: 'Scooter',
      chooseScooterStep: '2. Choose Your Scooter',
      checkingAvailability: 'Checking availability for your dates…',
      availableForDates: '{{available}} of {{total}} available for your dates',
      fleetCount: '{{count}} {{unit}} in our fleet',
      lockedScooterNote: 'This scooter was reserved for you via your booking link',
      noScootersAvailable: 'No scooters are currently available. Please contact us directly.',
      available: 'Available',
      unavailable: 'Unavailable',
      photosCount: '{{count}} photos',
      viewPhoto: 'View',
      viewPhotosAria: 'View photos of {{title}}',
      additionalNotes: 'Additional Notes',
      notesPlaceholder: 'Any special requests or information...',
      charactersCount: '{{count}}/255 characters',
      preSignTitle: '⚡ Pre-Sign Contract (Recommended)',
      preSignDesc: 'Speed up your pickup process! Pre-signing your contract saves time when you arrive.',
      preSignBenefits: 'No payment required • Not a commitment • Just speeds up pickup',
      completeFieldsToSign: 'Complete all required fields above to enable contract pre-signing.',
      missingLabel: 'Missing:',
      signatureSaved: 'Signature saved: {{name}}',
      submitting: 'Submitting...',
      submitRequest: 'Submit Booking Request',
      disclaimer: 'By submitting this form, you understand this is a request, not a confirmed booking. The shop will contact you to confirm availability and complete the booking.',
      selectDatesForPrice: 'Select your dates to see the price',
      depositShort: 'deposit',
      requestToBook: 'Request to Book',
      continueBtn: 'Continue',
      closeBtn: 'Close',
      prevPhoto: 'Previous photo',
      nextPhoto: 'Next photo',
      langLabel: 'Language',
      toastLoadFailed: 'Failed to load booking page',
      toastVersionFailed: 'Failed to generate contract version. Please try again.',
      toastPreviewFailed: 'Failed to load contract preview',
      toastEnterName: 'Please enter your full name',
      toastEnterEmail: 'Please enter your email address',
      toastEnterPhone: 'Please enter your phone number',
      toastSelectStart: 'Please select a start date',
      toastSelectScooter: 'Please select a scooter',
      toastSelectPricingGroup: 'Please select a pricing group',
      toastPricingFailed: 'Unable to calculate pricing',
      toastSubmitFailed: 'Failed to submit booking request. Please try again.',
      errFullNameRequired: 'Full name is required',
      errEmailRequired: 'Email is required',
      errEmailInvalid: 'Email is invalid',
      errPhoneRequired: 'Phone number is required',
      mfFullName: 'Full Name',
      mfEmail: 'Email',
      mfPhone: 'Phone Number',
      mfStartDate: 'Start Date',
      mfScooter: 'Scooter',
      mfPricingGroup: 'Pricing Group',
      mfPricingInfo: 'Pricing information'
    },
    theme: {
      dark: 'Dark',
      light: 'Light',
      blue: 'Blue',
      wood: 'Wood'
    },
    home: {
      quickActions: 'Quick Actions',
      recentActivity: 'Recent Activity',
      fleetStatusOverview: 'Fleet Status Overview',
      welcomeTitle: 'Welcome to OurScooters',
      welcomeSubtitle: 'Your comprehensive scooter fleet management dashboard',
      addScooter: 'New Scooter',
      registerScooter: 'Register new scooter',
      addClient: 'New Client',
      registerClient: 'Register new customer',
      newContract: 'New Contract',
      createAgreement: 'Create rental agreement',
      org: 'Org',
      allOrgs: 'All orgs',
      availableScooters: 'Available Scooters',
      readyForRental: 'Ready for rental',
      rentedScooters: 'Rented Scooters',
      currentlyInUse: 'Currently in use',
      maintenance: 'Maintenance',
      underRepair: 'Under repair',
      fleetOverview: 'Fleet Overview',
      available: 'available',
      rented: 'rented',
      activeRevenue: 'Active Revenue',
      monthlyRecurring: 'Monthly recurring revenue',
      totalClients: 'Total Clients',
      activeContracts: 'active contracts',
      avgBattery: 'Avg Battery',
      fleetBatteryHealth: 'Fleet battery health',
      avgFuel: 'Avg Fuel',
      fuel: 'fuel',
      electric: 'electric',
      fleetROI: 'Fleet ROI',
      rev: 'rev',
      cost: 'cost',
      inMaintenance: 'in maintenance',
      newClientRegistered: 'New client',
      registered: 'registered'
    },
    users: {
      title: 'Users',
      errorLoading: 'Failed to load users.',
      id: 'ID',
      email: 'Email',
      role: 'Role',
      username: 'Username',
      org: 'Org',
      roleSuperadmin: 'Superadmin',
      roleAdmin: 'Admin',
      roleStaff: 'Staff'
    },
    management: {
      title: 'Organization Management',
      errorLoading: 'Error loading settings.',
      timeFormat: 'Time Format',
      defaultCurrency: 'Default Currency',
      defaultCountryCode: 'Default Country Code',
      defaultDailyPrice: 'Default Daily Price',
      defaultDeposit: 'Default Contract Deposit',
      defaultRentLength: 'Default Rent Length (Days)',
      defaultPaymentMethod: 'Default Payment Method',
      buttonStyle: 'Button Style',
      buttonStyleDesc: 'Choose the visual style for all buttons throughout the application.',
      countryCodeHint: '1 for USA, 44 for UK, etc.',
      countryCodeExplain: 'Digits only. Will prefix phone numbers in client forms.',
      saving: 'Saving...',
      saveSettings: 'Save Settings',
      saved: 'Saved',
      modernStyle: 'Modern Style',
      classicStyle: 'Classic Style',
      originalStyle: 'Original Style',
      btnPrimary: 'Primary',
      btnSecondary: 'Secondary',
      btnGhost: 'Ghost',
      btnDanger: 'Danger',
      modernDesc: 'Gradient backgrounds with micro-interactions and rounded corners',
      classicDesc: 'Clean, minimal design with theme-aware colors and subtle borders',
      originalDesc: 'The original button design you liked before refactoring',
      timeFormatExample1: '15 September 2025 6:09 PM',
      timeFormatExample2: 'September 15 2025 6:09 PM',
      timeFormatExample3: '15 September 2025 18:09',
      timeFormatExample4: 'September 15 2025 18:09'
    },
    common: {
      language: 'Language',
      phoneTooShort: 'Phone number must be at least 7 digits',
      phoneTooLong: 'Phone number too long (maximum 15 digits)',
      theme: 'Theme',
      save: 'Save',
      cancel: 'Cancel',
      logout: 'Logout',
      loading: 'Loading...',
      reset: 'Reset',
      close: 'Close',
      back: 'Back',
      delete: 'Delete',
      clearAll: 'Clear All',
      upload: 'Upload',
      uploading: 'Uploading...',
      selectPhoto: 'Select Photo',
      addNotes: 'Add notes...',
      notes: 'Notes',
      optional: 'Optional',
      retake: 'Retake',
      usePhoto: 'Use Photo',
      viewAll: 'View All Photos',
      confirmDelete: 'Are you sure you want to delete this photo?',
      add: 'Add',
      copied: 'Copied',
      expand: 'Expand',
      collapse: 'Collapse',
      updateFailed: 'Update failed',
      updateFailedWithMessage: 'Update failed: {{message}}',
      failedToSendEmail: 'Failed to send email',
      statusChangeNotAllowed: 'This status change is not allowed',
      expenseAddedSuccessfully: 'Expense added successfully',
      edit: 'Edit'
    },
    toasts: {
      contractCreated: 'Contract created successfully',
      contractUpdated: 'Contract updated successfully',
      contractDeleted: 'Contract deleted successfully',
      contractCreateFailed: 'Failed to create contract',
      contractUpdateFailed: 'Failed to update contract',
      contractDeleteFailed: 'Failed to delete contract',
      scooterCreated: 'Scooter created successfully',
      scooterUpdated: 'Scooter updated successfully',
      scooterDeleted: 'Scooter deleted successfully',
      scooterStatusUpdated: 'Scooter status updated successfully',
      scooterStatusUpdateFailed: 'Failed to update scooter status',
      clientCreated: 'Client created successfully',
      clientUpdated: 'Client updated successfully',
      clientDeleted: 'Client deleted successfully',
      settingsUpdated: 'Settings updated successfully!',
      scooterStatusChangedAuto: 'Scooter Status Changed Automatically',
      scooterStatusChangeDesc: '{{scooterName}} was moved from {{fromStatus}} to {{toStatus}} {{reason}}.',
      becauseContractActivated: 'because the contract was activated',
      becauseContractDeactivated: 'because the contract was closed/archived',
      automatically: 'automatically',
      viewScooter: 'View Scooter',
      scooterOpenedFromNotif: 'Opened {{scooterName}} details',
      scooterOpenedFromNotifDesc: 'This scooter was automatically opened from a status change notification.',
      closedContractsMoveRestriction: 'Closed contracts can only be moved to Archived.'
    },
    login: {
      title: 'Login',
      email: 'Email',
      username: 'Username',
      password: 'Password',
      signIn: 'Sign in',
      signingIn: 'Signing in...',
      login: 'Login',
      loggingIn: 'Logging in...',
      loginFailed: 'Login failed',
      language: 'Language'
    },
    sidebar: {
      scooters: 'Scooters',
      clients: 'Clients',
      contracts: 'Contracts',
      finance: 'Finance',
      users: 'Users',
      activity: 'Activity Log',
      management: 'Management',
      howItWorks: 'How It Works',
      signIn: 'Sign in',
      expand: 'Expand sidebar',
      collapse: 'Collapse sidebar',
      logoutConfirm: 'Are you sure you want to logout?'
    },
    toolbar: {
      searchPlaceholder: 'Search by ID, model, or status…',
      clear: 'Clear',
      listView: 'List view',
      kanbanView: 'Kanban view',
      calendarView: 'Calendar',
      gridView: 'Grid view',
      mapView: 'Map view',
      filter: 'Filter',
      export: 'Export',
      total: 'Total: {{count}}',
      queryLabel: 'query',
      clearQuery: 'Clear query',
      removeFilter: 'Remove {{filter}}'
    },
    // Generic UI labels
    ui: {
      zoom: 'Zoom'
    },
    calendar: {
      week: 'Week',
      month: 'Month',
      all: 'All',
      plusMore: '+{{count}} more',
      scrollHint: 'Shift+Scroll to move horizontally',
      hideEmpty: 'Hide scooters without upcoming contracts'
    },
    status: {
      available: 'Available',
      rented: 'Rented',
      maintenance: 'Maintenance',
      retired: 'Retired',
      draft: 'Draft',
      active: 'Active',
      closed: 'Closed',
      archived: 'Archived'
    },
    clients: {
      id: 'Client ID',
      name: 'Name',
      messenger: 'WhatsApp',
      newClient: 'New client',
      creating: 'Creating client...',
      fullName: 'Full Name',
      fullNamePlaceholder: 'Enter full name',
      email: 'Email',
      emailPlaceholder: 'Enter email address',
      localPhone: 'Local Phone Number',
      localPhonePlaceholder: 'Enter local phone number (optional)',
      messengerNumber: 'WhatsApp Number',
      countryCodePlaceholder: '1',
      messengerNumberPlaceholder: 'Enter WhatsApp number (optional)',
      lineId: 'LINE ID',
      lineIdPlaceholder: 'Enter LINE ID (optional)',
      whatsappButton: 'WhatsApp',
      lineButton: 'LINE',
      contactViaWhatsApp: 'Contact via WhatsApp',
      contactViaLINE: 'Contact via LINE',
      notes: 'Notes',
      notesPlaceholder: 'Additional notes about this client (e.g., different country phone number)',
      addClient: 'New client',
      detailsTitle: 'Client Details',
      created: 'Created',
      clientId: 'Client ID',
      edit: 'Edit Client',
      previousContracts: 'Previous contracts:',
      noCurrentContracts: 'No current contracts',
      editClient: 'Edit Client',
      newContractBtn: 'New Contract',
      close: 'Close',
      statusHistory: 'Status history',
      unknownClient: 'Unknown Client'
    },
    scooters: {
      id: 'Scooter ID',
      modelRequired: 'Model is required',
      batteryRange: 'Battery must be 0–100',
      odometerNegative: 'Odometer cannot be negative',
      fuelRange: 'Fuel % must be 0–100',
      incompatibleEnergy: 'Electric cannot have fuel; fuel vehicles cannot have battery',
      editScooter: 'Edit Scooter',
      newScooter: 'New scooter',
      updating: 'Updating scooter...',
      creating: 'Creating scooter...',
      model: 'Model',
      modelPlaceholder: 'e.g. Ninebot Max',
      status: 'Status',
      typeLevel: 'Type & Level',
      odometer: 'Odometer',
      qrCode: 'QR Code',
      qrPlaceholder: 'Optional QR code',
      plateNumber: 'Plate Number',
      platePlaceholder: 'Optional plate number',
      nickname: 'Nickname',
      nicknamePlaceholder: 'e.g. Red Honda #1',
      nicknameOptional: 'Nickname (optional)',
      brand: 'Brand',
      brandPlaceholder: 'Optional brand',
      vehicleType: 'Vehicle type',
      electric: 'Electric',
      fuel: 'Fuel',
      batteryPct: 'Battery %',
      fuelPct: 'Fuel %',
      odometerKm: 'Odometer (km)',
      updateScooter: 'Update scooter',
      addScooter: 'New scooter',
      details: 'Details',
      edit: 'Edit',
      created: 'Created',
      statusHistory: 'Scooter status history',
      columnScooterId: 'Scooter ID',
      columnBrand: 'Brand',
      columnModel: 'Model',
      columnPlateNumber: 'Plate Number',
      columnNickname: 'Nickname',
      columnStatus: 'Status',
      columnVehicleStatus: 'Type & Level',
      columnOdometer: 'Odometer',
      pictures: 'Pictures',
      noPhotos: 'No photos yet',
      noPhotosYet: 'No photos yet',
      allPhotos: 'All Photos',
      viewAllPhotos: 'View All Photos',
      damage: 'Damage',
      damageOnly: 'Damage',
      showingDamage: 'Showing Damage',
      noDamagePhotos: 'No photos with damage',
      markAsDamage: 'Mark as damage',
      markDamage: 'Mark Damage',
      removeDamage: 'Remove Damage',
      markNoDamage: 'Mark no damage',
      damageDetected: 'Damage detected',
      scooterPhoto: 'Scooter photo',
      selectImageFile: 'Please select an image file',
      fileSizeLimit: 'File size must be less than 10MB',
      photoUploaded: 'Photo uploaded successfully',
      photoDeleted: 'Photo deleted',
      photoDeleteFailed: 'Failed to delete photo',
      photoUpdateFailed: 'Failed to update photo',
      photoUploadFailed: 'Failed to upload photo',
      photoStorageNotConfigured: 'Photo storage is not configured. Please contact your administrator.',
      deletePhoto: 'Delete photo',
      drawCircle: 'Draw Circle',
      stopDrawing: 'Stop Drawing',
      undo: 'Undo',
      redo: 'Redo',
      capture: 'Capture',
      takePhoto: 'Take Photo',
      chooseFromGallery: 'Choose from Gallery',
      addPhoto: 'Add Photo',
      photoPreview: 'Photo Preview',
      cameraError: 'Failed to access camera',
      cameraPermissionDenied: 'Camera permission denied'
    },
    pages: {
      manageColumns: 'Manage columns',
      columns: 'Columns',
      retiredHidden: 'Retired scooters are hidden',
      failedLoadScooters: 'Failed to load scooters',
      checkConnection: 'Please check your connection and try again.',
      retry: 'Retry',
      clearFilters: 'Clear filters',
      addScooter: 'New scooter',
      noScootersYet: 'No scooters yet',
      noScootersMatch: 'No scooters match your filters',
      addFirstScooter: 'Add your first scooter to get started.',
      tryAdjusting: 'Try adjusting or clearing your filters.',
      rowsPerPage: 'Rows per page:',
      pageOf: 'Page {{current}} of {{total}}',
      prevShort: 'Prev',
      nextShort: 'Next',
      prev: 'Previous page',
      next: 'Next page'
    },
    // Contracts page labels
    contractsPage: {
      newContract: 'New Contract',
      showArchived: 'Show archived contracts',
      newContractSubtitle: 'Quickly create and review a new rental agreement',
      createdDate: 'Created Date',
      recentlyUpdated: 'Recently Updated',
      recentlyClosed: 'Recently Closed',
      allClosed: 'All Closed',
      showAll: 'Show All',
      showRecent: 'Show Recent',
      sortBy: 'Sort by...',
      archivedOnlyToDraft: 'Archived contracts can only be moved to Draft.'
    },
    activity: {
      title: 'Activity Log',
      subtitle: 'Monitor employee activity and system changes. View user-friendly logs for troubleshooting and auditing.',
      filterActivity: 'Filter Activity',
      timePeriod: 'Time Period',
      lastHour: 'Last Hour',
      today: 'Today',
      thisWeek: 'This Week',
      thisMonth: 'This Month',
      custom: 'Custom',
      from: 'From',
      to: 'To',
      employee: 'Employee',
      type: 'Activity Type',
      allEmployees: 'All employees',
      allActivities: 'All activities',
      scooterMgmt: 'Scooter Management',
      contractMgmt: 'Contract Management',
      clientMgmt: 'Client Management',
      orgSettings: 'Organization Settings',
      searchEverything: 'Search Everything',
      searchPlaceholder: 'Search names, IDs, prices, statuses...'
    },
    contracts: {
      filters: 'Filters',
      editContract: 'Edit Contract',
      dates: 'Dates',
      for: 'for',
      scooter: 'Scooter',
      start: 'Start',
      end: 'End',
      duration: 'Duration',
      days: 'days',
      weeks: 'weeks',
      months: 'months',
      estimatedTotal: 'Estimated total',
      fetchingData: 'Fetching clients and scooters',
      scooterCurrentlyRented: 'ℹ️ This scooter is currently rented{{until}}.',
      until: 'until',
      futureReservationNote: "You can still create a contract for a future reservation - just make sure the dates don't overlap.",
      searchClients: 'Search clients by name/email/id',
      noMatches: 'No matches',
      selected: 'Selected:',
      scooterQuery: 'Scooter (ID or model contains)',
      scooterPlaceholder: 'e.g. Ninebot or UUID',
      dailyRateRange: 'Daily rate (min/max)',
      min: 'Min',
      max: 'Max',
      minExceedsMax: 'Min cannot exceed Max',
      endAfterStart: 'End time must be after start time',
      clientRequired: 'Client is required',
      scooterRequired: 'Scooter is required',
      startTimeRequired: 'Start time is required',
      dailyRatePositive: 'Daily rate must be > 0',
      addContract: 'New contract',
      client: 'Client',
      selectClient: 'Select client',
      searchScooters: 'Search scooters...',
      startTime: 'Start time',
      status: 'Status',
      dailyRate: 'Daily rate',
      deposit: 'Deposit',
      endTime: 'End Date',
      contractId: 'Contract ID',
      paymentMethod: 'Payment Method',
      cash: 'Cash',
      local_bank_transfer: 'Local Bank Transfer',
      international_bank_transfer: 'International Bank Transfer',
      notes: 'Notes',
      notesPlaceholder: 'Optional notes about this contract',
      createDraftForScooter: 'Create a new contract draft for this scooter.',
      saveAsDraftHint: 'Save as draft for later activation',
      startImmediatelyHint: 'Start contract immediately',
      creating: 'Creating...',
      createDraft: 'Create draft',
      createActive: 'Create active',
      day: 'day',
      forDays: 'for {{count}} day',
      forDays_plural: 'for {{count}} days',
      total: 'Total:',
      dayOf: 'day {{day}} of {{total}}'
    },
    contractsUi: {
      renting: 'renting',
      statusHistory: 'Status history',
      noHistory: 'No history available',
      created: 'Created',
      overdue: 'Overdue',
      overdueTitle: 'Contract is overdue',
      remainingTitle: 'Time remaining until contract end',
      remaining: 'left',
      unitDayShort: 'd',
      unitHourShort: 'h',
      unitMinuteShort: 'm',
      clientDetails: 'Client Details',
      scooterDetails: 'Scooter Details',
      clientNotFound: 'Client not found',
      scooterNotFound: 'Scooter not found',
      fullName: 'Full Name',
      localPhone: 'Local Phone',
      messenger: 'WhatsApp',
      brand: 'Brand',
      vehicleType: 'Vehicle type',
      battery: 'Battery',
      qrCode: 'QR Code',
      scooterStatus: 'Scooter status',
      noStatusChanges: 'No status changes yet',
      loadingHistory: 'Loading history...',
      characters: 'characters',
      back: 'Back',
      startDate: 'Start Date',
      endDate: 'End Date',
      contractId: 'Contract ID',
      editContract: 'Edit Contract',
      duplicate: 'Duplicate',
      duplicateContract: 'Duplicate contract',
      duplicateSubtitle: 'Choose start, duration, and end for the new draft',
      endTimeMustBeAfter: 'End time must be after start time',
      changeStatus: 'Change status',
      currentStatus: 'current',
      changeScooterStatus: 'Change scooter status',
      currentScooterStatus: 'current',
      showingRange: 'Showing {{start}}–{{end}} of {{total}}',
      contractCreatedTitle: 'Contract created',
      contractCreatedMessage: 'Do you want to open the created contract?',
      contractLabel: 'Contract',
      openContract: 'Open Contract',
      stayHere: 'Stay Here',
      scooterStatusQuestion: 'What should be the status of scooter "{{model}}"?',
      currentStatusLabel: 'Current status:',
      statusChangeHelp: 'This will help you manage the scooter\'s availability after the contract status change.',
      working: 'Working…',
      moveContractAndUpdate: 'Move Contract & Update Scooter',
      enterToConfirm: 'Enter to confirm · Esc to cancel',
      statusAvailable: 'Available',
      statusAvailableDesc: 'Scooter is ready for rental',
      statusRented: 'Rented',
      statusRentedDesc: 'Scooter is currently being rented',
      statusMaintenance: 'Maintenance',
      statusMaintenanceDesc: 'Scooter needs maintenance',
      statusRetired: 'Retired',
      statusRetiredDesc: 'Scooter is no longer in service',
      statusCurrent: 'Current',
      moveContractTitle: 'Move Contract',
      moveContractMessage: 'Please select the new status for the scooter when moving this contract.',
      fromLabel: 'From',
      toLabel: 'To',
      clientLabel: 'Client',
      scooterLabel: 'Scooter',
      currentContract: 'Current contract:',
      viewContract: 'View Contract'
    },
    contractDocument: {
      print: 'Print',
      saveAsPDF: 'Save as PDF',
      email: 'Email',
      addSignature: 'Add Signature',
      emailRequired: 'Email address is required',
      emailSent: 'Contract sent successfully',
      enterSignatureName: 'Enter signer name to append to document',
      loading: 'Loading contract...',
      preparingPDF: 'Preparing PDF...',
      downloadSuccess: 'PDF downloaded successfully',
      downloadFailed: 'Failed to generate PDF',
      retry: 'Retry',
      emailContract: 'Email Contract',
      recipientEmail: 'Recipient Email',
      sending: 'Sending...',
      send: 'Send'
    },
    businessDetails: {
      title: 'Business Details',
      settingsUpdated: 'Business details updated successfully',
      errorLoading: 'Error loading business details.',
      helpText: 'Fill in your business information below. These details will appear on rental contracts. Leave fields blank if you prefer to fill them by hand on printed contracts.',
      businessName: 'Business Name',
      businessNamePlaceholder: 'Enter your business name',
      businessAddress: 'Business Address',
      businessAddressPlaceholder: 'Enter your business address',
      businessPhone: 'Business Phone',
      businessPhonePlaceholder: '+66 2 123 4567',
      businessEmail: 'Business Email',
      businessEmailPlaceholder: 'e.g. business@example.com',
      taxId: 'Tax ID / Business Registration',
      taxIdPlaceholder: '1234567890123',
      representativeName: 'Authorized Representative Name',
      representativeNamePlaceholder: 'John Smith',
      representativeIdNumber: 'Representative ID Number',
      representativeIdNumberPlaceholder: '1234567890123',
      saving: 'Saving...',
      saveDetails: 'Save Business Details',
      saved: 'Saved'
    },
    messaging: {
      title: 'Contact Client',
      scanQR: 'Scan QR Code',
      orClickLink: 'Or click link below',
      openApp: 'Open Messaging App',
      selectTemplate: 'Select Message Template',
      customMessage: 'Custom Message',
      messageEditor: 'Message Editor',
      thankYou: 'Thank You',
      reminder: 'Reminder',
      payment: 'Payment Reminder',
      templateThankYou: 'Thank you for choosing {{businessName}}! We hope you enjoyed your scooter rental experience. Looking forward to serving you again!',
      templateReminder: 'Hi {{clientName}}! This is {{businessName}}. Reminder: Your rental period ends tomorrow. Please return the scooter.',
      templatePayment: 'Hi {{clientName}}! This is {{businessName}}. Payment reminder for contract #{{contractId}}.',
      noWhatsApp: 'No WhatsApp number available',
      noLINE: 'No LINE ID available',
      close: 'Close'
    },
    finance: {
      title: 'Finance Overview',
      exportCSV: 'Export CSV',
      timeRange: 'Time Range',
      day: 'Day',
      week: 'Week',
      month: 'Month',
      year: 'Year',
      all: 'All',
      searchPlaceholder: 'Search scooters...',
      showSoldOnly: 'Show sold only',
      totalPurchase: 'Total Purchase',
      totalMaintenance: 'Total Maintenance',
      totalIncome: 'Total Income',
      netProfit: 'Net Profit',
      topROIScooter: 'Top ROI Scooter',
      bestBrandModel: 'Best Brand/Model',
      scooters: 'scooters',
      scooter: 'Scooter',
      purchase: 'Purchase',
      maintenance: 'Maintenance',
      income: 'Income',
      sale: 'Sale',
      net: 'Net',
      roi: 'ROI',
      status: 'Status',
      profitable: 'Profitable',
      breakingEven: 'Breaking Even',
      lossMaker: 'Loss-Maker',
      totals: 'Totals',
      recentExpenses: 'Recent Expenses',
      noExpenses: 'No expenses recorded',
      loadingExpenses: 'Loading expenses...',
      noExpensesInRange: 'No expenses in selected time range',
      expenseTotal: 'Total',
      categoryOilChange: 'Oil Change',
      categoryTires: 'Tires',
      categoryBrakes: 'Brakes',
      categoryEngine: 'Engine',
      categoryBattery: 'Battery',
      categoryBodyParts: 'Body Parts',
      categoryCleaning: 'Cleaning',
      categoryRegistration: 'Registration',
      categoryInsurance: 'Insurance',
      categoryMiscParts: 'Misc Parts',
      categoryCustom: 'Custom',
      affectsExpenseDetails: 'affects expense details',
      tableView: 'Table',
      chartsView: 'Charts',
      toggleKPIs: 'Toggle KPIs',
      toggleInsights: 'Toggle Insights',
      toggleAdditionalTables: 'Toggle Additional Tables',
      showOperational: 'Show operational view',
      showFull: 'Show full view',
      operationalView: 'Operational view',
      fullView: 'Full view',
      operationalNet: 'Operational net',
      date: 'Date',
      category: 'Category',
      amount: 'Amount',
      note: 'Notes',
      actions: 'Actions',
      edit: 'Edit',
      delete: 'Delete',
      editExpense: 'Edit Expense',
      customLabel: 'Custom label',
      confirmDeleteExpense: 'Are you sure you want to delete this expense?',
      expenseDeleted: 'Expense deleted',
      deleteFailed: 'Failed to delete expense',
      expenseUpdated: 'Expense updated',
      updateFailed: 'Failed to update expense',
      expenseCreated: 'Expense created',
      addExpense: 'Add Expense',
      brandModelPerformance: 'Brand/Model Performance',
      brand: 'Brand',
      model: 'Model',
      count: 'Count',
      avgROI: 'Avg ROI',
      topExpenses: 'Top Expenses by Category',
      noData: 'No data',
      monthlyTrends: 'Monthly Financial Trends',
      expenseBreakdown: 'Expense Category Breakdown',
      maintenanceEfficiency: 'Maintenance Efficiency',
      topPerformers: 'Top Performers',
      needsAttention: 'Needs Attention',
      fleetHealth: 'Fleet Health Overview',
      totalScooters: 'Total Scooters',
      revenueVsMaintenance: 'Revenue vs Maintenance by Scooter',
      profitTrend: 'Profit Trend by Scooter',
      statusDistribution: 'Status Distribution',
      exportBasic: 'Export Basic Summary',
      exportDetailed: 'Export Detailed Expenses',
      exportingDetailed: 'Exporting detailed expenses...',
      exportCompleted: 'Export completed',
      exportFailed: 'Failed to export'
    }
  },
  th: {
    howItWorks: {
      title: 'วิธีใช้ OurScooter (สำหรับพนักงาน)',
      subtitle: 'ขั้นตอนง่าย ๆ กฎที่ชัดเจน และเคล็ดลับที่มีประโยชน์เพื่อให้ทำงานได้อย่างถูกต้องและมีประสิทธิภาพ',
      categories: 'หมวดหมู่',
      whyTitle: 'ทำไมต้อง OurScooter: จุดประสงค์และประโยชน์',
      whyDescription: 'OurScooter ช่วยให้พนักงานดำเนินงานเช่าสกู๊ตเตอร์ประจำวันได้อย่างมีประสิทธิภาพ: คลิกน้อยลง ผิดพลาดน้อยลง และมีบันทึกที่ชัดเจน',
      whySpeed: 'ความเร็ว: สร้างสัญญาได้ในไม่กี่วินาที นำรายละเอียดมาใช้ซ้ำได้ด้วยการทำซ้ำ',
      whyClarity: 'ความชัดเจน: สถานะแสดงให้เห็นว่าการเช่าแต่ละรายการอยู่ในขั้นตอนใดได้ในพริบตา',
      whyControl: 'การควบคุม: กรอง ค้นหา และสลับมุมมอง (ตาราง/คันบัง) ให้เหมาะกับงาน',
      whyAccuracy: 'ความถูกต้อง: ระบบป้องกันการกระทำที่ไม่ถูกต้องและการเช่าที่ทับซ้อนกัน',
      signingInTitle: 'การเข้าสู่ระบบและการคงสถานะเข้าสู่ระบบ',
      signingInToken: 'ชื่อผู้ใช้ + รหัสผ่าน → เซิร์ฟเวอร์ตรวจสอบและส่งคืน access + refresh tokens',
      signingInAutoRefresh: 'Access token มีอายุสั้น เราจะรีเฟรชให้อัตโนมัติแบบเงียบ ๆ โดยใช้ refresh token',
      signingInRoles: 'บทบาท: แอดมินและพนักงาน เลือกบริบทขององค์กรได้ในแถบด้านข้าง',
      signingInSession: 'ระยะเวลาเซสชัน: คุณจะอยู่ในระบบได้นานสูงสุด 1 วันหากไม่มีการใช้งาน แอปใช้ access token ระยะสั้น 15 นาทีและต่ออายุให้ในเบื้องหลัง หลังจากไม่ได้ใช้แอป 1 วัน (หรือเมื่อล้างคุกกี้) ระบบจะขอให้คุณเข้าสู่ระบบอีกครั้ง',
      scootersTitle: 'สกู๊ตเตอร์: การเพิ่มและแก้ไข',
      scootersOpen: 'เปิด "Add Scooter" เพื่อลงทะเบียนรถคันใหม่ ใส่รายละเอียดให้สั้นและถูกต้อง',
      scootersRequired: 'จำเป็น: ยี่ห้อ/รุ่น, สถานะ',
      scootersOptional: 'ไม่บังคับ: ราคาซื้อ, รายละเอียดน้ำมัน/แบตเตอรี่, หมายเหตุ',
      scootersTip: 'เคล็ดลับ: สถานะ "maintenance" จะนำสกู๊ตเตอร์ออกจากรายการที่ว่างจนกว่าจะนำกลับมา',
      clientsTitle: 'ลูกค้า: การเพิ่มและแก้ไข',
      clientsOpen: 'เปิด "New Client" บันทึกข้อมูลที่ช่วยให้คุณค้นหาพวกเขาได้ในภายหลัง',
      clientsRequired: 'จำเป็น: ชื่อ-นามสกุล',
      clientsOptional: 'ไม่บังคับ: โทรศัพท์, อีเมล, บัตรประชาชน/พาสปอร์ต, หมายเหตุ',
      contractsTitle: 'สัญญา: การสร้างและการจัดการ',
      contractsWaysTitle: 'วิธีสร้างสัญญา (4 ตัวเลือก)',
      contractsFromContracts: 'จาก Contracts: กด "Add Contract"',
      contractsFromScooters: 'จาก Scooters: เปิดสกู๊ตเตอร์ → "New Contract"',
      contractsFromClients: 'จาก Clients: เปิดลูกค้า → "New Contract"',
      contractsDuplicate: 'ทำซ้ำ: เปิดสัญญาที่มีอยู่ → "Duplicate" เพื่อนำรายละเอียดมาใช้ซ้ำอย่างปลอดภัย',
      contractsRequiredTitle: 'ฟิลด์ที่จำเป็น',
      contractsRequiredClient: 'ลูกค้าและสกู๊ตเตอร์',
      contractsRequiredDate: 'วันที่/เวลาเริ่มต้น',
      contractsRequiredRate: 'ราคารายวัน (เงินมัดจำ/วิธีการชำระเงินไม่บังคับ)',
      contractsStatusTitle: 'สถานะและเวิร์กโฟลว์ (อะไรที่ทำได้)',
      contractsStatusFlow: 'Draft → Active → Closed → Archived',
      contractsStatusActivate: 'คุณจะเปิดใช้งานได้เมื่อตั้งค่าฟิลด์ที่จำเป็นแล้วและสกู๊ตเตอร์ว่างเท่านั้น',
      contractsStatusClosed: 'จาก Closed คุณไปได้แค่ Archived เท่านั้น (กลับไป Active ไม่ได้)',
      contractsStatusForbidden: 'ไม่อนุญาต: สัญญา Active ที่ทับซ้อนกันสำหรับสกู๊ตเตอร์คันเดียวกัน',
      contractsStatusArchived: 'สัญญา Archived จะหายไปจากมุมมองเริ่มต้น เปิด "Include archived" เพื่อดู',
      contractsAutoTitle: 'การอัปเดตสถานะสกู๊ตเตอร์อัตโนมัติ',
      contractsAutoCreate: 'การสร้างสัญญาไม่เปลี่ยนสถานะสกู๊ตเตอร์ (ไม่ว่าสถานะสัญญาจะเป็นอะไร)',
      contractsAutoActive: 'เมื่อสัญญาเป็น Active → สกู๊ตเตอร์จะกลายเป็น "rented" อัตโนมัติ',
      contractsAutoClosed: 'เมื่อสัญญา Active ถูก Close/Archive → สกู๊ตเตอร์จะกลายเป็น "available" อัตโนมัติ',
      contractsAutoDraft: 'เมื่อสัญญา Active ถูกเปลี่ยนกลับเป็น Draft → สกู๊ตเตอร์จะกลายเป็น "available" อัตโนมัติ',
      contractsAutoPrevent: 'สิ่งนี้ป้องกันการจองซ้ำและทำให้ความพร้อมของสกู๊ตเตอร์ถูกต้อง',
      contractsTimeTitle: 'การติดตามเวลาสำหรับสัญญา Active',
      contractsTimeRemaining: 'สัญญา Active แสดงเวลาที่เหลือจนถึงวันสิ้นสุด (เช่น "2h 30m left")',
      contractsTimeOverdue: 'สัญญาที่เกินกำหนดแสดงเวลาที่ผ่านไป (เช่น "Overdue 1d 5h")',
      contractsTimeColors: 'สีบ่งบอกความเร่งด่วน: เขียว (4+ ชั่วโมง), เหลือง (1-4 ชั่วโมง), ส้ม (<1 ชั่วโมง), แดง (เกินกำหนด)',
      contractsTimeKanban: 'ในมุมมอง Kanban: แสดงเฉพาะตัวจับเวลาสำหรับสัญญา Active',
      contractsTimeList: 'ในมุมมอง List: แสดงทั้งป้ายสถานะและตัวจับเวลาสำหรับสัญญา Active',
      contractsViewsTitle: 'สัญญา: มุมมอง',
      contractsViewsTable: 'มุมมองตาราง: สแกน กรอง และส่งออกแถวได้อย่างรวดเร็ว',
      contractsViewsKanban: 'กระดาน Kanban: เวิร์กโฟลว์แบบภาพตามสถานะ (Draft, Active, Closed, Archived)',
      contractsViewsToggle: 'ใช้ปุ่มสลับมุมมองในแถบเครื่องมือ Contracts เพื่อสลับมุมมอง',
      contractsKanbanTitle: 'การทำงานบนกระดาน Kanban',
      contractsKanbanDrag: 'ลากแล้วปล่อยการ์ดเพื่อย้ายไปยังสถานะถัดไปที่อนุญาต',
      contractsKanbanEdit: 'เปิดการ์ดแล้วกด Edit เพื่อแก้ไขรายละเอียดหรือสถานะโดยตรง',
      contractsKanbanRules: 'มีกฎบังคับใช้: ย้ายจาก Closed กลับไป Active ไม่ได้; Archived เป็นขั้นสุดท้าย',
      contractsKanbanBlocked: 'การย้ายที่ทำไม่ได้จะถูกบล็อก (เช่น สกู๊ตเตอร์ Active อยู่ที่อื่นแล้ว)',
      contractsKanbanFilters: 'ใช้ตัวกรองเพื่อโฟกัสที่ลูกค้ารายเดียวหรือรวมรายการ Archived เมื่อจำเป็น',
      findingTitle: 'การค้นหาอย่างรวดเร็ว (กรองและค้นหา)',
      findingScooters: 'สกู๊ตเตอร์: กรองตามสถานะ ยี่ห้อ รุ่น ใช้ช่องค้นหาสำหรับการจับคู่ข้อความอย่างรวดเร็ว',
      findingClients: 'ลูกค้า/ผู้ใช้: ค้นหาตามชื่อ/อีเมล; ปรับขนาดหน้าเพื่อดูแถวมากขึ้น',
      findingContracts: 'สัญญา: กรองตามสถานะ; เลือกรวมที่เก็บถาวรได้',
      rowsTitle: 'เราแสดงกี่แถว?',
      rowsScooters: 'สกู๊ตเตอร์: โหลดเป็นหน้า ๆ และดึงเพิ่มเมื่อจำเป็น (สูงสุด 100 ต่อการโหลด)',
      rowsClients: 'ลูกค้า/ผู้ใช้: คุณเลือกได้ 10/20/30/40/50 แถวต่อหน้า',
      rowsContracts: 'สัญญา: แสดงสัญญาขององค์กรปัจจุบัน; รวมที่เก็บถาวรเพื่อขยายรายการ',
      themesTitle: 'ธีมและค่าเริ่มต้น',
      themesChange: 'เปลี่ยนธีม (Dark/Light/Blue/Wood) ที่แถบด้านซ้ายล่าง',
      themesAdmin: 'แอดมินสามารถกำหนดค่าเริ่มต้นขององค์กรใน Management (เช่น สกุลเงิน อัตรา)',
      usersTitle: 'ผู้ใช้และการจัดการ (แอดมิน)',
      usersStaff: 'พนักงาน: งานประจำวัน ดูและสร้างสัญญา จัดการสกู๊ตเตอร์/ลูกค้า',
      usersAdmin: 'แอดมิน: ทุกอย่างที่พนักงานทำได้ บวกการตั้งค่าระดับองค์กรและการลบ',
      tipsTitle: 'เคล็ดลับและการแก้ปัญหา',
      tipsActivate: 'เปิดใช้งานไม่ได้? ตรวจสอบสถานะสกู๊ตเตอร์ (ต้องว่าง) และฟิลด์ที่จำเป็น',
      tipsContract: 'มองไม่เห็นสัญญา? อาจถูกเก็บถาวร เปิด "Include archived"',
      tipsSession: 'เซสชันสิ้นสุด? ถ้าเกินหนึ่งวันแล้ว แค่เข้าสู่ระบบใหม่อีกครั้ง'
    },
    booking: {
      orgNotFound: 'ไม่พบร้าน',
      orgNotFoundDesc: 'ไม่พบหน้าจองที่คุณกำลังมองหา อาจเป็นเพราะ:',
      orgNotFoundReason1: 'ชื่อร้าน (slug) ไม่ถูกต้อง',
      orgNotFoundReason2: 'ร้านยังไม่ได้ตั้งค่าหน้าจอง',
      orgNotFoundReason3: 'หน้าจองถูกลบไปแล้ว',
      orgNotFoundOwner: 'หากคุณเป็นเจ้าของร้าน โปรดตรวจสอบว่าได้ตั้งค่า slug ของร้านใน Management → Technical Details แล้ว',
      requestReceived: 'ได้รับคำขอแล้ว!',
      requestReceivedDesc: 'เราได้รับคำขอจองของคุณแล้ว นี่ยังไม่ใช่การจองที่ยืนยันแล้ว',
      willContact: '{{business}} จะติดต่อคุณในไม่ช้าเพื่อยืนยันการเช่า',
      requestIdLabel: 'รหัสคำขอ:',
      contactLabel: 'ติดต่อ:',
      showroomBadge: 'โชว์รูมสกู๊ตเตอร์',
      bookBadge: 'จองสกู๊ตเตอร์',
      showroomIntro: 'เลือกชมสกู๊ตเตอร์ของเรา เลือกคันที่ชอบ แล้วส่งคำขอจอง เราจะยืนยันกับคุณในไม่ช้า',
      bookIntro: 'ส่งคำขอจองด้านล่าง แล้วเราจะติดต่อคุณในไม่ช้าเพื่อยืนยัน',
      yourDetailsStep: '3. ข้อมูลของคุณ',
      yourInformation: 'ข้อมูลของคุณ',
      fullName: 'ชื่อ-นามสกุล',
      fullNamePlaceholder: 'กรอกชื่อ-นามสกุล',
      email: 'อีเมล',
      emailPlaceholder: 'กรอกที่อยู่อีเมล',
      localPhone: 'หมายเลขโทรศัพท์ในประเทศ',
      phonePlaceholder: 'หมายเลขโทรศัพท์',
      messengerNumber: 'หมายเลข Messenger (WhatsApp ฯลฯ)',
      messengerPlaceholder: 'หมายเลข WhatsApp (ไม่บังคับ)',
      sameAsPhone: 'เหมือนกับเบอร์โทร',
      lineId: 'LINE ID',
      lineIdPlaceholder: 'LINE ID (ไม่บังคับ)',
      passportNumber: 'หมายเลขพาสปอร์ต',
      passportPlaceholder: 'หมายเลขพาสปอร์ต (ไม่บังคับ)',
      nationality: 'สัญชาติ',
      nationalityPlaceholder: 'สัญชาติ (ไม่บังคับ)',
      localAddress: 'ที่อยู่ในประเทศ',
      localAddressPlaceholder: 'ที่อยู่ในประเทศ (ไม่บังคับ)',
      rentalDatesStep: '1. วันที่เช่า',
      bookingDetails: 'รายละเอียดการจอง',
      startDate: 'วันที่เริ่ม',
      duration: 'ระยะเวลา',
      endDate: 'วันที่สิ้นสุด',
      dayUnit: 'วัน',
      daysUnit: 'วัน',
      weekUnit: 'สัปดาห์',
      weeksUnit: 'สัปดาห์',
      monthUnit: 'เดือน',
      monthsUnit: 'เดือน',
      scooterUnit: 'คัน',
      scootersUnit: 'คัน',
      pricingSummary: 'สรุปราคา',
      lineDailyRate: '{{count}} {{unit}} × ราคารายวัน:',
      lineWeeklyRate: '{{count}} {{unit}} × ราคารายสัปดาห์:',
      lineDailyFromWeekly: '{{count}} {{unit}} × ราคารายวัน (จากรายสัปดาห์):',
      lineMonthlyRate: '{{count}} {{unit}} × ราคารายเดือน:',
      lineDailyFromMonthly: '{{count}} {{unit}} × ราคารายวัน (จากรายเดือน):',
      subtotal: 'ยอดรวมย่อย:',
      deposit: 'เงินมัดจำ:',
      total: 'รวมทั้งหมด:',
      selectToSeePricingShowroom: 'เลือกวันที่และสกู๊ตเตอร์เพื่อดูราคา',
      selectToSeePricingGroup: 'เลือกวันที่และกลุ่มราคาเพื่อดูราคา',
      selectPricingGroup: 'เลือกกลุ่มราคา',
      perDay: '/วัน',
      perWeek: '/สัปดาห์',
      perMonth: '/เดือน',
      specialPromotions: 'โปรโมชันพิเศษ:',
      scooterPreference: 'สกู๊ตเตอร์ที่ต้องการ',
      noPreference: 'ไม่ระบุ',
      scooterFallback: 'สกู๊ตเตอร์',
      chooseScooterStep: '2. เลือกสกู๊ตเตอร์ของคุณ',
      checkingAvailability: 'กำลังตรวจสอบความพร้อมสำหรับวันที่ของคุณ…',
      availableForDates: 'พร้อมให้บริการ {{available}} จาก {{total}} คันสำหรับวันที่ของคุณ',
      fleetCount: 'มี {{count}} {{unit}} ในกองรถของเรา',
      lockedScooterNote: 'สกู๊ตเตอร์คันนี้ถูกจองไว้สำหรับคุณผ่านลิงก์จองของคุณ',
      noScootersAvailable: 'ขณะนี้ไม่มีสกู๊ตเตอร์ว่าง กรุณาติดต่อเราโดยตรง',
      available: 'ว่าง',
      unavailable: 'ไม่ว่าง',
      photosCount: '{{count}} รูป',
      viewPhoto: 'ดู',
      viewPhotosAria: 'ดูรูปของ {{title}}',
      additionalNotes: 'หมายเหตุเพิ่มเติม',
      notesPlaceholder: 'คำขอพิเศษหรือข้อมูลเพิ่มเติม...',
      charactersCount: '{{count}}/255 ตัวอักษร',
      preSignTitle: '⚡ ลงนามสัญญาล่วงหน้า (แนะนำ)',
      preSignDesc: 'เร่งกระบวนการรับรถของคุณ! การลงนามสัญญาล่วงหน้าช่วยประหยัดเวลาเมื่อคุณมาถึง',
      preSignBenefits: 'ไม่ต้องชำระเงิน • ไม่ใช่ข้อผูกมัด • แค่ช่วยให้รับรถเร็วขึ้น',
      completeFieldsToSign: 'กรอกข้อมูลที่จำเป็นด้านบนให้ครบเพื่อเปิดใช้การลงนามสัญญาล่วงหน้า',
      missingLabel: 'ยังขาด:',
      signatureSaved: 'บันทึกลายเซ็นแล้ว: {{name}}',
      submitting: 'กำลังส่ง...',
      submitRequest: 'ส่งคำขอจอง',
      disclaimer: 'เมื่อส่งแบบฟอร์มนี้ คุณเข้าใจว่านี่เป็นคำขอ ไม่ใช่การจองที่ยืนยันแล้ว ร้านจะติดต่อคุณเพื่อยืนยันความพร้อมและดำเนินการจองให้เสร็จสมบูรณ์',
      selectDatesForPrice: 'เลือกวันที่ของคุณเพื่อดูราคา',
      depositShort: 'มัดจำ',
      requestToBook: 'ส่งคำขอจอง',
      continueBtn: 'ดำเนินการต่อ',
      closeBtn: 'ปิด',
      prevPhoto: 'รูปก่อนหน้า',
      nextPhoto: 'รูปถัดไป',
      langLabel: 'ภาษา',
      toastLoadFailed: 'ไม่สามารถโหลดหน้าจองได้',
      toastVersionFailed: 'ไม่สามารถสร้างเวอร์ชันสัญญาได้ กรุณาลองอีกครั้ง',
      toastPreviewFailed: 'ไม่สามารถโหลดตัวอย่างสัญญาได้',
      toastEnterName: 'กรุณากรอกชื่อ-นามสกุลของคุณ',
      toastEnterEmail: 'กรุณากรอกที่อยู่อีเมลของคุณ',
      toastEnterPhone: 'กรุณากรอกหมายเลขโทรศัพท์ของคุณ',
      toastSelectStart: 'กรุณาเลือกวันที่เริ่ม',
      toastSelectScooter: 'กรุณาเลือกสกู๊ตเตอร์',
      toastSelectPricingGroup: 'กรุณาเลือกกลุ่มราคา',
      toastPricingFailed: 'ไม่สามารถคำนวณราคาได้',
      toastSubmitFailed: 'ไม่สามารถส่งคำขอจองได้ กรุณาลองอีกครั้ง',
      errFullNameRequired: 'กรุณากรอกชื่อ-นามสกุล',
      errEmailRequired: 'กรุณากรอกอีเมล',
      errEmailInvalid: 'อีเมลไม่ถูกต้อง',
      errPhoneRequired: 'กรุณากรอกหมายเลขโทรศัพท์',
      mfFullName: 'ชื่อ-นามสกุล',
      mfEmail: 'อีเมล',
      mfPhone: 'หมายเลขโทรศัพท์',
      mfStartDate: 'วันที่เริ่ม',
      mfScooter: 'สกู๊ตเตอร์',
      mfPricingGroup: 'กลุ่มราคา',
      mfPricingInfo: 'ข้อมูลราคา'
    },
    theme: {
      dark: 'มืด',
      light: 'สว่าง',
      blue: 'สีน้ำเงิน',
      wood: 'ลายไม้'
    },
    home: {
      quickActions: 'ทางลัด',
      recentActivity: 'กิจกรรมล่าสุด',
      fleetStatusOverview: 'ภาพรวมสถานะรถ',
      welcomeTitle: 'ยินดีต้อนรับสู่ OurScooter',
      welcomeSubtitle: 'แดชบอร์ดจัดการฟลีตรถสกู๊ตเตอร์แบบครบวงจร',
      addScooter: 'รถสกู๊ตเตอร์ใหม่',
      registerScooter: 'เพิ่มรถคันใหม่',
      addClient: 'ลูกค้าใหม่',
      registerClient: 'เพิ่มลูกค้าใหม่',
      newContract: 'สร้างสัญญา',
      createAgreement: 'สร้างสัญญาเช่า',
      org: 'องค์กร',
      allOrgs: 'ทุกองค์กร',
      availableScooters: 'รถพร้อมให้เช่า',
      readyForRental: 'พร้อมปล่อยเช่า',
      rentedScooters: 'รถกำลังเช่า',
      currentlyInUse: 'กำลังใช้งาน',
      maintenance: 'ซ่อมบำรุง',
      underRepair: 'อยู่ระหว่างซ่อม',
      fleetOverview: 'ภาพรวมรถทั้งหมด',
      available: 'ว่าง',
      rented: 'เช่าอยู่',
      activeRevenue: 'รายได้ที่ใช้งานอยู่',
      monthlyRecurring: 'รายได้รายเดือน',
      totalClients: 'ลูกค้าทั้งหมด',
      activeContracts: 'สัญญาที่ใช้งานอยู่',
      avgBattery: 'แบตเตอรี่เฉลี่ย',
      fleetBatteryHealth: 'สุขภาพแบตเตอรี่รวม',
      avgFuel: 'น้ำมันเฉลี่ย',
      fuel: 'น้ำมัน',
      electric: 'ไฟฟ้า',
      fleetROI: 'ผลตอบแทนรถ',
      rev: 'รายได้',
      cost: 'ต้นทุน',
      inMaintenance: 'อยู่ระหว่างซ่อมบำรุง',
      newClientRegistered: 'ลูกค้าใหม่',
      registered: 'ลงทะเบียนแล้ว'
    },
    users: {
      title: 'ผู้ใช้',
      errorLoading: 'โหลดรายชื่อผู้ใช้ไม่สำเร็จ',
      id: 'ไอดี',
      email: 'อีเมล',
      role: 'สิทธิ์',
      username: 'ชื่อผู้ใช้',
      org: 'องค์กร',
      roleSuperadmin: 'ผู้ดูแลระบบสูงสุด',
      roleAdmin: 'ผู้ดูแลระบบ',
      roleStaff: 'พนักงาน'
    },
    management: {
      title: 'การจัดการองค์กร',
      errorLoading: 'โหลดการตั้งค่าไม่สำเร็จ',
      timeFormat: 'รูปแบบเวลา',
      defaultCurrency: 'สกุลเงินเริ่มต้น',
      defaultCountryCode: 'รหัสประเทศเริ่มต้น',
      defaultDailyPrice: 'ราคาต่อวันเริ่มต้น',
      defaultDeposit: 'มัดจำสัญญาเริ่มต้น',
      defaultRentLength: 'ระยะเวลาเช่า (วัน)',
      defaultPaymentMethod: 'วิธีชำระเงินเริ่มต้น',
      buttonStyle: 'รูปแบบปุ่ม',
      buttonStyleDesc: 'เลือกรูปแบบปุ่มสำหรับทั้งระบบ',
      countryCodeHint: 'เช่น 66 สำหรับไทย, 1 สำหรับสหรัฐฯ',
      countryCodeExplain: 'กรอกเป็นตัวเลขเท่านั้น ใช้เติมหน้าหมายเลขโทรศัพท์ในฟอร์มลูกค้า',
      saving: 'กำลังบันทึก...',
      saveSettings: 'บันทึกการตั้งค่า',
      saved: 'บันทึกแล้ว',
      modernStyle: 'สไตล์สมัยใหม่',
      classicStyle: 'สไตล์คลาสสิก',
      originalStyle: 'สไตล์ดั้งเดิม',
      btnPrimary: 'ปุ่มหลัก',
      btnSecondary: 'ปุ่มรอง',
      btnGhost: 'โกสต์',
      btnDanger: 'อันตราย',
      modernDesc: 'พื้นหลังไล่สีพร้อมแอนิเมชันและมุมโค้งมน',
      classicDesc: 'ดีไซน์เรียบง่าย ใช้สีตามธีมและเส้นขอบบางๆ',
      originalDesc: 'ดีไซน์ปุ่มเดิมก่อนปรับปรุงโค้ด',
      timeFormatExample1: '15 กันยายน 2025 6:09 หลังเที่ยง',
      timeFormatExample2: 'กันยายน 15 2025 6:09 หลังเที่ยง',
      timeFormatExample3: '15 กันยายน 2025 18:09',
      timeFormatExample4: 'กันยายน 15 2025 18:09'
    },
    common: {
      language: 'ภาษา',
      phoneTooShort: 'หมายเลขโทรศัพท์ต้องมีอย่างน้อย 7 หลัก',
      phoneTooLong: 'หมายเลขโทรศัพท์ยาวเกินไป (สูงสุด 15 หลัก)',
      theme: 'ธีม',
      save: 'บันทึก',
      cancel: 'ยกเลิก',
      logout: 'ออกจากระบบ',
      loading: 'กำลังโหลด...',
      reset: 'ล้างค่า',
      close: 'ปิด',
      back: 'ย้อนกลับ',
      delete: 'ลบ',
      clearAll: 'ล้างทั้งหมด',
      upload: 'อัปโหลด',
      uploading: 'กำลังอัปโหลด...',
      selectPhoto: 'เลือกรูปภาพ',
      addNotes: 'เพิ่มหมายเหตุ...',
      notes: 'หมายเหตุ',
      optional: 'ไม่บังคับ',
      retake: 'ถ่ายใหม่',
      usePhoto: 'ใช้รูปภาพ',
      viewAll: 'ดูรูปภาพทั้งหมด',
      confirmDelete: 'แน่ใจหรือไม่ว่าต้องการลบรูปภาพนี้?',
      add: 'เพิ่ม',
      copied: 'คัดลอกแล้ว',
      expand: 'ขยาย',
      collapse: 'ย่อ',
      updateFailed: 'อัปเดตไม่สำเร็จ',
      updateFailedWithMessage: 'อัปเดตไม่สำเร็จ: {{message}}',
      failedToSendEmail: 'ส่งอีเมลไม่สำเร็จ',
      statusChangeNotAllowed: 'ไม่สามารถเปลี่ยนสถานะนี้ได้',
      expenseAddedSuccessfully: 'เพิ่มค่าใช้จ่ายเรียบร้อย',
      edit: 'แก้ไข'
    },
    toasts: {
      contractCreated: 'สร้างสัญญาเรียบร้อย',
      contractUpdated: 'อัปเดตสัญญาเรียบร้อย',
      contractDeleted: 'ลบสัญญาเรียบร้อย',
      contractCreateFailed: 'สร้างสัญญาไม่สำเร็จ',
      contractUpdateFailed: 'อัปเดตสัญญาไม่สำเร็จ',
      contractDeleteFailed: 'ลบสัญญาไม่สำเร็จ',
      scooterCreated: 'เพิ่มรถเรียบร้อย',
      scooterUpdated: 'อัปเดตรถเรียบร้อย',
      scooterDeleted: 'ลบรถเรียบร้อย',
      scooterStatusUpdated: 'อัปเดตสถานะรถเรียบร้อย',
      scooterStatusUpdateFailed: 'อัปเดตสถานะรถไม่สำเร็จ',
      clientCreated: 'เพิ่มลูกค้าเรียบร้อย',
      clientUpdated: 'อัปเดตข้อมูลลูกค้าเรียบร้อย',
      clientDeleted: 'ลบลูกค้าเรียบร้อย',
      settingsUpdated: 'บันทึกการตั้งค่าเรียบร้อย',
      scooterStatusChangedAuto: 'ระบบเปลี่ยนสถานะรถให้อัตโนมัติ',
      scooterStatusChangeDesc: '{{scooterName}} เปลี่ยนสถานะจาก {{fromStatus}} เป็น {{toStatus}} {{reason}}',
      becauseContractActivated: 'เพราะสัญญาถูกเปิดใช้งาน',
      becauseContractDeactivated: 'เพราะสัญญาถูกปิดหรือเก็บถาวร',
      automatically: 'อัตโนมัติ',
      viewScooter: 'ดูรายละเอียดรถ',
      scooterOpenedFromNotif: 'เปิด {{scooterName}} แล้ว',
      scooterOpenedFromNotifDesc: 'เปิดรถคันนี้จากแจ้งเตือนการเปลี่ยนสถานะ',
      closedContractsMoveRestriction: 'สัญญาที่ปิดแล้วสามารถย้ายไปเก็บถาวรได้เท่านั้น'
    },
    login: {
      title: 'เข้าสู่ระบบ',
      email: 'อีเมล',
      username: 'ชื่อผู้ใช้',
      password: 'รหัสผ่าน',
      signIn: 'เข้าสู่ระบบ',
      signingIn: 'กำลังเข้าสู่ระบบ...',
      login: 'เข้าสู่ระบบ',
      loggingIn: 'กำลังเข้าสู่ระบบ...',
      loginFailed: 'เข้าสู่ระบบไม่สำเร็จ',
      language: 'ภาษา'
    },
    sidebar: {
      scooters: 'รถสกู๊ตเตอร์',
      clients: 'ลูกค้า',
      contracts: 'สัญญาเช่า',
      finance: 'การเงิน',
      users: 'ผู้ใช้',
      activity: 'บันทึกกิจกรรม',
      management: 'การจัดการ',
      howItWorks: 'วิธีใช้งาน',
      signIn: 'เข้าสู่ระบบ',
      expand: 'ขยายแถบข้าง',
      collapse: 'ย่อแถบข้าง',
      logoutConfirm: 'ต้องการออกจากระบบใช่ไหม?'
    },
    toolbar: {
      searchPlaceholder: 'ค้นหาไอดี รุ่น หรือสถานะ…',
      clear: 'ล้าง',
      listView: 'มุมมองรายการ',
      kanbanView: 'มุมมองคัมบัง',
      calendarView: 'ปฏิทิน',
      gridView: 'มุมมองกริด',
      mapView: 'มุมมองแผนที่',
      filter: 'ตัวกรอง',
      export: 'ส่งออก',
      total: 'ทั้งหมด {{count}} รายการ',
      queryLabel: 'ค้นหา',
      clearQuery: 'ล้างการค้นหา',
      removeFilter: 'ลบ {{filter}}'
    },
    ui: {
      zoom: 'ซูม'
    },
    calendar: {
      week: 'สัปดาห์',
      month: 'เดือน',
      all: 'ทั้งหมด',
      plusMore: '+{{count}} รายการ',
      scrollHint: 'กด Shift แล้วเลื่อนเมาส์เพื่อเลื่อนในแนวนอน',
      hideEmpty: 'ซ่อนรถที่ไม่มีสัญญาในช่วงนี้'
    },
    status: {
      available: 'พร้อมให้เช่า',
      rented: 'กำลังเช่า',
      maintenance: 'ซ่อมบำรุง',
      retired: 'ปลดระวาง',
      draft: 'ฉบับร่าง',
      active: 'ใช้งานอยู่',
      closed: 'ปิดแล้ว',
      archived: 'เก็บถาวร'
    },
    clients: {
      id: 'รหัสลูกค้า',
      name: 'ชื่อ',
      messenger: 'วอทส์แอป',
      newClient: 'ลูกค้าใหม่',
      creating: 'กำลังเพิ่มลูกค้า...',
      fullName: 'ชื่อ-นามสกุล',
      fullNamePlaceholder: 'กรอกชื่อ-นามสกุล',
      email: 'อีเมล',
      emailPlaceholder: 'กรอกอีเมล',
      localPhone: 'เบอร์โทรในประเทศ',
      localPhonePlaceholder: 'เบอร์โทร (ไม่บังคับ)',
      messengerNumber: 'เบอร์วอทส์แอป',
      countryCodePlaceholder: '66',
      messengerNumberPlaceholder: 'เบอร์วอทส์แอป (ไม่บังคับ)',
      lineId: 'ไลน์ไอดี',
      lineIdPlaceholder: 'ไลน์ไอดี (ไม่บังคับ)',
      whatsappButton: 'วอทส์แอป',
      lineButton: 'ไลน์',
      contactViaWhatsApp: 'ติดต่อผ่านวอทส์แอป',
      contactViaLINE: 'ติดต่อผ่านไลน์',
      notes: 'หมายเหตุ',
      notesPlaceholder: 'ข้อมูลเพิ่มเติม (เช่น เบอร์ต่างประเทศ)',
      addClient: 'ลูกค้าใหม่',
      detailsTitle: 'รายละเอียดลูกค้า',
      created: 'วันที่สร้าง',
      clientId: 'รหัสลูกค้า',
      edit: 'แก้ไขลูกค้า',
      previousContracts: 'สัญญาเก่า',
      noCurrentContracts: 'ยังไม่มีสัญญาปัจจุบัน',
      editClient: 'แก้ไขลูกค้า',
      newContractBtn: 'สร้างสัญญา',
      close: 'ปิด',
      statusHistory: 'ประวัติสถานะ',
      unknownClient: 'ลูกค้าไม่ทราบชื่อ'
    },
    scooters: {
      id: 'หมายเลขรถ',
      modelRequired: 'กรุณาระบุรุ่นรถ',
      batteryRange: 'แบตเตอรี่ต้องอยู่ระหว่าง 0–100%',
      odometerNegative: 'เลขไมล์ต้องไม่เป็นค่าติดลบ',
      fuelRange: 'เปอร์เซ็นต์น้ำมันต้องอยู่ระหว่าง 0–100%',
      incompatibleEnergy: 'รถไฟฟ้าไม่มีน้ำมัน และรถน้ำมันไม่มีแบตเตอรี่',
      editScooter: 'แก้ไขรถ',
      newScooter: 'เพิ่มรถ',
      updating: 'กำลังบันทึก...',
      creating: 'กำลังเพิ่มรถ...',
      model: 'รุ่น',
      modelPlaceholder: 'เช่น Ninebot Max',
      status: 'สถานะ',
      typeLevel: 'ประเภทและระดับ',
      odometer: 'เลขไมล์',
      qrCode: 'คิวอาร์โค้ด',
      qrPlaceholder: 'คิวอาร์โค้ด (ไม่บังคับ)',
      plateNumber: 'หมายเลขทะเบียน',
      platePlaceholder: 'หมายเลขทะเบียน (ไม่บังคับ)',
      nickname: 'ชื่อเล่น',
      nicknamePlaceholder: 'เช่น ฮอนด้าแดง #1',
      nicknameOptional: 'ชื่อเล่น (ไม่บังคับ)',
      brand: 'ยี่ห้อ',
      brandPlaceholder: 'ยี่ห้อ (ไม่บังคับ)',
      vehicleType: 'ประเภทพาหนะ',
      electric: 'ไฟฟ้า',
      fuel: 'น้ำมัน',
      batteryPct: 'แบตเตอรี่ %',
      fuelPct: 'น้ำมัน %',
      odometerKm: 'เลขไมล์ (กม.)',
      updateScooter: 'บันทึก',
      addScooter: 'เพิ่มรถ',
      details: 'รายละเอียด',
      edit: 'แก้ไข',
      created: 'วันที่สร้าง',
      statusHistory: 'ประวัติสถานะ',
      columnScooterId: 'หมายเลขรถ',
      columnBrand: 'ยี่ห้อ',
      columnModel: 'รุ่น',
      columnPlateNumber: 'หมายเลขทะเบียน',
      columnNickname: 'ชื่อเล่น',
      columnStatus: 'สถานะ',
      columnVehicleStatus: 'ประเภทและระดับ',
      columnOdometer: 'เลขไมล์',
      pictures: 'รูปภาพ',
      noPhotos: 'ยังไม่มีรูปภาพ',
      noPhotosYet: 'ยังไม่มีรูปภาพ',
      allPhotos: 'รูปภาพทั้งหมด',
      viewAllPhotos: 'ดูรูปภาพทั้งหมด',
      damage: 'ความเสียหาย',
      damageOnly: 'ความเสียหาย',
      showingDamage: 'แสดงความเสียหาย',
      noDamagePhotos: 'ไม่มีรูปภาพที่มีความเสียหาย',
      markAsDamage: 'ทำเครื่องหมายว่ามีความเสียหาย',
      markDamage: 'ทำเครื่องหมายความเสียหาย',
      removeDamage: 'ลบเครื่องหมายความเสียหาย',
      markNoDamage: 'ทำเครื่องหมายว่าไม่มีความเสียหาย',
      damageDetected: 'พบความเสียหาย',
      scooterPhoto: 'รูปภาพรถสกู๊ตเตอร์',
      selectImageFile: 'กรุณาเลือกไฟล์รูปภาพ',
      fileSizeLimit: 'ขนาดไฟล์ต้องไม่เกิน 10MB',
      photoUploaded: 'อัปโหลดรูปภาพเรียบร้อย',
      photoDeleted: 'ลบรูปภาพเรียบร้อย',
      photoDeleteFailed: 'ลบรูปภาพไม่สำเร็จ',
      photoUpdateFailed: 'อัปเดตรูปภาพไม่สำเร็จ',
      photoUploadFailed: 'อัปโหลดรูปภาพไม่สำเร็จ',
      photoStorageNotConfigured: 'ยังไม่ได้ตั้งค่าระบบจัดเก็บรูปภาพ กรุณาติดต่อผู้ดูแลระบบ',
      deletePhoto: 'ลบรูปภาพ',
      drawCircle: 'วาดวงกลม',
      stopDrawing: 'หยุดวาด',
      undo: 'ยกเลิก',
      redo: 'ทำซ้ำ',
      capture: 'ถ่ายภาพ',
      takePhoto: 'ถ่ายภาพ',
      chooseFromGallery: 'เลือกจากแกลเลอรี',
      addPhoto: 'เพิ่มรูปภาพ',
      photoPreview: 'ตัวอย่างรูปภาพ',
      cameraError: 'ไม่สามารถเข้าถึงกล้องได้',
      cameraPermissionDenied: 'ปฏิเสธการเข้าถึงกล้อง'
    },
    pages: {
      manageColumns: 'จัดการคอลัมน์',
      columns: 'คอลัมน์',
      retiredHidden: 'ซ่อนรถที่ปลดระวาง',
      failedLoadScooters: 'โหลดรายการรถไม่สำเร็จ',
      checkConnection: 'กรุณาตรวจสอบการเชื่อมต่อแล้วลองอีกครั้ง',
      retry: 'ลองใหม่',
      clearFilters: 'ล้างตัวกรอง',
      addScooter: 'รถสกู๊ตเตอร์ใหม่',
      noScootersYet: 'ยังไม่มีรถ',
      noScootersMatch: 'ไม่พบรถที่ตรงกับตัวกรอง',
      addFirstScooter: 'เพิ่มรถคันแรกเพื่อเริ่มใช้งาน',
      tryAdjusting: 'ลองปรับหรือล้างตัวกรองดู',
      rowsPerPage: 'แถวต่อหน้า',
      pageOf: 'หน้า {{current}} จาก {{total}}',
      prevShort: 'ก่อนหน้า',
      nextShort: 'ถัดไป',
      prev: 'หน้าก่อนหน้า',
      next: 'หน้าถัดไป'
    },
    // Contracts page labels (Thai)
    contractsPage: {
      newContract: 'สร้างสัญญา',
      showArchived: 'แสดงสัญญาที่เก็บถาวร',
      newContractSubtitle: 'สร้างและตรวจสอบสัญญาเช่าได้อย่างรวดเร็ว',
      createdDate: 'วันที่สร้าง',
      recentlyUpdated: 'อัปเดตล่าสุด',
      recentlyClosed: 'ปิดล่าสุด',
      allClosed: 'ปิดทั้งหมด',
      showAll: 'แสดงทั้งหมด',
      showRecent: 'แสดงล่าสุด',
      sortBy: 'จัดเรียงตาม...',
      archivedOnlyToDraft: 'สัญญาที่เก็บถาวรสามารถย้ายไปเป็นฉบับร่างได้เท่านั้น'
    },
    activity: {
      title: 'บันทึกกิจกรรม',
      subtitle: 'ติดตามกิจกรรมของพนักงานและการเปลี่ยนแปลงของระบบ เพื่อตรวจสอบและแก้ไขปัญหา',
      filterActivity: 'ตัวกรองกิจกรรม',
      timePeriod: 'ช่วงเวลา',
      lastHour: 'ชั่วโมงที่ผ่านมา',
      today: 'วันนี้',
      thisWeek: 'สัปดาห์นี้',
      thisMonth: 'เดือนนี้',
      custom: 'กำหนดเอง',
      from: 'จาก',
      to: 'ถึง',
      employee: 'พนักงาน',
      type: 'ประเภทกิจกรรม',
      allEmployees: 'พนักงานทั้งหมด',
      allActivities: 'ทุกกิจกรรม',
      scooterMgmt: 'จัดการรถสกู๊ตเตอร์',
      contractMgmt: 'จัดการสัญญา',
      clientMgmt: 'จัดการลูกค้า',
      orgSettings: 'ตั้งค่าองค์กร',
      searchEverything: 'ค้นหาทั้งหมด',
      searchPlaceholder: 'ค้นหาชื่อ ไอดี ราคา สถานะ...'
    },
    contracts: {
      filters: 'ตัวกรอง',
      editContract: 'แก้ไขสัญญา',
      dates: 'วันเวลา',
      for: 'สำหรับ',
      scooter: 'รถสกู๊ตเตอร์',
      start: 'เริ่ม',
      end: 'สิ้นสุด',
      duration: 'ระยะเวลา',
      days: 'วัน',
      weeks: 'สัปดาห์',
      months: 'เดือน',
      estimatedTotal: 'ราคารวมโดยประมาณ',
      fetchingData: 'กำลังโหลดข้อมูลลูกค้าและรถ...',
      scooterCurrentlyRented: 'ℹ️ รถคันนี้กำลังเช่าอยู่{{until}}',
      until: 'ถึง',
      futureReservationNote: 'สามารถสร้างสัญญาสำหรับการจองล่วงหน้าได้ แต่ต้องตรวจสอบให้แน่ใจว่าเวลาไม่ทับซ้อนกัน',
      searchClients: 'ค้นหาลูกค้าด้วยชื่อ/อีเมล/ไอดี',
      noMatches: 'ไม่พบรายการ',
      selected: 'เลือกแล้ว',
      scooterQuery: 'รถสกู๊ตเตอร์ (ค้นหาไอดีหรือรุ่น)',
      scooterPlaceholder: 'เช่น Ninebot หรือ UUID',
      dailyRateRange: 'ราคาต่อวัน (ต่ำสุด/สูงสุด)',
      min: 'ต่ำสุด',
      max: 'สูงสุด',
      minExceedsMax: 'ค่าต่ำสุดต้องไม่มากกว่าค่าสูงสุด',
      endAfterStart: 'เวลาสิ้นสุดต้องอยู่หลังเวลาเริ่ม',
      clientRequired: 'กรุณาเลือกลูกค้า',
      scooterRequired: 'กรุณาเลือกรถ',
      startTimeRequired: 'กรุณาระบุเวลาเริ่ม',
      dailyRatePositive: 'ราคาต่อวันต้องมากกว่า 0',
      addContract: 'สัญญาใหม่',
      client: 'ลูกค้า',
      selectClient: 'เลือกลูกค้า',
      searchScooters: 'ค้นหารถ...',
      startTime: 'เวลาเริ่ม',
      status: 'สถานะ',
      dailyRate: 'ราคาต่อวัน',
      deposit: 'มัดจำ',
      endTime: 'เวลาสิ้นสุด',
      contractId: 'รหัสสัญญา',
      paymentMethod: 'วิธีชำระเงิน',
      cash: 'เงินสด',
      local_bank_transfer: 'โอนธนาคารในประเทศ',
      international_bank_transfer: 'โอนธนาคารระหว่างประเทศ',
      notes: 'หมายเหตุ',
      notesPlaceholder: 'หมายเหตุเพิ่มเติม',
      createDraftForScooter: 'สร้างสัญญาฉบับร่างสำหรับรถคันนี้',
      saveAsDraftHint: 'บันทึกเป็นฉบับร่างเพื่อเปิดใช้งานภายหลัง',
      startImmediatelyHint: 'เริ่มสัญญาทันที',
      creating: 'กำลังสร้าง...',
      createDraft: 'สร้างเป็นฉบับร่าง',
      createActive: 'สร้างและเปิดใช้งาน',
      day: 'วัน',
      forDays: 'เป็นเวลา {{count}} วัน',
      forDays_plural: 'เป็นเวลา {{count}} วัน',
      total: 'รวม',
      dayOf: 'วันที่ {{day}} จาก {{total}}'
    },
    contractsUi: {
      renting: 'เช่า',
      statusHistory: 'ประวัติสถานะ',
      noHistory: 'ยังไม่มีประวัติการเปลี่ยนแปลง',
      created: 'สร้าง',
      overdue: 'เกินกำหนด',
      overdueTitle: 'สัญญาเกินกำหนด',
      remainingTitle: 'เวลาที่เหลือจนสิ้นสุดสัญญา',
      remaining: 'เหลือ',
      unitDayShort: 'วัน',
      unitHourShort: 'ชม.',
      unitMinuteShort: 'นาที',
      clientDetails: 'รายละเอียดลูกค้า',
      scooterDetails: 'รายละเอียดรถ',
      clientNotFound: 'ไม่พบข้อมูลลูกค้า',
      scooterNotFound: 'ไม่พบข้อมูลรถ',
      fullName: 'ชื่อ-นามสกุล',
      localPhone: 'เบอร์โทรในประเทศ',
      messenger: 'วอทส์แอป',
      brand: 'ยี่ห้อ',
      vehicleType: 'ประเภทพาหนะ',
      battery: 'แบตเตอรี่',
      qrCode: 'คิวอาร์โค้ด',
      scooterStatus: 'สถานะรถ',
      noStatusChanges: 'ยังไม่มีการเปลี่ยนสถานะ',
      loadingHistory: 'กำลังโหลดประวัติ...',
      characters: 'ตัวอักษร',
      back: 'ย้อนกลับ',
      startDate: 'วันเริ่มต้น',
      endDate: 'วันสิ้นสุด',
      contractId: 'รหัสสัญญา',
      editContract: 'แก้ไขสัญญา',
      duplicate: 'ทำสำเนา',
      duplicateContract: 'ทำสำเนาสัญญา',
      duplicateSubtitle: 'เลือกวันเริ่ม ระยะเวลา และวันสิ้นสุดสำหรับฉบับร่างใหม่',
      endTimeMustBeAfter: 'เวลาสิ้นสุดต้องอยู่หลังเวลาเริ่มต้น',
      changeStatus: 'เปลี่ยนสถานะ',
      currentStatus: 'ปัจจุบัน',
      changeScooterStatus: 'เปลี่ยนสถานะรถ',
      currentScooterStatus: 'ปัจจุบัน',
      showingRange: 'แสดง {{start}}–{{end}} จาก {{total}}',
      contractCreatedTitle: 'สร้างสัญญาเรียบร้อย',
      contractCreatedMessage: 'ต้องการเปิดดูสัญญาที่สร้างไหม?',
      contractLabel: 'สัญญา',
      openContract: 'เปิดดูสัญญา',
      stayHere: 'อยู่หน้านี้',
      scooterStatusQuestion: 'ต้องการให้รถ "{{model}}" มีสถานะอะไร?',
      currentStatusLabel: 'สถานะปัจจุบัน',
      statusChangeHelp: 'ช่วยให้คุณจัดการความพร้อมของรถหลังการเปลี่ยนสถานะสัญญา',
      working: 'กำลังดำเนินการ...',
      moveContractAndUpdate: 'ย้ายสัญญาและอัปเดตรถ',
      enterToConfirm: 'กด Enter เพื่อยืนยัน หรือ Esc เพื่อยกเลิก',
      statusAvailable: 'พร้อมให้เช่า',
      statusAvailableDesc: 'รถพร้อมปล่อยเช่า',
      statusRented: 'กำลังเช่า',
      statusRentedDesc: 'รถกำลังถูกเช่าอยู่',
      statusMaintenance: 'ซ่อมบำรุง',
      statusMaintenanceDesc: 'รถต้องการการซ่อมบำรุง',
      statusRetired: 'ปลดระวาง',
      statusRetiredDesc: 'รถหมดสภาพแล้ว',
      statusCurrent: 'ปัจจุบัน',
      moveContractTitle: 'ย้ายสัญญา',
      moveContractMessage: 'กรุณาเลือกสถานะใหม่สำหรับรถเมื่อย้ายสัญญานี้',
      fromLabel: 'จาก',
      toLabel: 'ไปยัง',
      clientLabel: 'ลูกค้า',
      scooterLabel: 'รถ',
      currentContract: 'สัญญาปัจจุบัน',
      viewContract: 'ดูสัญญา'
    },
    contractDocument: {
      print: 'พิมพ์',
      saveAsPDF: 'บันทึกเป็น PDF',
      email: 'อีเมล',
      addSignature: 'เพิ่มลายเซ็น',
      emailRequired: 'กรุณากรอกอีเมล',
      emailSent: 'ส่งสัญญาเรียบร้อย',
      enterSignatureName: 'กรอกชื่อผู้ลงนามเพื่อเพิ่มในเอกสาร',
      loading: 'กำลังโหลดสัญญา...',
      preparingPDF: 'กำลังเตรียมไฟล์ PDF...',
      downloadSuccess: 'ดาวน์โหลด PDF เรียบร้อย',
      downloadFailed: 'สร้างไฟล์ PDF ไม่สำเร็จ',
      retry: 'ลองอีกครั้ง',
      emailContract: 'ส่งสัญญาทางอีเมล',
      recipientEmail: 'อีเมลผู้รับ',
      sending: 'กำลังส่ง...',
      send: 'ส่ง'
    },
    businessDetails: {
      title: 'ข้อมูลธุรกิจ',
      settingsUpdated: 'บันทึกข้อมูลธุรกิจเรียบร้อย',
      errorLoading: 'โหลดข้อมูลธุรกิจไม่สำเร็จ',
      helpText: 'กรอกข้อมูลธุรกิจด้านล่าง ข้อมูลนี้จะแสดงในสัญญาเช่า หากเว้นว่าง ระบบจะแสดงช่องว่างเพื่อให้กรอกด้วยลายมือเมื่อพิมพ์',
      businessName: 'ชื่อธุรกิจ',
      businessNamePlaceholder: 'กรอกชื่อธุรกิจ',
      businessAddress: 'ที่อยู่ธุรกิจ',
      businessAddressPlaceholder: 'กรอกที่อยู่ธุรกิจ',
      businessPhone: 'เบอร์โทรธุรกิจ',
      businessPhonePlaceholder: '+66 2 123 4567',
      businessEmail: 'อีเมลธุรกิจ',
      businessEmailPlaceholder: 'business@example.com',
      taxId: 'เลขภาษี / เลขทะเบียนธุรกิจ',
      taxIdPlaceholder: '1234567890123',
      representativeName: 'ชื่อผู้มีอำนาจลงนาม',
      representativeNamePlaceholder: 'สมชาย ใจดี',
      representativeIdNumber: 'เลขบัตรประชาชนผู้แทน',
      representativeIdNumberPlaceholder: '1234567890123',
      saving: 'กำลังบันทึก...',
      saveDetails: 'บันทึกข้อมูลธุรกิจ',
      saved: 'บันทึกแล้ว'
    },
    messaging: {
      title: 'ติดต่อลูกค้า',
      scanQR: 'สแกน QR Code',
      orClickLink: 'หรือคลิกลิงก์ด้านล่าง',
      openApp: 'เปิดแอปแชต',
      selectTemplate: 'เลือกข้อความสำเร็จรูป',
      customMessage: 'ข้อความกำหนดเอง',
      messageEditor: 'ตัวแก้ไขข้อความ',
      thankYou: 'ขอบคุณ',
      reminder: 'เตือนสัญญา',
      payment: 'เตือนชำระเงิน',
      templateThankYou: 'ขอบคุณที่เลือกใช้บริการ {{businessName}}! หวังว่าคุณจะได้รับประสบการณ์ที่ดีในการเช่ารถสกู๊ตเตอร์ หวังว่าจะได้บริการคุณอีกครั้ง',
      templateReminder: 'สวัสดีคุณ {{clientName}}! นี่คือ {{businessName}} เตือนว่าระยะเวลาเช่าจะสิ้นสุดพรุ่งนี้ กรุณาคืนรถด้วย',
      templatePayment: 'สวัสดีคุณ {{clientName}}! นี่คือ {{businessName}} เตือนชำระเงินสำหรับสัญญา #{{contractId}}',
      noWhatsApp: 'ไม่มีเบอร์วอทส์แอป',
      noLINE: 'ไม่มีไลน์ไอดี',
      close: 'ปิด'
    },
    finance: {
      title: 'ภาพรวมการเงิน',
      exportCSV: 'ส่งออก CSV',
      timeRange: 'ช่วงเวลา',
      day: 'วัน',
      week: 'สัปดาห์',
      month: 'เดือน',
      year: 'ปี',
      all: 'ทั้งหมด',
      searchPlaceholder: 'ค้นหารถ...',
      showSoldOnly: 'แสดงเฉพาะที่ขายแล้ว',
      totalPurchase: 'ค่าซื้อรวม',
      totalMaintenance: 'ค่าซ่อมบำรุงรวม',
      totalIncome: 'รายได้รวม',
      netProfit: 'กำไรสุทธิ',
      topROIScooter: 'รถที่ให้ผลตอบแทนสูงสุด',
      bestBrandModel: 'ยี่ห้อ/รุ่นที่ดีที่สุด',
      scooters: 'รถ',
      scooter: 'รถ',
      purchase: 'ค่าซื้อ',
      maintenance: 'ซ่อมบำรุง',
      income: 'รายได้',
      sale: 'ราคาขาย',
      net: 'กำไร/ขาดทุน',
      roi: 'ผลตอบแทน',
      status: 'สถานะ',
      profitable: 'ทำกำไร',
      breakingEven: 'คืนทุน',
      lossMaker: 'ขาดทุน',
      totals: 'รวม',
      recentExpenses: 'ค่าใช้จ่ายล่าสุด',
      noExpenses: 'ยังไม่มีค่าใช้จ่าย',
      loadingExpenses: 'กำลังโหลดค่าใช้จ่าย...',
      noExpensesInRange: 'ไม่มีค่าใช้จ่ายในช่วงเวลาที่เลือก',
      expenseTotal: 'รวม',
      categoryOilChange: 'เปลี่ยนน้ำมัน',
      categoryTires: 'ยาง',
      categoryBrakes: 'เบรก',
      categoryEngine: 'เครื่องยนต์',
      categoryBattery: 'แบตเตอรี่',
      categoryBodyParts: 'ตัวถัง',
      categoryCleaning: 'ทำความสะอาด',
      categoryRegistration: 'จดทะเบียน',
      categoryInsurance: 'ประกันภัย',
      categoryMiscParts: 'อะไหล่อื่นๆ',
      categoryCustom: 'กำหนดเอง',
      affectsExpenseDetails: 'ส่งผลต่อรายละเอียดค่าใช้จ่าย',
      tableView: 'ตาราง',
      chartsView: 'กราฟ',
      toggleKPIs: 'สลับตัวชี้วัด',
      toggleInsights: 'สลับข้อมูลเชิงลึก',
      toggleAdditionalTables: 'สลับตารางเพิ่มเติม',
      showOperational: 'แสดงมุมมองการดำเนินงาน',
      showFull: 'แสดงมุมมองเต็มรูปแบบ',
      operationalView: 'มุมมองการดำเนินงาน',
      fullView: 'มุมมองเต็มรูปแบบ',
      operationalNet: 'กำไรจากการดำเนินงาน',
      date: 'วันที่',
      category: 'หมวดหมู่',
      amount: 'จำนวนเงิน',
      note: 'หมายเหตุ',
      actions: 'การดำเนินการ',
      edit: 'แก้ไข',
      delete: 'ลบ',
      editExpense: 'แก้ไขค่าใช้จ่าย',
      customLabel: 'ป้ายกำกับกำหนดเอง',
      confirmDeleteExpense: 'แน่ใจหรือไม่ว่าต้องการลบค่าใช้จ่ายนี้?',
      expenseDeleted: 'ลบค่าใช้จ่ายแล้ว',
      deleteFailed: 'ลบค่าใช้จ่ายไม่สำเร็จ',
      expenseUpdated: 'อัปเดตค่าใช้จ่ายแล้ว',
      updateFailed: 'อัปเดตค่าใช้จ่ายไม่สำเร็จ',
      expenseCreated: 'สร้างค่าใช้จ่ายแล้ว',
      addExpense: 'เพิ่มค่าใช้จ่าย',
      brandModelPerformance: 'ประสิทธิภาพยี่ห้อ/รุ่น',
      brand: 'ยี่ห้อ',
      model: 'รุ่น',
      count: 'จำนวน',
      avgROI: 'ผลตอบแทนเฉลี่ย',
      topExpenses: 'ค่าใช้จ่ายสูงสุดตามหมวดหมู่',
      noData: 'ไม่มีข้อมูล',
      monthlyTrends: 'แนวโน้มทางการเงินรายเดือน',
      expenseBreakdown: 'รายละเอียดค่าใช้จ่ายตามหมวดหมู่',
      maintenanceEfficiency: 'ประสิทธิภาพการซ่อมบำรุง',
      topPerformers: 'ผลการดำเนินงานที่ดีที่สุด',
      needsAttention: 'ต้องการความสนใจ',
      fleetHealth: 'ภาพรวมสุขภาพฟลีต',
      totalScooters: 'รถทั้งหมด',
      revenueVsMaintenance: 'รายได้เทียบกับค่าซ่อมบำรุงตามรถ',
      profitTrend: 'แนวโน้มกำไรตามรถ',
      statusDistribution: 'การกระจายสถานะ',
      exportBasic: 'ส่งออกสรุปพื้นฐาน',
      exportDetailed: 'ส่งออกค่าใช้จ่ายรายละเอียด',
      exportingDetailed: 'กำลังส่งออกค่าใช้จ่ายรายละเอียด...',
      exportCompleted: 'ส่งออกเสร็จสิ้น',
      exportFailed: 'ส่งออกไม่สำเร็จ'
    },
    landing: {
      heroTitle: 'ปี 2026 แล้ว ยังใช้สเปรดชีตอยู่หรือเปล่า',
      heroSubtitle: 'เปลี่ยนมาใช้ระบบดิจิทัลกันเถอะ',
      heroDescription: 'แพลตฟอร์มเช่ารถสกู๊ตเตอร์ที่ใช้งานได้จริง',
      heroDescription2: 'หยุดเสียเงิน เริ่มทำอย่างมีเหตุผล',
      ctaButton: 'ดูการทำงาน →',
      ctaLink: 'หรือทำบนกระดาษต่อไป ตามใจคุณ',
      contractTimeLabel: 'เวลาเฉลี่ยในการสร้างสัญญา',
      contractTimeHint: 'นี่คือความเร็วที่คุณสร้างสัญญาได้เลย ไม่ต้องรอ ไม่มีแล็ก',
      problemsTitle: 'คุณอาจทำผิดอยู่',
      problemsSubtitle: '(เราพูดตามตรง)',
      problem1Title: 'สเปรดชีตไม่ใช่โมเดลธุรกิจ',
      problem1Desc: 'ถ้า Excel ล่ม ธุรกิจคุณก็ล่ม นั่นไม่ใช่ความยืดหยุ่น นั่นคือการเสี่ยงกับรายได้ของคุณ ตื่นได้แล้ว - นี่ไม่ใช่ปี 2015 แล้ว',
      problem2Title: 'คุณกำลังเสีย 15-30% ไปกับข้อผิดพลาดด้วยมือ',
      problem2Desc: 'รถจองซ้ำ การต่ออายุที่พลาด สัญญาที่หายไป ทุกขั้นตอนที่ทำด้วยมือคือเงินที่ไหลออกประตู หยุดได้แล้ว',
      problem3Title: '\'ระบบ\' ของคุณคือ WhatsApp + ความจำ',
      problem3Desc: 'ข้อความลูกค้ากระจายไป 3 แอป วันที่สัญญาอยู่ในหัว การติดตามการชำระเงินอยู่ในสมุดบันทึก นี่ไม่ใช่ปี 2010 แล้ว ตามทันหน่อย',
      problem4Title: 'คุณไม่สามารถขยายสิ่งที่คุณวัดไม่ได้',
      problem4Desc: 'รถคันไหนทำกำไรมากที่สุด? ลูกค้าคนไหนเช่านานที่สุด? ROI จริงของคุณคือเท่าไหร่? ถ้าไม่รู้ คุณกำลังบินตาบอด และนั่นแพง',
      challengeTitle: 'คิดว่าเราผิด?',
      challengeText: 'พิสูจน์ให้ดู แสดงระบบกระดาษของคุณมา แล้วเราจะแสดงให้เห็นว่าปี 2026 เป็นอย่างไร',
      challengeButton: 'ดูว่าปี 2026 เป็นอย่างไร →',
      featuresTitle: 'สิ่งที่คุณจะได้ (ที่คุณไม่รู้ว่าคุณต้องการ)',
      featuresSubtitle: 'ฟังก์ชันจริง ความเร็วจริง ผลลัพธ์จริง',
      featuresSubtitle2: 'ไม่มีน้ำ ไม่มีแค่ข้อเท็จจริง',
      feature1Title: 'แดชบอร์ดฟลีตแบบเรียลไทม์',
      feature1Desc: 'เห็นทุกอย่าง รู้ทุกอย่าง ทำทุกอย่าง พร้อมใช้งาน เช่า ซ่อมบำรุง - ทั้งหมดในมุมมองเดียว ไม่ต้องรอให้ "ไปตรวจสอบบันทึก" อีกต่อไป',
      feature1Item1: 'อัปเดตสถานะแบบเรียลไทม์ทั่วทั้งฟลีต',
      feature1Item2: 'ติดตามรายได้ที่สมเหตุสมผล',
      feature1Item3: 'คำนวณ ROI ได้ง่าย ไม่ต้องใช้ปริญญาเอก',
      feature2Title: 'สัญญาที่ไม่แย่',
      feature2Desc: 'สร้างสัญญาได้ในไม่กี่วินาที ติดตามแบบเรียลไทม์ ไม่จองซ้ำ ไม่พลาดวันต่ออายุ ไม่ต้องสงสัยว่า "ใครมีรถ #42?" อีกต่อไป',
      feature2Item1: 'มุมมอง Kanban เพราะสเปรดชีตไม่ใช่ภาพ',
      feature2Item2: 'ตรวจจับความขัดแย้งอัตโนมัติ (ใช่ เราตรวจสอบ)',
      feature2Item3: 'วางแผนการส่งมอบที่ใช้งานได้จริง',
      feature3Title: 'การต่ออายุและการแจ้งเตือน',
      feature3Desc: 'แจ้งเตือนการต่ออายุประกัน การต่อทะเบียน ความคุ้มครอง วันที่สิ้นสุดสัญญา ติดตามทั้งหมดอัตโนมัติ แจ้งเตือนก่อนจะสาย เพราะประกันหมดอายุ = ปัญหาแพง และการต่ออายุสัญญาที่พลาด = รายได้ที่หายไป',
      feature3Item1: 'แจ้งเตือนการต่ออายุประกัน (อย่าปล่อยให้หมดอายุ)',
      feature3Item2: 'แจ้งเตือนสัญญาครบกำหนด (ไม่พลาดการต่ออายุอีกต่อไป)',
      feature3Item3: 'ป้ายสถานะ: ใช้งาน ใกล้ครบกำหนด เกินกำหนด (คุณจะรู้)',
      feature4Title: 'มุมมองปฏิทินที่สมเหตุสมผล',
      feature4Desc: 'ดูตารางฟลีตทั้งหมดในมุมมองเดียว รู้ว่ารถเช่าเมื่อไหร่ วางแผนล่วงหน้า ไม่จองซ้ำ ไม่สับสนว่า "สัญญานี้สิ้นสุดเมื่อไหร่?" อีกต่อไป',
      feature4Item1: 'ไทม์ไลน์ภาพของสัญญาทั้งหมด',
      feature4Item2: 'ดูความพร้อมใช้งานในมุมมองเดียว',
      feature4Item3: 'วางแผนการส่งมอบและการรับมอบอย่างมืออาชีพ',
      feature5Title: 'QR code = ติดต่อลูกค้าทันที',
      feature5Desc: 'สแกน QR code ส่งข้อความลูกค้าทันที ไม่ต้องค้นหาใน WhatsApp อีกต่อไป ไม่ต้องถามว่า "นี่คือใครอีกแล้ว?" อีกต่อไป แค่สแกน ส่งข้อความ เสร็จ นี่คือปี 2026 แล้ว ทำตัวให้เหมาะสม',
      feature5Item1: 'QR code บนรถทุกคัน',
      feature5Item2: 'สแกนครั้งเดียว = WhatsApp/Line พร้อมข้อความที่กรอกไว้ล่วงหน้า',
      feature5Item3: 'ไม่ต้องล่าหาข้อมูลติดต่อลูกค้าอีกต่อไป',
      qrPhoneLabel: 'หมายเลขโทรศัพท์',
      qrPhoneHint: 'ใส่หมายเลขใดก็ได้เพื่อทดสอบ (เช่น หมายเลขแฟนของภรรยาคุณ)',
      qrMessageLabel: 'ข้อความ',
      qrMessageHint: 'แก้ไขข้อความนี้เพื่อปรับแต่งสิ่งที่ลูกค้าเห็น',
      qrScanHint: 'สแกนด้วยโทรศัพท์เพื่อทดสอบ',
      qrClickLink: 'หรือคลิกเพื่อเปิด WhatsApp',
      feature6Title: 'ผู้ใช้ไม่จำกัด ความโปร่งใสเต็มรูปแบบ',
      feature6Desc: 'เพิ่มสมาชิกทีมได้มากเท่าที่ต้องการ ไม่มีค่าธรรมเนียมต่อผู้ใช้ แอดมินเห็นทุกอย่าง - ใครทำอะไร เมื่อไหร่ และทำไม บันทึกกิจกรรมครบถ้วน ไม่มีความลับ ไม่มีเซอร์ไพรส์',
      feature6Item1: 'ผู้ใช้ไม่จำกัด (จริงๆ ไม่มีขีดจำกัด)',
      feature6Item2: 'บันทึกกิจกรรมครบถ้วนสำหรับแอดมิน',
      feature6Item3: 'สิทธิ์ตามบทบาท (ผู้ใช้/แอดมิน)',
      feature7Title: 'การแจ้งเตือนประกันที่ช่วยคุณประหยัดเงิน',
      feature7Desc: 'อย่าปล่อยให้ประกันหมดอายุอีกต่อไป รับการแจ้งเตือนก่อนจะสาย หน้าต่างการต่ออายุที่กำหนดเอง การแจ้งเตือนอัจฉริยะ เพราะประกันหมดอายุ = ปัญหาแพง และเราไม่ชอบปัญหาที่แพง',
      feature7Item1: 'แจ้งเตือนการต่ออายุประกัน (ไม่พลาดกำหนดเวลา)',
      feature7Item2: 'หน้าต่างการแจ้งเตือนที่กำหนดเอง (7 วัน 30 วัน คุณเลือก)',
      feature7Item3: 'แจ้งเตือนแดชบอร์ดที่ดึงความสนใจจริงๆ',
      feature8Title: 'ติดตามการซ่อมบำรุงและวิเคราะห์ความสามารถในการทำกำไร',
      feature8Desc: 'ติดตามค่าใช้จ่ายทุกอย่าง รู้ว่ารถคันไหนทำเงินให้คุณ ดู ROI ต่อรถ ติดตามต้นทุนการซ่อมบำรุง ตัดสินใจตามข้อมูล หยุดเดา',
      feature8Item1: 'ติดตามค่าใช้จ่ายการซ่อมบำรุงครบถ้วน',
      feature8Item2: 'วิเคราะห์ความสามารถในการทำกำไรต่อรถ',
      feature8Item3: 'คำนวณ ROI ที่สำคัญจริงๆ',
      feature9Title: 'รูปภาพและมุมมองมือถือที่ใช้งานได้จริง',
      feature9Desc: 'ถ่ายรูประหว่างเดินทาง อินเทอร์เฟซที่เหมาะกับมือถือ รวมกล้อง แกลเลอรีรูปภาพต่อรถ ทำเครื่องหมายความเสียหายบนรูปภาพโดยตรง กรองตามสถานะความเสียหาย เพราะภาพหนึ่งภาพมีค่าพันคำ และพันคำจะไม่ช่วยคุณในศาล',
      feature9Item1: 'มุมมองมือถือที่เหมาะกับการทำงานภาคสนาม',
      feature9Item2: 'รวมกล้อง - ถ่ายรูปโดยตรงในแอป',
      feature9Item3: 'ทำเครื่องหมายความเสียหายบนรูปภาพด้วยตัวบ่งชี้ภาพ',
      feature9Item4: 'กรองรูปภาพตามสถานะความเสียหาย',
      feature10Title: 'สร้างสัญญาแบบมืออาชีพ',
      feature10Desc: 'กรอกฟิลด์ส่วนใหญ่อัตโนมัติ ดูตัวอย่างก่อนพิมพ์ มีเวอร์ชันไทยและอังกฤษ รูปแบบพร้อมพิมพ์ ไม่ต้องพิมพ์ด้วยมือ ไม่มีข้อผิดพลาด แค่สร้าง ดูตัวอย่าง พิมพ์ เสร็จ',
      feature10Item1: 'กรอกอัตโนมัติจากข้อมูลลูกค้าและรถ',
      feature10Item2: 'ดูตัวอย่างก่อนพิมพ์ (ไม่มีเซอร์ไพรส์)',
      feature10Item3: 'มีเวอร์ชันไทยและอังกฤษ',
      speedTitle: 'เร็วมากจนคุณคิดว่ามันพัง',
      speedDesc: 'React Query caching. Prefetch-on-hover. Token rehydration. Code splitting. Lazy loading. Optimistic updates.',
      speedDesc2: 'เราพูดภาษาของคุณ: ความเร็ว',
      speedMetric1: 'เวลาโหลดหน้า',
      speedMetric2: 'ค้นหาและกรอง',
      speedMetric3: 'รีเฟรชข้อมูล',
      finalCTATitle: 'พร้อมหยุดเสียเงินแล้วหรือยัง?',
      finalCTAText: 'หรือทำบนกระดาษต่อไป เราไม่ใช่เจ้านายคุณ',
      finalCTAText2: '(แต่รายได้ของคุณอาจดีกว่านี้ แค่บอก)',
      finalCTAButton: 'ดูการทำงาน →',
      finalCTALink: 'หรือบอกว่าเราผิด',
      viewFullContract: 'ดูสัญญาเต็มรูปแบบ'
    }
  },
  fil: {
    booking: {
      orgNotFound: 'Hindi nahanap ang organisasyon',
      orgNotFoundDesc: 'Ang booking page na hinahanap mo ay hindi umiiral. Maaaring ibig sabihin nito:',
      orgNotFoundReason1: 'Mali ang slug ng organisasyon',
      orgNotFoundReason2: 'Hindi pa nag-set up ng booking page ang organisasyon',
      orgNotFoundReason3: 'Inalis na ang booking page',
      orgNotFoundOwner: 'Kung ikaw ang may-ari ng organisasyon, tiyaking na-configure mo ang slug ng organisasyon sa Management → Technical Details.',
      requestReceived: 'Natanggap ang Kahilingan!',
      requestReceivedDesc: 'Natanggap na ang iyong booking request. Hindi pa ito kumpirmadong booking.',
      willContact: 'Makikipag-ugnayan sa iyo ang {{business}} sa lalong madaling panahon para kumpirmahin ang iyong rental.',
      requestIdLabel: 'Request ID:',
      contactLabel: 'Kontak:',
      showroomBadge: 'Scooter Showroom',
      bookBadge: 'Mag-book ng Scooter',
      showroomIntro: 'Tingnan ang aming mga scooter, piliin ang gusto mo, at magpadala ng booking request. Kukumpirmahin namin sa iyo agad.',
      bookIntro: 'Isumite ang iyong booking request sa ibaba at makikipag-ugnayan kami sa iyo agad para kumpirmahin.',
      yourDetailsStep: '3. Iyong mga Detalye',
      yourInformation: 'Iyong Impormasyon',
      fullName: 'Buong Pangalan',
      fullNamePlaceholder: 'Ilagay ang buong pangalan',
      email: 'Email',
      emailPlaceholder: 'Ilagay ang email address',
      localPhone: 'Lokal na Numero ng Telepono',
      phonePlaceholder: 'Numero ng telepono',
      messengerNumber: 'Numero ng Messenger (WhatsApp, atbp.)',
      messengerPlaceholder: 'WhatsApp number (opsyonal)',
      sameAsPhone: 'Kapareho ng telepono',
      lineId: 'LINE ID',
      lineIdPlaceholder: 'LINE ID (opsyonal)',
      passportNumber: 'Numero ng Pasaporte',
      passportPlaceholder: 'Numero ng pasaporte (opsyonal)',
      nationality: 'Nasyonalidad',
      nationalityPlaceholder: 'Nasyonalidad (opsyonal)',
      localAddress: 'Lokal na Address',
      localAddressPlaceholder: 'Lokal na address sa bansa (opsyonal)',
      rentalDatesStep: '1. Mga Petsa ng Rental',
      bookingDetails: 'Mga Detalye ng Booking',
      startDate: 'Petsa ng Simula',
      duration: 'Tagal',
      endDate: 'Petsa ng Pagtatapos',
      dayUnit: 'araw',
      daysUnit: 'araw',
      weekUnit: 'linggo',
      weeksUnit: 'linggo',
      monthUnit: 'buwan',
      monthsUnit: 'buwan',
      scooterUnit: 'scooter',
      scootersUnit: 'scooters',
      pricingSummary: 'Buod ng Presyo',
      lineDailyRate: '{{count}} {{unit}} × Araw-araw na Rate:',
      lineWeeklyRate: '{{count}} {{unit}} × Lingguhang Rate:',
      lineDailyFromWeekly: '{{count}} {{unit}} × Araw-araw na Rate (mula sa lingguhan):',
      lineMonthlyRate: '{{count}} {{unit}} × Buwanang Rate:',
      lineDailyFromMonthly: '{{count}} {{unit}} × Araw-araw na Rate (mula sa buwanan):',
      subtotal: 'Subtotal:',
      deposit: 'Deposito:',
      total: 'Kabuuan:',
      selectToSeePricingShowroom: 'Pumili ng mga petsa at scooter para makita ang presyo',
      selectToSeePricingGroup: 'Pumili ng mga petsa at pricing group para makita ang presyo',
      selectPricingGroup: 'Pumili ng Pricing Group',
      perDay: '/araw',
      perWeek: '/linggo',
      perMonth: '/buwan',
      specialPromotions: 'Mga Espesyal na Promosyon:',
      scooterPreference: 'Gustong Scooter',
      noPreference: 'Walang kagustuhan',
      scooterFallback: 'Scooter',
      chooseScooterStep: '2. Piliin ang Iyong Scooter',
      checkingAvailability: 'Sinusuri ang availability para sa iyong mga petsa…',
      availableForDates: '{{available}} sa {{total}} ang available para sa iyong mga petsa',
      fleetCount: '{{count}} {{unit}} sa aming fleet',
      lockedScooterNote: 'Ang scooter na ito ay nakareserba para sa iyo sa pamamagitan ng iyong booking link',
      noScootersAvailable: 'Walang available na scooter sa ngayon. Mangyaring makipag-ugnayan sa amin nang direkta.',
      available: 'Available',
      unavailable: 'Hindi available',
      photosCount: '{{count}} larawan',
      viewPhoto: 'Tingnan',
      viewPhotosAria: 'Tingnan ang mga larawan ng {{title}}',
      additionalNotes: 'Karagdagang Tala',
      notesPlaceholder: 'Anumang espesyal na kahilingan o impormasyon...',
      charactersCount: '{{count}}/255 na karakter',
      preSignTitle: '⚡ Pirmahan ang Kontrata Nang Maaga (Inirerekomenda)',
      preSignDesc: 'Pabilisin ang iyong pickup process! Ang maagang pagpirma ng kontrata ay nakakatipid ng oras pagdating mo.',
      preSignBenefits: 'Walang bayad • Hindi commitment • Pinapabilis lang ang pickup',
      completeFieldsToSign: 'Kumpletuhin ang lahat ng kinakailangang field sa itaas para ma-enable ang maagang pagpirma ng kontrata.',
      missingLabel: 'Kulang:',
      signatureSaved: 'Na-save ang pirma: {{name}}',
      submitting: 'Isinusumite...',
      submitRequest: 'Isumite ang Booking Request',
      disclaimer: 'Sa pagsumite ng form na ito, naiintindihan mong ito ay kahilingan, hindi kumpirmadong booking. Makikipag-ugnayan ang shop para kumpirmahin ang availability at kumpletuhin ang booking.',
      selectDatesForPrice: 'Pumili ng iyong mga petsa para makita ang presyo',
      depositShort: 'deposito',
      requestToBook: 'Humiling na Mag-book',
      continueBtn: 'Magpatuloy',
      closeBtn: 'Isara',
      prevPhoto: 'Nakaraang larawan',
      nextPhoto: 'Susunod na larawan',
      langLabel: 'Wika',
      toastLoadFailed: 'Nabigong i-load ang booking page',
      toastVersionFailed: 'Nabigong bumuo ng contract version. Pakisubukang muli.',
      toastPreviewFailed: 'Nabigong i-load ang contract preview',
      toastEnterName: 'Pakilagay ang iyong buong pangalan',
      toastEnterEmail: 'Pakilagay ang iyong email address',
      toastEnterPhone: 'Pakilagay ang iyong numero ng telepono',
      toastSelectStart: 'Pumili ng petsa ng simula',
      toastSelectScooter: 'Pumili ng scooter',
      toastSelectPricingGroup: 'Pumili ng pricing group',
      toastPricingFailed: 'Hindi makalkula ang presyo',
      toastSubmitFailed: 'Nabigong isumite ang booking request. Pakisubukang muli.',
      errFullNameRequired: 'Kailangan ang buong pangalan',
      errEmailRequired: 'Kailangan ang email',
      errEmailInvalid: 'Hindi wasto ang email',
      errPhoneRequired: 'Kailangan ang numero ng telepono',
      mfFullName: 'Buong Pangalan',
      mfEmail: 'Email',
      mfPhone: 'Numero ng Telepono',
      mfStartDate: 'Petsa ng Simula',
      mfScooter: 'Scooter',
      mfPricingGroup: 'Pricing Group',
      mfPricingInfo: 'Impormasyon sa presyo'
    },
    theme: {
      dark: 'Madilim',
      light: 'Maliwanag',
      blue: 'Asul',
      wood: 'Kahoy'
    },
    home: {
      quickActions: 'Mabilis na Aksyon',
      recentActivity: 'Kamakailang Aktibidad',
      fleetStatusOverview: 'Pangkalahatang Status ng Fleet',
      welcomeTitle: 'Maligayang pagdating sa OurScooter',
      welcomeSubtitle: 'Ang iyong komprehensibong dashboard para sa pamamahala ng scooter fleet',
      addScooter: 'Bagong Scooter',
      registerScooter: 'Magrehistro ng bagong scooter',
      addClient: 'Bagong Client',
      registerClient: 'Magrehistro ng bagong customer',
      newContract: 'Bagong Contract',
      createAgreement: 'Gumawa ng rental agreement',
      org: 'Org',
      allOrgs: 'Lahat ng org',
      availableScooters: 'Available na Scooters',
      readyForRental: 'Handa na para sa rental',
      rentedScooters: 'Nakarenta na Scooters',
      currentlyInUse: 'Kasalukuyang ginagamit',
      maintenance: 'Maintenance',
      underRepair: 'Nasa ilalim ng repair',
      fleetOverview: 'Pangkalahatang View ng Fleet',
      available: 'magagamit',
      rented: 'nakarenta',
      activeRevenue: 'Aktibong Revenue',
      monthlyRecurring: 'Buwanang recurring revenue',
      totalClients: 'Kabuuang Clients',
      activeContracts: 'aktibong contracts',
      avgBattery: 'Karaniwang Baterya',
      fleetBatteryHealth: 'Kalusugan ng fleet battery',
      avgFuel: 'Karaniwang Gasolina',
      fuel: 'gasolina',
      electric: 'elektriko',
      fleetROI: 'ROI ng Fleet',
      rev: 'rev',
      cost: 'gastos',
      inMaintenance: 'ay nasa maintenance',
      newClientRegistered: 'Bagong client',
      registered: 'ay nairehistro'
    },
    users: {
      title: 'Mga Gumagamit',
      errorLoading: 'Error sa pag-load ng users.',
      id: 'ID',
      email: 'Email',
      role: 'Tungkulin',
      username: 'Username',
      org: 'Org',
      roleSuperadmin: 'Superadmin',
      roleAdmin: 'Admin',
      roleStaff: 'Kawani'
    },
    management: {
      title: 'Pamamahala ng Organisasyon',
      errorLoading: 'Error sa pag-load ng settings.',
      timeFormat: 'Format ng Oras',
      defaultCurrency: 'Default na Currency',
      defaultCountryCode: 'Default na Country Code',
      defaultDailyPrice: 'Default na Daily Price',
      defaultDeposit: 'Default na Contract Deposit',
      defaultRentLength: 'Default na Rent Length (Days)',
      defaultPaymentMethod: 'Default na Payment Method',
      buttonStyle: 'Estilo ng Button',
      buttonStyleDesc: 'Pumili ng visual style para sa lahat ng buttons sa buong application.',
      countryCodeHint: '1 para sa USA, 44 para sa UK, atbp.',
      countryCodeExplain: 'Digits lang. Maglalagay ng prefix sa phone numbers sa client forms.',
      saving: 'Nagse-save...',
      saveSettings: 'I-save ang Settings',
      saved: 'Na-save na',
      modernStyle: 'Modernong Estilo',
      classicStyle: 'Klasikong Estilo',
      originalStyle: 'Orihinal na Estilo',
      btnPrimary: 'Pangunahin',
      btnSecondary: 'Pangalawa',
      btnGhost: 'Ghost',
      btnDanger: 'Panganib',
      modernDesc: 'Gradient backgrounds na may micro-interactions at rounded corners',
      classicDesc: 'Malinis, minimal design na may theme-aware colors at subtle borders',
      originalDesc: 'Ang original button design na nagustuhan mo bago ang refactoring',
      timeFormatExample1: '15 September 2025 6:09 PM',
      timeFormatExample2: 'September 15 2025 6:09 PM',
      timeFormatExample3: '15 September 2025 18:09',
      timeFormatExample4: 'September 15 2025 18:09'
    },
    common: {
      language: 'Wika',
      phoneTooShort: 'Ang numero ng telepono ay dapat hindi bababa sa 7 digit',
      phoneTooLong: 'Masyadong mahaba ang numero ng telepono (maximum 15 digit)',
      theme: 'Tema',
      save: 'I-save',
      cancel: 'I-cancel',
      logout: 'Mag-logout',
      loading: 'Naglo-load...',
      reset: 'I-reset',
      close: 'Isara',
      back: 'Bumalik',
      delete: 'Tanggalin',
      clearAll: 'I-clear lahat',
      upload: 'I-upload',
      uploading: 'Nag-u-upload...',
      selectPhoto: 'Pumili ng Photo',
      addNotes: 'Magdagdag ng notes...',
      notes: 'Mga Tala',
      optional: 'Opsyonal',
      retake: 'Kunin ulit',
      usePhoto: 'Gamitin ang Photo',
      viewAll: 'Tingnan lahat ng Photos',
      confirmDelete: 'Sigurado ka bang gusto mong tanggalin ang photo na ito?',
      add: 'Magdagdag',
      copied: 'Na-copy na',
      expand: 'I-expand',
      collapse: 'I-collapse',
      updateFailed: 'Nabigo ang pag-update',
      updateFailedWithMessage: 'Nabigo ang pag-update: {{message}}',
      failedToSendEmail: 'Nabigo ang pagpadala ng email',
      statusChangeNotAllowed: 'Hindi pinapayagan ang pagbabago ng status na ito',
      expenseAddedSuccessfully: 'Matagumpay na naidagdag ang expense',
      retry: 'Subukan ulit',
      edit: 'I-edit'
    },
    toasts: {
      contractCreated: 'Matagumpay na nagawa ang contract',
      contractUpdated: 'Matagumpay na na-update ang contract',
      contractDeleted: 'Matagumpay na natanggal ang contract',
      contractCreateFailed: 'Nabigo ang paggawa ng contract',
      contractUpdateFailed: 'Nabigo ang pag-update ng contract',
      contractDeleteFailed: 'Nabigo ang pagtanggal ng contract',
      scooterCreated: 'Matagumpay na nagawa ang scooter',
      scooterUpdated: 'Matagumpay na na-update ang scooter',
      scooterDeleted: 'Matagumpay na natanggal ang scooter',
      scooterStatusUpdated: 'Matagumpay na na-update ang scooter status',
      scooterStatusUpdateFailed: 'Nabigo ang pag-update ng scooter status',
      clientCreated: 'Matagumpay na nagawa ang client',
      clientUpdated: 'Matagumpay na na-update ang client',
      clientDeleted: 'Matagumpay na natanggal ang client',
      settingsUpdated: 'Matagumpay na na-update ang settings!',
      scooterStatusChangedAuto: 'Awtomatikong Nagbago ang Scooter Status',
      scooterStatusChangeDesc: 'Ang {{scooterName}} ay inilipat mula {{fromStatus}} patungo sa {{toStatus}} {{reason}}.',
      becauseContractActivated: 'dahil na-activate ang contract',
      becauseContractDeactivated: 'dahil na-close/archive ang contract',
      automatically: 'awtomatiko',
      viewScooter: 'Tingnan ang Scooter',
      scooterOpenedFromNotif: 'Binuksan ang {{scooterName}} details',
      scooterOpenedFromNotifDesc: 'Ang scooter na ito ay awtomatikong binuksan mula sa status change notification.',
      closedContractsMoveRestriction: 'Ang closed contracts ay maaari lang ilipat sa Archived.'
    },
    login: {
      title: 'Mag-login',
      email: 'Email',
      username: 'Username',
      password: 'Password',
      signIn: 'Mag-sign in',
      signingIn: 'Nagse-sign in...',
      login: 'Mag-login',
      loggingIn: 'Naglo-login...',
      loginFailed: 'Nabigo ang login',
      language: 'Wika'
    },
    sidebar: {
      scooters: 'Mga Scooter',
      clients: 'Mga Client',
      contracts: 'Mga Contract',
      finance: 'Pananalapi',
      users: 'Mga User',
      activity: 'Tala ng Aktibidad',
      management: 'Pamamahala',
      howItWorks: 'Paano ito Gumagana',
      signIn: 'Mag-sign in',
      expand: 'I-expand ang sidebar',
      collapse: 'I-collapse ang sidebar',
      logoutConfirm: 'Sigurado ka bang gusto mong mag-logout?',
      menu: 'Menu'
    },
    howItWorks: {
      title: 'Paano Gamitin ang OurScooter (Para sa Staff)',
      subtitle: 'Simpleng hakbang, malinaw na patakaran, at kapaki-pakinabang na tip para gawin nang maayos at mahusay ang inyong trabaho.',
      categories: 'Mga Kategorya',
      whyTitle: 'Bakit OurScooter: layunin at benepisyo',
      whyDescription: 'Tumutulong ang OurScooter sa staff na patakbuhin nang mahusay ang pang-araw-araw na scooter rental operations: mas kaunting clicks, mas kaunting pagkakamali, at malinaw na records.',
      whySpeed: 'Bilis: gumawa ng contracts sa loob ng ilang segundo, muling gamitin ang mga detalye sa pamamagitan ng duplication.',
      whyClarity: 'Kalinawan: ipinapakita ng mga status kung nasaan ang bawat rental sa isang sulyap.',
      whyControl: 'Kontrol: i-filter, maghanap, at magpalit ng views (table/kanban) para umangkop sa gawain.',
      whyAccuracy: 'Kawastuhan: pinipigilan ng mga guardrails ang mga invalid moves at overlapping rentals.',
      signingInTitle: 'Pag-sign in at pagpapanatiling naka-sign in',
      signingInToken: 'Username + password → nag-verify ang server at nagbabalik ng access + refresh tokens.',
      signingInAutoRefresh: 'Maikli ang buhay ng access tokens; awtomatiko naming ina-refresh nang tahimik gamit ang refresh token.',
      signingInRoles: 'Mga Role: admin at staff. Napipili ang org context sa sidebar.',
      signingInSession: 'Tagal ng Session: Mananatili kayong naka-sign in hanggang 1 araw ng kawalan ng aktibidad. Gumagamit ang app ng maikling 15‑minute access tokens at ina-renew ang mga ito sa background. Pagkatapos ng 1 araw na hindi paggamit ng app (o kung na-clear ang cookies), hihilingin kayong mag-sign in ulit.',
      scootersTitle: 'Mga Scooter: pagdaragdag at pag-edit',
      scootersOpen: 'Buksan ang "Add Scooter" para magrehistro ng bagong sasakyan. Panatilihing maikli at tumpak ang mga detalye.',
      scootersRequired: 'Kailangan: brand/model, status.',
      scootersOptional: 'Opsyonal: purchase cost, fuel/battery details, notes.',
      scootersTip: 'Tip: Inaalis ng status na "maintenance" ang scooter sa availability hanggang maibalik.',
      clientsTitle: 'Mga Client: pagdaragdag at pag-edit',
      clientsOpen: 'Buksan ang "New Client". Kunin ang makakatulong sa inyo na mahanap sila mamaya.',
      clientsRequired: 'Kailangan: buong pangalan.',
      clientsOptional: 'Opsyonal: telepono, email, ID/passport, notes.',
      contractsTitle: 'Mga Contract: paggawa at pamamahala',
      contractsWaysTitle: 'Mga paraan para gumawa ng contract (4 na opsyon)',
      contractsFromContracts: 'Mula sa Contracts: pindutin ang "Add Contract".',
      contractsFromScooters: 'Mula sa Scooters: buksan ang isang scooter → "New Contract".',
      contractsFromClients: 'Mula sa Clients: buksan ang isang client → "New Contract".',
      contractsDuplicate: 'Duplicate: buksan ang isang existing contract → "Duplicate" para ligtas na muling gamitin ang mga detalye.',
      contractsRequiredTitle: 'Mga kailangang field',
      contractsRequiredClient: 'Client at Scooter.',
      contractsRequiredDate: 'Start date/time.',
      contractsRequiredRate: 'Daily rate. (Opsyonal ang Deposit/payment method.)',
      contractsStatusTitle: 'Mga Status at workflow (ano ang pinapayagan)',
      contractsStatusFlow: 'Draft → Active → Closed → Archived.',
      contractsStatusActivate: 'Maaari lang kayong mag-activate kung naka-set na ang mga kailangang field at available ang scooter.',
      contractsStatusClosed: 'Mula sa Closed maaari lang kayong pumunta sa Archived (hindi pwedeng bumalik sa Active).',
      contractsStatusForbidden: 'Bawal: overlapping Active contracts para sa parehong scooter.',
      contractsStatusArchived: 'Nawawala ang Archived contracts sa default views. I-toggle ang "Include archived" para makita ang mga ito.',
      contractsAutoTitle: 'Awtomatikong pag-update ng scooter status',
      contractsAutoCreate: 'Hindi binabago ng paggawa ng contract ang scooter status (anuman ang contract status).',
      contractsAutoActive: 'Kapag naging Active ang isang contract → awtomatikong nagiging "rented" ang scooter.',
      contractsAutoClosed: 'Kapag na-Close/Archive ang isang Active contract → awtomatikong nagiging "available" ang scooter.',
      contractsAutoDraft: 'Kapag ibinalik sa Draft ang isang Active contract → awtomatikong nagiging "available" ang scooter.',
      contractsAutoPrevent: 'Pinipigilan nito ang double-bookings at pinapanatiling tumpak ang availability ng scooter.',
      contractsTimeTitle: 'Pagsubaybay ng oras para sa Active contracts',
      contractsTimeRemaining: 'Ipinapakita ng Active contracts ang natitirang oras hanggang sa end date (hal., "2h 30m left").',
      contractsTimeOverdue: 'Ipinapakita ng Overdue contracts ang lumipas na oras (hal., "Overdue 1d 5h").',
      contractsTimeColors: 'Ipinapahiwatig ng mga kulay ang urgency: Green (4+ hours), Yellow (1-4 hours), Orange (<1 hour), Red (overdue).',
      contractsTimeKanban: 'Sa Kanban view: timer lang ang ipinapakita para sa Active contracts.',
      contractsTimeList: 'Sa List view: parehong status badge at timer ang ipinapakita para sa Active contracts.',
      contractsViewsTitle: 'Mga Contract: views',
      contractsViewsTable: 'Table view: mabilis na pag-scan, pag-filter, at pag-export ng mga row.',
      contractsViewsKanban: 'Kanban board: visual workflow ayon sa status (Draft, Active, Closed, Archived).',
      contractsViewsToggle: 'Gamitin ang view toggle sa Contracts toolbar para magpalit ng views.',
      contractsKanbanTitle: 'Pagtatrabaho sa Kanban board',
      contractsKanbanDrag: 'I-drag & drop ang isang card para ilipat ito sa susunod na pinapayagang status.',
      contractsKanbanEdit: 'Buksan ang isang card at pindutin ang Edit para baguhin ang mga detalye o status nang direkta.',
      contractsKanbanRules: 'Naa-apply ang mga patakaran: hindi kayo pwedeng lumipat mula sa Closed pabalik sa Active; final na ang Archived.',
      contractsKanbanBlocked: 'Naka-block ang mga unavailable moves (hal., naka-Active na ang scooter sa ibang lugar).',
      contractsKanbanFilters: 'Gamitin ang mga filter para mag-focus sa isang client o isama ang Archived items kung kailangan.',
      findingTitle: 'Mabilis na paghahanap (filter & search)',
      findingScooters: 'Mga Scooter: i-filter ayon sa status, brand, model. Gamitin ang search box para sa mabilis na text matches.',
      findingClients: 'Mga Client/User: maghanap ayon sa name/email; ayusin ang page size para makita ang mas maraming row.',
      findingContracts: 'Mga Contract: i-filter ayon sa status; opsyonal na isama ang archived.',
      rowsTitle: 'Ilang row ang ipinapakita namin?',
      rowsScooters: 'Mga Scooter: naglo-load sa mga page at kumukuha ng mas marami kung kailangan (hanggang 100 bawat load).',
      rowsClients: 'Mga Client/User: maaari kayong pumili ng 10/20/30/40/50 row bawat page.',
      rowsContracts: 'Mga Contract: ipinapakita ang current org contracts; isama ang archived para palawakin ang list.',
      themesTitle: 'Mga Theme at defaults',
      themesChange: 'Palitan ang theme (Dark/Light/Blue/Wood) sa kaliwang sidebar sa ibaba.',
      themesAdmin: 'Maaaring i-configure ng mga Admin ang organization defaults sa Management (hal., currency, rates).',
      usersTitle: 'Mga User & Management (admins)',
      usersStaff: 'Staff: pang-araw-araw na operations, tingnan at gumawa ng contracts, pamahalaan ang scooters/clients.',
      usersAdmin: 'Admin: lahat ng kayang gawin ng staff, plus org‑level settings at deletions.',
      tipsTitle: 'Mga Tip & troubleshooting',
      tipsActivate: 'Hindi ma-activate? I-check ang scooter status (dapat available) at mga kailangang field.',
      tipsContract: 'Hindi nakikita ang contract? Maaaring naka-archive, i-enable ang "Include archived".',
      tipsSession: 'Natapos ang session? Kung lagpas na ng isang araw, mag-sign in lang ulit.'
    },
    toolbar: {
      searchPlaceholder: 'Maghanap sa ID, model, o status…',
      clear: 'I-clear',
      listView: 'Listahan',
      kanbanView: 'Kanban',
      calendarView: 'Kalendaryo',
      gridView: 'Grid',
      mapView: 'Mapa',
      filter: 'I-filter',
      export: 'I-export',
      total: 'Kabuuan: {{count}}',
      queryLabel: 'query',
      clearQuery: 'I-clear ang query',
      removeFilter: 'Tanggalin ang {{filter}}'
    },
    ui: {
      zoom: 'Zoom'
    },
    calendar: {
      week: 'Linggo',
      month: 'Buwan',
      all: 'Lahat',
      plusMore: '+{{count}} pa',
      scrollHint: 'Shift+Scroll para mag-move nang pahalang',
      hideEmpty: 'Itago ang scooters na walang upcoming contracts'
    },
    status: {
      available: 'Magagamit',
      rented: 'Nakarenta',
      maintenance: 'Pag-aayos',
      retired: 'Retirado',
      draft: 'Borador',
      active: 'Aktibo',
      closed: 'Sarado',
      archived: 'Naka-archive'
    },
    clients: {
      id: 'ID ng Client',
      name: 'Pangalan',
      messenger: 'WhatsApp',
      newClient: 'Bagong client',
      creating: 'Gumagawa ng client...',
      fullName: 'Buong Pangalan',
      fullNamePlaceholder: 'Ilagay ang full name',
      email: 'Email',
      emailPlaceholder: 'Ilagay ang email address',
      localPhone: 'Numero ng Telepono',
      localPhonePlaceholder: 'Ilagay ang local phone number (optional)',
      messengerNumber: 'Numero ng WhatsApp',
      countryCodePlaceholder: '63',
      messengerNumberPlaceholder: 'Ilagay ang WhatsApp number (optional)',
      lineId: 'LINE ID',
      lineIdPlaceholder: 'Ilagay ang LINE ID (optional)',
      whatsappButton: 'WhatsApp',
      lineButton: 'LINE',
      contactViaWhatsApp: 'Makipag-ugnayan sa pamamagitan ng WhatsApp',
      contactViaLINE: 'Makipag-ugnayan sa pamamagitan ng LINE',
      notes: 'Mga Tala',
      notesPlaceholder: 'Karagdagang notes tungkol sa client na ito (hal., ibang country phone number)',
      addClient: 'Bagong client',
      detailsTitle: 'Mga Detalye ng Client',
      created: 'Ginawa',
      clientId: 'ID ng Client',
      edit: 'I-edit ang Client',
      previousContracts: 'Nakaraang contracts:',
      noCurrentContracts: 'Walang current contracts',
      editClient: 'I-edit ang Client',
      newContractBtn: 'Bagong Contract',
      close: 'Isara',
      statusHistory: 'Kasaysayan ng Status',
      unknownClient: 'Hindi kilalang Client',
      fullNameRequired: 'Kailangan ang buong pangalan',
      emailInvalid: 'Hindi wasto ang email address',
      failedLoad: 'Nabigo ang pag-load ng clients',
      noClientsYet: 'Wala pang clients',
      noClientsMatch: 'Walang clients na tumugma sa inyong search',
      addFirstClient: 'Magdagdag ng inyong unang client para makapagsimula',
      tryAdjusting: 'Subukan ang pag-adjust ng inyong search'
    },
    scooters: {
      id: 'ID ng Scooter',
      modelRequired: 'Kailangan ang model',
      batteryRange: 'Ang battery ay dapat nasa 0–100',
      odometerNegative: 'Hindi pwedeng negative ang odometer',
      fuelRange: 'Ang fuel % ay dapat nasa 0–100',
      incompatibleEnergy: 'Ang electric ay walang fuel; ang fuel vehicles ay walang battery',
      editScooter: 'I-edit ang Scooter',
      newScooter: 'Bagong scooter',
      updating: 'Nag-u-update ng scooter...',
      creating: 'Gumagawa ng scooter...',
      model: 'Modelo',
      modelPlaceholder: 'hal. Ninebot Max',
      status: 'Status',
      typeLevel: 'Uri at Antas',
      odometer: 'Odometro',
      qrCode: 'QR Code',
      qrPlaceholder: 'Opsyonal na QR code',
      plateNumber: 'Numero ng Plaka',
      platePlaceholder: 'Opsyonal na plate number',
      nickname: 'Palayaw',
      nicknamePlaceholder: 'hal. Red Honda #1',
      nicknameOptional: 'Palayaw (optional)',
      brand: 'Tatak',
      brandPlaceholder: 'Opsyonal na tatak',
      vehicleType: 'Uri ng Sasakyan',
      electric: 'Elektriko',
      fuel: 'Gasolina',
      batteryPct: 'Baterya %',
      fuelPct: 'Gasolina %',
      odometerKm: 'Odometro (km)',
      updateScooter: 'I-update ang scooter',
      addScooter: 'Bagong scooter',
      details: 'Mga Detalye',
      edit: 'I-edit',
      created: 'Ginawa',
      statusHistory: 'Kasaysayan ng status ng scooter',
      columnScooterId: 'ID ng Scooter',
      columnBrand: 'Tatak',
      columnModel: 'Modelo',
      columnPlateNumber: 'Numero ng Plaka',
      columnNickname: 'Palayaw',
      columnStatus: 'Status',
      columnVehicleStatus: 'Uri at Antas',
      columnOdometer: 'Odometro',
      pictures: 'Mga Larawan',
      noPhotos: 'Wala pang photos',
      noPhotosYet: 'Wala pang photos',
      allPhotos: 'Lahat ng Photos',
      viewAllPhotos: 'Tingnan lahat ng Photos',
      damage: 'Pinsala',
      damageOnly: 'Pinsala',
      showingDamage: 'Nagpapakita ng Damage',
      noDamagePhotos: 'Walang photos na may damage',
      markAsDamage: 'Mark bilang damage',
      markDamage: 'Markahan ang Pinsala',
      removeDamage: 'Tanggalin ang Damage',
      markNoDamage: 'Mark na walang damage',
      damageDetected: 'Nakita ang damage',
      scooterPhoto: 'Larawan ng scooter',
      selectImageFile: 'Pumili ng image file',
      fileSizeLimit: 'Ang file size ay dapat mas mababa sa 10MB',
      photoUploaded: 'Matagumpay na na-upload ang photo',
      photoDeleted: 'Natanggal ang photo',
      photoDeleteFailed: 'Nabigo ang pagtanggal ng photo',
      photoUpdateFailed: 'Nabigo ang pag-update ng photo',
      photoUploadFailed: 'Nabigo ang pag-upload ng photo',
      photoStorageNotConfigured: 'Hindi pa naka-configure ang photo storage. Pakikontak ang inyong administrator.',
      deletePhoto: 'Tanggalin ang photo',
      drawCircle: 'Gumuhit ng Circle',
      stopDrawing: 'Itigil ang Drawing',
      undo: 'I-undo',
      redo: 'I-redo',
      capture: 'Kunin ang litrato',
      takePhoto: 'Kumuha ng Litrato',
      chooseFromGallery: 'Pumili mula sa Gallery',
      addPhoto: 'Magdagdag ng Litrato',
      photoPreview: 'Preview ng Litrato',
      cameraError: 'Nabigo ang pag-access sa camera',
      cameraPermissionDenied: 'Tinanggihan ang pahintulot sa camera'
    },
    pages: {
      manageColumns: 'Pamahalaan ang columns',
      columns: 'Mga Hanay',
      retiredHidden: 'Nakatago ang retired scooters',
      failedLoadScooters: 'Nabigo ang pag-load ng scooters',
      checkConnection: 'Pakitingnan ang inyong connection at subukan ulit.',
      retry: 'Subukan ulit',
      clearFilters: 'I-clear ang filters',
      addScooter: 'Bagong scooter',
      noScootersYet: 'Wala pang scooters',
      noScootersMatch: 'Walang scooters na tumugma sa inyong filters',
      addFirstScooter: 'Magdagdag ng inyong unang scooter para makapagsimula.',
      tryAdjusting: 'Subukan ang pag-adjust o pag-clear ng inyong filters.',
      rowsPerPage: 'Mga row bawat pahina:',
      pageOf: 'Pahina {{current}} ng {{total}}',
      prevShort: 'Nakaraan',
      nextShort: 'Susunod',
      prev: 'Nakaraang page',
      next: 'Susunod na page'
    },
    contractsPage: {
      newContract: 'Bagong Contract',
      showArchived: 'Ipakita ang archived contracts',
      newContractSubtitle: 'Mabilis na gumawa at mag-review ng bagong rental agreement',
      createdDate: 'Petsa ng Paggawa',
      recentlyUpdated: 'Kamakailang Na-update',
      recentlyClosed: 'Kamakailang Na-close',
      allClosed: 'Lahat ng Closed',
      showAll: 'Ipakita lahat',
      showRecent: 'Ipakita ang kamakailan',
      sortBy: 'I-sort ayon sa...',
      archivedOnlyToDraft: 'Ang archived contracts ay maaari lang ilipat sa Draft.'
    },
    activity: {
      title: 'Tala ng Aktibidad',
      subtitle: 'Subaybayan ang aktibidad ng empleyado at mga pagbabago sa system. Tingnan ang user-friendly logs para sa troubleshooting at auditing.',
      filterActivity: 'I-filter ang Activity',
      timePeriod: 'Panahon',
      lastHour: 'Nakaraang Oras',
      today: 'Ngayon',
      thisWeek: 'Sa linggong ito',
      thisMonth: 'Sa buwang ito',
      custom: 'Pasadya',
      from: 'Mula',
      to: 'Hanggang',
      employee: 'Empleyado',
      type: 'Uri ng Aktibidad',
      allEmployees: 'Lahat ng empleyado',
      allActivities: 'Lahat ng aktibidad',
      scooterMgmt: 'Pamamahala ng Scooter',
      contractMgmt: 'Pamamahala ng Contract',
      clientMgmt: 'Pamamahala ng Client',
      orgSettings: 'Mga Setting ng Organisasyon',
      searchEverything: 'Maghanap ng Lahat',
      searchPlaceholder: 'Maghanap ng names, IDs, prices, statuses...',
      clearAll: 'I-clear lahat'
    },
    contracts: {
      filters: 'Mga Filter',
      editContract: 'I-edit ang Contract',
      dates: 'Mga Petsa',
      for: 'para sa',
      scooter: 'Scooter',
      start: 'Simula',
      end: 'End',
      duration: 'Tagal',
      days: 'araw',
      weeks: 'linggo',
      months: 'buwan',
      estimatedTotal: 'Tinatayang total',
      fetchingData: 'Kumukuha ng clients at scooters',
      scooterCurrentlyRented: 'ℹ️ Ang scooter na ito ay kasalukuyang nakarenta{{until}}.',
      until: 'hanggang',
      futureReservationNote: 'Pwede ka pa ring gumawa ng contract para sa future reservation - siguraduhin lang na hindi mag-o-overlap ang dates.',
      searchClients: 'Maghanap ng clients sa name/email/id',
      noMatches: 'Walang tumugma',
      selected: 'Napili:',
      scooterQuery: 'Scooter (ID o model contains)',
      scooterPlaceholder: 'hal. Ninebot o UUID',
      dailyRateRange: 'Araw-araw na rate (min/max)',
      min: 'Min',
      max: 'Max',
      minExceedsMax: 'Hindi pwedeng mas mataas ang Min kaysa sa Max',
      endAfterStart: 'Ang end time ay dapat pagkatapos ng start time',
      clientRequired: 'Kailangan ang client',
      scooterRequired: 'Kailangan ang scooter',
      startTimeRequired: 'Kailangan ang start time',
      dailyRatePositive: 'Ang daily rate ay dapat > 0',
      addContract: 'Bagong contract',
      client: 'Kliyente',
      selectClient: 'Pumili ng client',
      searchScooters: 'Maghanap ng scooters...',
      startTime: 'Oras ng Simula',
      status: 'Status',
      dailyRate: 'Araw-araw na rate',
      deposit: 'Deposito',
      endTime: 'Petsa ng Pagtatapos',
      contractId: 'ID ng Kontrata',
      paymentMethod: 'Paraan ng Pagbabayad',
      cash: 'Cash',
      local_bank_transfer: 'Lokal na Bank Transfer',
      international_bank_transfer: 'Internasyonal na Bank Transfer',
      notes: 'Mga Tala',
      notesPlaceholder: 'Optional notes tungkol sa contract na ito',
      createDraftForScooter: 'Gumawa ng bagong contract draft para sa scooter na ito.',
      saveAsDraftHint: 'I-save bilang draft para sa pag-activate mamaya',
      startImmediatelyHint: 'Simulan agad ang contract',
      creating: 'Gumagawa...',
      createDraft: 'Gumawa ng draft',
      createActive: 'Gumawa ng active',
      day: 'day',
      forDays: 'para sa {{count}} day',
      forDays_plural: 'para sa {{count}} days',
      total: 'Kabuuan:',
      dayOf: 'day {{day}} ng {{total}}',
      failedLoad: 'Nabigo ang pag-load ng contracts'
    },
    contractsUi: {
      renting: 'nakarenta',
      statusHistory: 'Kasaysayan ng Status',
      noHistory: 'Walang available na history',
      created: 'Ginawa',
      overdue: 'Lumampas na',
      overdueTitle: 'Lumampas na ang contract',
      remainingTitle: 'Natitirang oras hanggang sa pagtatapos ng contract',
      remaining: 'natitira',
      unitDayShort: 'd',
      unitHourShort: 'h',
      unitMinuteShort: 'm',
      clientDetails: 'Mga Detalye ng Client',
      scooterDetails: 'Mga Detalye ng Scooter',
      clientNotFound: 'Hindi nahanap ang client',
      scooterNotFound: 'Hindi nahanap ang scooter',
      fullName: 'Buong Pangalan',
      localPhone: 'Numero ng Telepono',
      messenger: 'WhatsApp',
      brand: 'Tatak',
      vehicleType: 'Uri ng Sasakyan',
      battery: 'Baterya',
      qrCode: 'QR Code',
      scooterStatus: 'Status ng Scooter',
      noStatusChanges: 'Wala pang pagbabago ng status',
      loadingHistory: 'Naglo-load ng history...',
      characters: 'mga karakter',
      back: 'Bumalik',
      startDate: 'Petsa ng Simula',
      endDate: 'Petsa ng Pagtatapos',
      contractId: 'ID ng Kontrata',
      editContract: 'I-edit ang Contract',
      duplicate: 'I-duplicate',
      duplicateContract: 'I-duplicate ang contract',
      duplicateSubtitle: 'Pumili ng start, duration, at end para sa bagong draft',
      endTimeMustBeAfter: 'Ang end time ay dapat pagkatapos ng start time',
      changeStatus: 'Palitan ang status',
      currentStatus: 'kasalukuyan',
      changeScooterStatus: 'Palitan ang status ng scooter',
      currentScooterStatus: 'kasalukuyan',
      showingRange: 'Nagpapakita ng {{start}}–{{end}} ng {{total}}',
      contractCreatedTitle: 'Nagawa ang contract',
      contractCreatedMessage: 'Gusto mo bang buksan ang contract na ginawa?',
      contractLabel: 'Kontrata',
      openContract: 'Buksan ang Contract',
      stayHere: 'Manatili dito',
      scooterStatusQuestion: 'Ano ang dapat na status ng scooter "{{model}}"?',
      currentStatusLabel: 'Kasalukuyang status:',
      statusChangeHelp: 'Makakatulong ito sa inyo na pamahalaan ang availability ng scooter pagkatapos ng contract status change.',
      working: 'Nagtatrabaho…',
      moveContractAndUpdate: 'Ilipat ang Contract & I-update ang Scooter',
      enterToConfirm: 'Enter para i-confirm · Esc para i-cancel',
      statusAvailable: 'Magagamit',
      statusAvailableDesc: 'Handa na ang scooter para sa rental',
      statusRented: 'Nakarenta',
      statusRentedDesc: 'Kasalukuyang nakarenta ang scooter',
      statusMaintenance: 'Pag-aayos',
      statusMaintenanceDesc: 'Kailangan ng maintenance ang scooter',
      statusRetired: 'Retirado',
      statusRetiredDesc: 'Hindi na sa serbisyo ang scooter',
      statusCurrent: 'Kasalukuyan',
      moveContractTitle: 'Ilipat ang Contract',
      moveContractMessage: 'Pakipili ang bagong status para sa scooter kapag inililipat ang contract na ito.',
      fromLabel: 'Mula',
      toLabel: 'Patungo',
      clientLabel: 'Kliyente',
      scooterLabel: 'Scooter',
      currentContract: 'Kasalukuyang contract:',
      viewContract: 'Tingnan ang Contract'
    },
    contractDocument: {
      print: 'I-print',
      saveAsPDF: 'I-save bilang PDF',
      email: 'Email',
      addSignature: 'Magdagdag ng Signature',
      emailRequired: 'Kailangan ang email address',
      emailSent: 'Matagumpay na naipadala ang contract',
      enterSignatureName: 'Ilagay ang signer name para idagdag sa document',
      loading: 'Naglo-load ng contract...',
      preparingPDF: 'Inihahanda ang PDF...',
      downloadSuccess: 'Matagumpay na na-download ang PDF',
      downloadFailed: 'Bigo sa pag-generate ng PDF',
      retry: 'Subukan ulit',
      emailContract: 'I-email ang Contract',
      recipientEmail: 'Email ng Tatanggap',
      sending: 'Nagpapadala...',
      send: 'Ipadala'
    },
    businessDetails: {
      title: 'Mga Detalye ng Negosyo',
      settingsUpdated: 'Matagumpay na na-update ang business details',
      errorLoading: 'Error sa pag-load ng business details.',
      helpText: 'Punan ang inyong business information sa ibaba. Ang mga details na ito ay lalabas sa rental contracts. Iwanan ang fields na blank kung mas gusto ninyong punan ito nang kamay sa printed contracts.',
      businessName: 'Pangalan ng Negosyo',
      businessNamePlaceholder: 'Ilagay ang inyong business name',
      businessAddress: 'Address ng Negosyo',
      businessAddressPlaceholder: 'Ilagay ang inyong business address',
      businessPhone: 'Telepono ng Negosyo',
      businessPhonePlaceholder: '+63 2 123 4567',
      businessEmail: 'Email ng Negosyo',
      businessEmailPlaceholder: 'hal. business@example.com',
      taxId: 'Tax ID / Rehistro ng Negosyo',
      taxIdPlaceholder: '1234567890123',
      representativeName: 'Pangalan ng Kinatawan',
      representativeNamePlaceholder: 'Juan dela Cruz',
      representativeIdNumber: 'ID Number ng Kinatawan',
      representativeIdNumberPlaceholder: '1234567890123',
      saving: 'Nagse-save...',
      saveDetails: 'I-save ang Business Details',
      saved: 'Na-save na'
    },
    messaging: {
      title: 'Makipag-ugnayan sa Customer',
      scanQR: 'I-scan ang QR Code',
      orClickLink: 'O i-click ang link sa ibaba',
      openApp: 'Buksan ang Messaging App',
      selectTemplate: 'Pumili ng Message Template',
      customMessage: 'Pasadyang Mensahe',
      messageEditor: 'Editor ng Mensahe',
      thankYou: 'Salamat',
      reminder: 'Paalala',
      payment: 'Paalala sa Pagbabayad',
      templateThankYou: 'Salamat sa pagpili sa {{businessName}}! Umaasa kami na nasiyahan kayo sa inyong scooter rental experience. Umaasa kaming makapagserbisyo ulit sa inyo!',
      templateReminder: 'Hi {{clientName}}! Ito ay {{businessName}}. Reminder: Matatapos bukas ang inyong rental period. Pakibalik ang scooter.',
      templatePayment: 'Hi {{clientName}}! Ito ay {{businessName}}. Payment reminder para sa contract #{{contractId}}.',
      noWhatsApp: 'Walang available na WhatsApp number',
      noLINE: 'Walang available na LINE ID',
      close: 'Isara',
      messagePreview: 'Preview ng Mensahe'
    },
    finance: {
      title: 'Pangkalahatang Pananalapi',
      exportCSV: 'I-export ang CSV',
      timeRange: 'Saklaw ng Panahon',
      day: 'Araw',
      week: 'Linggo',
      month: 'Buwan',
      year: 'Taon',
      all: 'Lahat',
      searchPlaceholder: 'Maghanap ng scooters...',
      showSoldOnly: 'Ipakita lang ang nabenta',
      totalPurchase: 'Kabuuang Pagbili',
      totalMaintenance: 'Kabuuang Pagpapanatili',
      totalIncome: 'Kabuuang Kita',
      netProfit: 'Netong Kita',
      topROIScooter: 'Scooter na may Pinakamataas na ROI',
      bestBrandModel: 'Pinakamahusay na Brand/Model',
      scooters: 'mga scooter',
      scooter: 'Scooter',
      purchase: 'Pagbili',
      maintenance: 'Pagpapanatili',
      income: 'Kita',
      sale: 'Pagbebenta',
      net: 'Neto',
      roi: 'ROI',
      status: 'Status',
      profitable: 'Kumikita',
      breakingEven: 'Break Even',
      lossMaker: 'Nalulugi',
      totals: 'Kabuuan',
      recentExpenses: 'Kamakailang Gastos',
      noExpenses: 'Walang na-record na expenses',
      loadingExpenses: 'Naglo-load ng expenses...',
      noExpensesInRange: 'Walang expenses sa napiling time range',
      expenseTotal: 'Kabuuan',
      categoryOilChange: 'Pagpapalit ng Langis',
      categoryTires: 'Mga Gulong',
      categoryBrakes: 'Mga Preno',
      categoryEngine: 'Makina',
      categoryBattery: 'Baterya',
      categoryBodyParts: 'Mga Bahagi ng Katawan',
      categoryCleaning: 'Paglinis',
      categoryRegistration: 'Pagrehistro',
      categoryInsurance: 'Seguro',
      categoryMiscParts: 'Iba pang Bahagi',
      categoryCustom: 'Pasadya',
      affectsExpenseDetails: 'nakakaapekto sa expense details',
      tableView: 'Talahanayan',
      chartsView: 'Mga Tsart',
      toggleKPIs: 'I-toggle ang KPIs',
      toggleInsights: 'I-toggle ang Insights',
      toggleAdditionalTables: 'I-toggle ang Additional Tables',
      showOperational: 'Ipakita ang operational view',
      showFull: 'Ipakita ang full view',
      operationalView: 'Tanaw ng Operasyon',
      fullView: 'Buong tanaw',
      operationalNet: 'Net ng Operasyon',
      date: 'Petsa',
      category: 'Kategorya',
      amount: 'Halaga',
      note: 'Mga Tala',
      actions: 'Mga Aksyon',
      edit: 'I-edit',
      delete: 'Tanggalin',
      editExpense: 'I-edit ang Expense',
      customLabel: 'Pasadyang label',
      confirmDeleteExpense: 'Sigurado ka bang gusto mong tanggalin ang expense na ito?',
      expenseDeleted: 'Natanggal ang expense',
      deleteFailed: 'Nabigo ang pagtanggal ng expense',
      expenseUpdated: 'Na-update ang expense',
      updateFailed: 'Nabigo ang pag-update ng expense',
      expenseCreated: 'Nagawa ang expense',
      addExpense: 'Magdagdag ng Expense',
      brandModelPerformance: 'Pagganap ng Tatak/Modelo',
      brand: 'Tatak',
      model: 'Modelo',
      count: 'Bilang',
      avgROI: 'Karaniwang ROI',
      topExpenses: 'Top Expenses ayon sa Category',
      noData: 'Walang data',
      monthlyTrends: 'Buwanang Tendensiya sa Pananalapi',
      expenseBreakdown: 'Breakdown ng Kategorya ng Gastos',
      maintenanceEfficiency: 'Kahusayan ng Maintenance',
      topPerformers: 'Nangungunang Performers',
      needsAttention: 'Kailangan ng Atensyon',
      fleetHealth: 'Pangkalahatan ng Kalusugan ng Fleet',
      totalScooters: 'Kabuuang Scooters',
      revenueVsMaintenance: 'Revenue vs Maintenance ayon sa Scooter',
      profitTrend: 'Profit Trend ayon sa Scooter',
      statusDistribution: 'Distribusyon ng Status',
      exportBasic: 'I-export ang Basic Summary',
      exportDetailed: 'I-export ang Detailed Expenses',
      exportingDetailed: 'Nag-e-export ng detailed expenses...',
      exportCompleted: 'Natapos ang export',
      exportFailed: 'Nabigo ang export'
    },
    landing: {
      heroTitle: '2026 na. Gumagamit ka pa rin ng spreadsheets.',
      heroSubtitle: 'Mag-digitalize ka na.',
      heroDescription: 'Ang scooter rental platform na talagang gumagana.',
      heroDescription2: 'Tigilan ang pagkalugi. Magsimula nang may saysay.',
      ctaButton: 'Tingnan ang Pagganap →',
      ctaLink: 'O patuloy na gawin sa papel, nasa sa iyo',
      contractTimeLabel: 'Average na oras ng paggawa ng kontrata',
      contractTimeHint: 'Ganun kabilis ka makakagawa ng kontrata. Walang paghihintay. Walang lag.',
      problemsTitle: 'Mali ka siguro.',
      problemsSubtitle: '(Sinabi namin ang sinabi namin.)',
      problem1Title: 'Ang spreadsheets ay hindi business model',
      problem1Desc: 'Kung mag-crash ang Excel, mag-crash din ang negosyo mo. Hindi yan resilience, yan ay Russian roulette sa kita mo. Gumising ka - hindi na 2015.',
      problem2Title: 'Nawawalan ka ng 15-30% sa manual errors',
      problem2Desc: 'Double-booked na scooters. Nakalimutang renewals. Nawalang kontrata. Bawat manual na hakbang ay pera na umaalis. Tigilan mo na.',
      problem3Title: 'Ang \'sistema\' mo ay WhatsApp + memory',
      problem3Desc: 'Mga mensahe ng client na nakakalat sa 3 apps. Mga petsa ng kontrata sa utak mo. Pag-track ng bayad sa notebook. Pare, hindi na 2010. Sumabay ka na.',
      problem4Title: 'Hindi mo ma-scale ang hindi mo ma-measure',
      problem4Desc: 'Aling scooter ang pinaka-profitable? Aling client ang pinakamatagal mag-renta? Ano ang actual ROI mo? Kung hindi mo alam, bulag ka. At mahal yan.',
      challengeTitle: 'Sa tingin mo mali kami?',
      challengeText: 'Patunayan mo. Ipakita mo ang sistema mong papel at ipapakita namin sa iyo kung ano ang hitsura ng 2026.',
      challengeButton: 'Ipakita Mo sa Akin ang Hitsura ng 2026 →',
      featuresTitle: 'Ang makukuha mo (na hindi mo alam na kailangan mo)',
      featuresSubtitle: 'Tunay na functionality. Tunay na bilis. Tunay na resulta.',
      featuresSubtitle2: 'Walang palabok, puro katotohanan lang.',
      feature1Title: 'Real-time fleet dashboard',
      feature1Desc: 'Tingnan lahat. Alamin lahat. Gumawa ng aksyon sa lahat. Available, rented, maintenance - lahat sa isang tingin. Wala nang "check ko lang ang notes ko" moments.',
      feature1Item1: 'Live status updates sa buong fleet mo',
      feature1Item2: 'Revenue tracking na may saysay talaga',
      feature1Item3: 'ROI calculations na hindi kailangan ng PhD',
      feature2Title: 'Mga kontrata na hindi basura',
      feature2Desc: 'Gumawa ng kontrata sa ilang segundo. I-track sila nang real-time. Hindi na mag-double-book. Hindi na mawalan ng renewal date. Hindi na magtaka "sino may scooter #42?"',
      feature2Item1: 'Kanban view dahil hindi visual ang spreadsheets',
      feature2Item2: 'Automatic conflict detection (oo, chine-check namin)',
      feature2Item3: 'Delivery planning na talagang gumagana',
      feature3Title: 'Renewals & reminders',
      feature3Desc: 'Insurance renewal reminders. Registration. Coverage. Contract end dates. Lahat tracked. Lahat automated. Lahat nagre-remind sa iyo bago mahuli ang lahat. Dahil expired insurance = mahal na problema. At missed contract renewals = nawalang kita.',
      feature3Item1: 'Insurance renewal reminders (huwag mong hayaang ma-expire)',
      feature3Item2: 'Contract due reminders (hindi na makakalimot ng renewal)',
      feature3Item3: 'Status badges: Active, Due Soon, Overdue (malalaman mo)',
      feature4Title: 'Calendar view na may saysay',
      feature4Desc: 'Tingnan ang buong schedule ng fleet mo sa isang tingin. Alamin kung kailan naka-renta ang mga scooter. Magplano nang maaga. Wala nang double-booking. Wala nang "kailan ba matatapos ang kontratang ito?" confusion.',
      feature4Item1: 'Visual timeline ng lahat ng kontrata',
      feature4Item2: 'Tingnan ang availability sa isang tingin',
      feature4Item3: 'Magplano ng deliveries at pickups nang propesyonal',
      feature5Title: 'QR code = instant customer contact',
      feature5Desc: 'I-scan ang QR code. I-message ang customer mo agad. Wala nang paghahanap sa WhatsApp. Wala nang "sino na naman ito?" moments. I-scan lang, i-message, tapos. 2026 na, kumilos ka na.',
      feature5Item1: 'QR codes sa bawat scooter',
      feature5Item2: 'Isang scan = WhatsApp/Line na may pre-filled message',
      feature5Item3: 'Wala nang paghahanap ng customer contacts',
      qrPhoneLabel: 'Phone Number',
      qrPhoneHint: 'Maglagay ng anumang numero para sa testing (hal., numero ng boyfriend ng asawa mo)',
      qrMessageLabel: 'Message',
      qrMessageHint: 'Maaari mong baguhin ang mensaheng ito para i-customize ang makikita ng customers',
      qrScanHint: 'I-scan gamit ang phone mo para i-test',
      qrClickLink: 'O i-click para buksan ang WhatsApp',
      feature6Title: 'Unlimited users. Full transparency.',
      feature6Desc: 'Magdagdag ng kasing dami ng team members na kailangan mo. Walang per-user fees. Nakikita ng admins ang lahat - sino ang gumawa ng ano, kailan, at bakit. Buong activity log. Walang lihim. Walang sorpresa.',
      feature6Item1: 'Unlimited users (seryoso, walang limit)',
      feature6Item2: 'Kumpletong activity log para sa admins',
      feature6Item3: 'Role-based permissions (user/admin)',
      feature7Title: 'Insurance reminders na nagse-save ng pera mo',
      feature7Desc: 'Huwag nang hayaang ma-expire ang insurance. Makakuha ng reminders bago mahuli ang lahat. Custom renewal windows. Smart alerts. Dahil expired insurance = mahal na problema. At hindi namin gusto ang mahal na problema.',
      feature7Item1: 'Insurance renewal reminders (hindi makakalimot ng deadline)',
      feature7Item2: 'Custom reminder windows (7 araw, 30 araw, ikaw ang pumili)',
      feature7Item3: 'Dashboard alerts na talagang nakaka-attract ng atensyon',
      feature8Title: 'Maintenance tracking & profitability analytics',
      feature8Desc: 'I-track ang bawat expense. Alamin kung aling scooter ang nagpapakita sa iyo. Tingnan ang ROI per scooter. I-track ang maintenance costs. Gumawa ng data-driven decisions. Tigilan ang paghula.',
      feature8Item1: 'Buong maintenance expense tracking',
      feature8Item2: 'Per-scooter profitability analytics',
      feature8Item3: 'ROI calculations na talagang mahalaga',
      feature9Title: 'Photos & mobile view na talagang gumagana',
      feature9Desc: 'Kumuha ng photos on the go. Mobile-optimized interface. Camera integration. Photo galleries per scooter. Markahan ang damage direkta sa photos. I-filter ayon sa damage status. Dahil ang isang larawan ay katumbas ng libong salita. At libong salita ay hindi makakatulong sa iyo sa korte.',
      feature9Item1: 'Mobile view na optimized para sa field work',
      feature9Item2: 'Camera integration - kumuha ng photos direkta sa app',
      feature9Item3: 'Markahan ang damage sa photos gamit ang visual indicators',
      feature9Item4: 'I-filter ang photos ayon sa damage status',
      feature10Title: 'Propesyonal na contract generation',
      feature10Desc: 'Auto-fill ang karamihan ng fields. Preview bago mag-print. Thai at English versions. Print-ready format. Wala nang manual typing. Wala nang errors. Gumawa lang, preview, print. Tapos.',
      feature10Item1: 'Auto-fill mula sa client & scooter data',
      feature10Item2: 'Preview bago mag-print (walang sorpresa)',
      feature10Item3: 'Kasama ang Thai & English versions',
      speedTitle: 'Napakabilis, iisipin mong sira',
      speedDesc: 'React Query caching. Prefetch-on-hover. Token rehydration. Code splitting. Lazy loading. Optimistic updates.',
      speedDesc2: 'Nagsasalita kami ng wika mo: bilis.',
      speedMetric1: 'Page Load Time',
      speedMetric2: 'Search & Filter',
      speedMetric3: 'Data Refresh',
      finalCTATitle: 'Handa na bang tigilan ang pagkalugi?',
      finalCTAText: 'O patuloy na gawin sa papel. Hindi kami ang boss mo.',
      finalCTAText2: '(Pero mas maganda sana ang kita mo. Sinasabi lang.)',
      finalCTAButton: 'Tingnan ang Pagganap →',
      finalCTALink: 'O sabihin na mali kami',
      viewFullContract: 'Tingnan ang Buong Kontrata'
    }
  },
  vi: {
    howItWorks: {
      title: 'Cách sử dụng OurScooter (Dành cho nhân viên)',
      subtitle: 'Các bước đơn giản, quy tắc rõ ràng và mẹo hữu ích để làm việc đúng cách và hiệu quả.',
      categories: 'Danh mục',
      whyTitle: 'Tại sao chọn OurScooter: mục đích và lợi ích',
      whyDescription: 'OurScooter giúp nhân viên vận hành công việc cho thuê xe hằng ngày hiệu quả: ít thao tác hơn, ít sai sót hơn và hồ sơ rõ ràng.',
      whySpeed: 'Tốc độ: tạo hợp đồng trong vài giây, tái sử dụng thông tin qua nhân bản.',
      whyClarity: 'Rõ ràng: trạng thái cho thấy mỗi lượt thuê đang ở đâu chỉ trong một cái nhìn.',
      whyControl: 'Kiểm soát: lọc, tìm kiếm và chuyển đổi chế độ xem (bảng/kanban) cho phù hợp với công việc.',
      whyAccuracy: 'Chính xác: các rào chắn ngăn các thao tác không hợp lệ và lượt thuê chồng chéo.',
      signingInTitle: 'Đăng nhập và duy trì đăng nhập',
      signingInToken: 'Tên đăng nhập + mật khẩu → máy chủ xác minh và trả về access + refresh token.',
      signingInAutoRefresh: 'Access token có thời hạn ngắn; chúng tôi tự động làm mới ngầm bằng refresh token.',
      signingInRoles: 'Vai trò: admin và nhân viên. Ngữ cảnh tổ chức được chọn ở thanh bên.',
      signingInSession: 'Thời lượng phiên: Bạn duy trì đăng nhập tối đa 1 ngày không hoạt động. Ứng dụng dùng access token ngắn 15 phút và tự gia hạn trong nền. Sau 1 ngày không dùng ứng dụng (hoặc nếu xóa cookie), bạn sẽ được yêu cầu đăng nhập lại.',
      scootersTitle: 'Xe: thêm và chỉnh sửa',
      scootersOpen: 'Mở "Add Scooter" để đăng ký một xe mới. Giữ thông tin ngắn gọn và chính xác.',
      scootersRequired: 'Bắt buộc: hãng/mẫu, trạng thái.',
      scootersOptional: 'Tùy chọn: giá mua, thông tin xăng/pin, ghi chú.',
      scootersTip: 'Mẹo: trạng thái "maintenance" loại xe khỏi danh sách có sẵn cho đến khi được trả lại.',
      clientsTitle: 'Khách hàng: thêm và chỉnh sửa',
      clientsOpen: 'Mở "New Client". Ghi lại những gì giúp bạn tìm lại họ sau này.',
      clientsRequired: 'Bắt buộc: họ và tên.',
      clientsOptional: 'Tùy chọn: điện thoại, email, CMND/hộ chiếu, ghi chú.',
      contractsTitle: 'Hợp đồng: tạo và quản lý',
      contractsWaysTitle: 'Cách tạo hợp đồng (4 lựa chọn)',
      contractsFromContracts: 'Từ Contracts: nhấn "Add Contract".',
      contractsFromScooters: 'Từ Scooters: mở một xe → "New Contract".',
      contractsFromClients: 'Từ Clients: mở một khách hàng → "New Contract".',
      contractsDuplicate: 'Nhân bản: mở một hợp đồng hiện có → "Duplicate" để tái sử dụng thông tin một cách an toàn.',
      contractsRequiredTitle: 'Các trường bắt buộc',
      contractsRequiredClient: 'Khách hàng và Xe.',
      contractsRequiredDate: 'Ngày/giờ bắt đầu.',
      contractsRequiredRate: 'Giá theo ngày. (Tiền cọc/phương thức thanh toán là tùy chọn.)',
      contractsStatusTitle: 'Trạng thái và quy trình (những gì được phép)',
      contractsStatusFlow: 'Draft → Active → Closed → Archived.',
      contractsStatusActivate: 'Bạn chỉ có thể kích hoạt khi các trường bắt buộc đã được đặt và xe có sẵn.',
      contractsStatusClosed: 'Từ Closed bạn chỉ có thể chuyển sang Archived (không thể quay lại Active).',
      contractsStatusForbidden: 'Không cho phép: các hợp đồng Active chồng chéo cho cùng một xe.',
      contractsStatusArchived: 'Hợp đồng Archived biến mất khỏi chế độ xem mặc định. Bật "Include archived" để xem.',
      contractsAutoTitle: 'Tự động cập nhật trạng thái xe',
      contractsAutoCreate: 'Tạo hợp đồng không thay đổi trạng thái xe (bất kể trạng thái hợp đồng).',
      contractsAutoActive: 'Khi một hợp đồng trở thành Active → xe tự động trở thành "rented".',
      contractsAutoClosed: 'Khi một hợp đồng Active được Close/Archive → xe tự động trở thành "available".',
      contractsAutoDraft: 'Khi một hợp đồng Active bị đưa về Draft → xe tự động trở thành "available".',
      contractsAutoPrevent: 'Điều này ngăn việc đặt trùng và giữ cho tình trạng sẵn có của xe chính xác.',
      contractsTimeTitle: 'Theo dõi thời gian cho hợp đồng Active',
      contractsTimeRemaining: 'Hợp đồng Active hiển thị thời gian còn lại đến ngày kết thúc (vd: "2h 30m left").',
      contractsTimeOverdue: 'Hợp đồng quá hạn hiển thị thời gian đã trôi qua (vd: "Overdue 1d 5h").',
      contractsTimeColors: 'Màu sắc thể hiện mức độ khẩn cấp: Xanh lá (4+ giờ), Vàng (1-4 giờ), Cam (<1 giờ), Đỏ (quá hạn).',
      contractsTimeKanban: 'Trong chế độ Kanban: chỉ hiển thị bộ đếm thời gian cho hợp đồng Active.',
      contractsTimeList: 'Trong chế độ List: hiển thị cả huy hiệu trạng thái và bộ đếm thời gian cho hợp đồng Active.',
      contractsViewsTitle: 'Hợp đồng: chế độ xem',
      contractsViewsTable: 'Chế độ bảng: quét nhanh, lọc và xuất các hàng.',
      contractsViewsKanban: 'Bảng Kanban: quy trình trực quan theo trạng thái (Draft, Active, Closed, Archived).',
      contractsViewsToggle: 'Dùng nút chuyển chế độ xem trong thanh công cụ Contracts để đổi chế độ.',
      contractsKanbanTitle: 'Làm việc trên bảng Kanban',
      contractsKanbanDrag: 'Kéo & thả một thẻ để chuyển nó sang trạng thái tiếp theo được phép.',
      contractsKanbanEdit: 'Mở một thẻ và nhấn Edit để thay đổi thông tin hoặc trạng thái trực tiếp.',
      contractsKanbanRules: 'Áp dụng quy tắc: bạn không thể chuyển từ Closed về Active; Archived là cuối cùng.',
      contractsKanbanBlocked: 'Các thao tác không khả dụng bị chặn (vd: xe đã Active ở nơi khác).',
      contractsKanbanFilters: 'Dùng bộ lọc để tập trung vào một khách hàng hoặc bao gồm các mục Archived khi cần.',
      findingTitle: 'Tìm nhanh (lọc & tìm kiếm)',
      findingScooters: 'Xe: lọc theo trạng thái, hãng, mẫu. Dùng ô tìm kiếm để khớp văn bản nhanh.',
      findingClients: 'Khách hàng/Người dùng: tìm theo tên/email; điều chỉnh kích thước trang để xem nhiều hàng hơn.',
      findingContracts: 'Hợp đồng: lọc theo trạng thái; tùy chọn bao gồm đã lưu trữ.',
      rowsTitle: 'Chúng tôi hiển thị bao nhiêu hàng?',
      rowsScooters: 'Xe: tải theo trang và lấy thêm khi cần (tối đa 100 mỗi lần tải).',
      rowsClients: 'Khách hàng/Người dùng: bạn có thể chọn 10/20/30/40/50 hàng mỗi trang.',
      rowsContracts: 'Hợp đồng: hiển thị hợp đồng của tổ chức hiện tại; bao gồm đã lưu trữ để mở rộng danh sách.',
      themesTitle: 'Giao diện và mặc định',
      themesChange: 'Thay đổi giao diện (Dark/Light/Blue/Wood) ở thanh bên dưới cùng bên trái.',
      themesAdmin: 'Admin có thể cấu hình mặc định của tổ chức trong Management (vd: tiền tệ, mức giá).',
      usersTitle: 'Người dùng & Quản lý (admin)',
      usersStaff: 'Nhân viên: vận hành hằng ngày, xem và tạo hợp đồng, quản lý xe/khách hàng.',
      usersAdmin: 'Admin: mọi thứ nhân viên làm được, cộng thêm cài đặt cấp tổ chức và xóa.',
      tipsTitle: 'Mẹo & khắc phục sự cố',
      tipsActivate: 'Không kích hoạt được? Kiểm tra trạng thái xe (phải có sẵn) và các trường bắt buộc.',
      tipsContract: 'Không thấy hợp đồng? Có thể nó đã được lưu trữ; bật "Include archived".',
      tipsSession: 'Phiên đã kết thúc? Nếu đã hơn một ngày, chỉ cần đăng nhập lại.'
    },
    booking: {
      orgNotFound: 'Không tìm thấy tổ chức',
      orgNotFoundDesc: 'Trang đặt xe bạn đang tìm không tồn tại. Điều này có thể có nghĩa là:',
      orgNotFoundReason1: 'Slug của tổ chức không chính xác',
      orgNotFoundReason2: 'Tổ chức chưa thiết lập trang đặt xe',
      orgNotFoundReason3: 'Trang đặt xe đã bị xóa',
      orgNotFoundOwner: 'Nếu bạn là chủ tổ chức, hãy đảm bảo bạn đã cấu hình slug của tổ chức trong Management → Technical Details.',
      requestReceived: 'Đã nhận yêu cầu!',
      requestReceivedDesc: 'Yêu cầu đặt xe của bạn đã được nhận. Đây chưa phải là đặt xe đã xác nhận.',
      willContact: '{{business}} sẽ liên hệ với bạn sớm để xác nhận việc thuê xe.',
      requestIdLabel: 'Mã yêu cầu:',
      contactLabel: 'Liên hệ:',
      showroomBadge: 'Phòng trưng bày xe',
      bookBadge: 'Đặt xe',
      showroomIntro: 'Xem các xe của chúng tôi, chọn chiếc bạn thích và gửi yêu cầu đặt xe. Chúng tôi sẽ xác nhận với bạn sớm.',
      bookIntro: 'Gửi yêu cầu đặt xe bên dưới và chúng tôi sẽ liên hệ với bạn sớm để xác nhận.',
      yourDetailsStep: '3. Thông tin của bạn',
      yourInformation: 'Thông tin của bạn',
      fullName: 'Họ và tên',
      fullNamePlaceholder: 'Nhập họ và tên',
      email: 'Email',
      emailPlaceholder: 'Nhập địa chỉ email',
      localPhone: 'Số điện thoại trong nước',
      phonePlaceholder: 'Số điện thoại',
      messengerNumber: 'Số Messenger (WhatsApp, v.v.)',
      messengerPlaceholder: 'Số WhatsApp (tùy chọn)',
      sameAsPhone: 'Giống số điện thoại',
      lineId: 'LINE ID',
      lineIdPlaceholder: 'LINE ID (tùy chọn)',
      passportNumber: 'Số hộ chiếu',
      passportPlaceholder: 'Số hộ chiếu (tùy chọn)',
      nationality: 'Quốc tịch',
      nationalityPlaceholder: 'Quốc tịch (tùy chọn)',
      localAddress: 'Địa chỉ trong nước',
      localAddressPlaceholder: 'Địa chỉ trong nước (tùy chọn)',
      rentalDatesStep: '1. Ngày thuê',
      bookingDetails: 'Chi tiết đặt xe',
      startDate: 'Ngày bắt đầu',
      duration: 'Thời hạn',
      endDate: 'Ngày kết thúc',
      dayUnit: 'ngày',
      daysUnit: 'ngày',
      weekUnit: 'tuần',
      weeksUnit: 'tuần',
      monthUnit: 'tháng',
      monthsUnit: 'tháng',
      scooterUnit: 'xe',
      scootersUnit: 'xe',
      pricingSummary: 'Tóm tắt giá',
      lineDailyRate: '{{count}} {{unit}} × Giá theo ngày:',
      lineWeeklyRate: '{{count}} {{unit}} × Giá theo tuần:',
      lineDailyFromWeekly: '{{count}} {{unit}} × Giá theo ngày (từ giá tuần):',
      lineMonthlyRate: '{{count}} {{unit}} × Giá theo tháng:',
      lineDailyFromMonthly: '{{count}} {{unit}} × Giá theo ngày (từ giá tháng):',
      subtotal: 'Tạm tính:',
      deposit: 'Tiền đặt cọc:',
      total: 'Tổng cộng:',
      selectToSeePricingShowroom: 'Chọn ngày và xe để xem giá',
      selectToSeePricingGroup: 'Chọn ngày và nhóm giá để xem giá',
      selectPricingGroup: 'Chọn nhóm giá',
      perDay: '/ngày',
      perWeek: '/tuần',
      perMonth: '/tháng',
      specialPromotions: 'Khuyến mãi đặc biệt:',
      scooterPreference: 'Xe ưu tiên',
      noPreference: 'Không ưu tiên',
      scooterFallback: 'Xe máy',
      chooseScooterStep: '2. Chọn xe của bạn',
      checkingAvailability: 'Đang kiểm tra tình trạng cho ngày của bạn…',
      availableForDates: '{{available}} trong {{total}} xe có sẵn cho ngày của bạn',
      fleetCount: '{{count}} {{unit}} trong đội xe của chúng tôi',
      lockedScooterNote: 'Chiếc xe này đã được giữ cho bạn qua liên kết đặt xe của bạn',
      noScootersAvailable: 'Hiện không có xe nào. Vui lòng liên hệ trực tiếp với chúng tôi.',
      available: 'Có sẵn',
      unavailable: 'Không có sẵn',
      photosCount: '{{count}} ảnh',
      viewPhoto: 'Xem',
      viewPhotosAria: 'Xem ảnh của {{title}}',
      additionalNotes: 'Ghi chú thêm',
      notesPlaceholder: 'Bất kỳ yêu cầu hoặc thông tin đặc biệt nào...',
      charactersCount: '{{count}}/255 ký tự',
      preSignTitle: '⚡ Ký hợp đồng trước (Khuyến nghị)',
      preSignDesc: 'Tăng tốc quá trình nhận xe! Ký hợp đồng trước giúp tiết kiệm thời gian khi bạn đến.',
      preSignBenefits: 'Không cần thanh toán • Không phải cam kết • Chỉ giúp nhận xe nhanh hơn',
      completeFieldsToSign: 'Hoàn thành tất cả các trường bắt buộc ở trên để bật tính năng ký hợp đồng trước.',
      missingLabel: 'Còn thiếu:',
      signatureSaved: 'Đã lưu chữ ký: {{name}}',
      submitting: 'Đang gửi...',
      submitRequest: 'Gửi yêu cầu đặt xe',
      disclaimer: 'Bằng việc gửi biểu mẫu này, bạn hiểu rằng đây là yêu cầu, không phải đặt xe đã xác nhận. Cửa hàng sẽ liên hệ với bạn để xác nhận tình trạng và hoàn tất việc đặt xe.',
      selectDatesForPrice: 'Chọn ngày của bạn để xem giá',
      depositShort: 'đặt cọc',
      requestToBook: 'Yêu cầu đặt xe',
      continueBtn: 'Tiếp tục',
      closeBtn: 'Đóng',
      prevPhoto: 'Ảnh trước',
      nextPhoto: 'Ảnh tiếp theo',
      langLabel: 'Ngôn ngữ',
      toastLoadFailed: 'Không thể tải trang đặt xe',
      toastVersionFailed: 'Không thể tạo phiên bản hợp đồng. Vui lòng thử lại.',
      toastPreviewFailed: 'Không thể tải bản xem trước hợp đồng',
      toastEnterName: 'Vui lòng nhập họ và tên của bạn',
      toastEnterEmail: 'Vui lòng nhập địa chỉ email của bạn',
      toastEnterPhone: 'Vui lòng nhập số điện thoại của bạn',
      toastSelectStart: 'Vui lòng chọn ngày bắt đầu',
      toastSelectScooter: 'Vui lòng chọn một xe',
      toastSelectPricingGroup: 'Vui lòng chọn nhóm giá',
      toastPricingFailed: 'Không thể tính giá',
      toastSubmitFailed: 'Không thể gửi yêu cầu đặt xe. Vui lòng thử lại.',
      errFullNameRequired: 'Họ và tên là bắt buộc',
      errEmailRequired: 'Email là bắt buộc',
      errEmailInvalid: 'Email không hợp lệ',
      errPhoneRequired: 'Số điện thoại là bắt buộc',
      mfFullName: 'Họ và tên',
      mfEmail: 'Email',
      mfPhone: 'Số điện thoại',
      mfStartDate: 'Ngày bắt đầu',
      mfScooter: 'Xe máy',
      mfPricingGroup: 'Nhóm giá',
      mfPricingInfo: 'Thông tin giá'
    },
    theme: {
      dark: 'Tối',
      light: 'Sáng',
      blue: 'Xanh',
      wood: 'Gỗ'
    },
    home: {
      quickActions: 'Thao tác nhanh',
      recentActivity: 'Hoạt động gần đây',
      fleetStatusOverview: 'Tổng quan trạng thái đội xe',
      welcomeTitle: 'Chào mừng đến với OurScooters',
      welcomeSubtitle: 'Bảng điều khiển quản lý đội xe scooter toàn diện của bạn',
      addScooter: 'Xe mới',
      registerScooter: 'Đăng ký xe scooter mới',
      addClient: 'Khách hàng mới',
      registerClient: 'Đăng ký khách hàng mới',
      newContract: 'Hợp đồng mới',
      createAgreement: 'Tạo hợp đồng thuê',
      org: 'Tổ chức',
      allOrgs: 'Tất cả tổ chức',
      availableScooters: 'Xe sẵn sàng',
      readyForRental: 'Sẵn sàng cho thuê',
      rentedScooters: 'Xe đang thuê',
      currentlyInUse: 'Đang được sử dụng',
      maintenance: 'Bảo trì',
      underRepair: 'Đang sửa chữa',
      fleetOverview: 'Tổng quan đội xe',
      available: 'sẵn sàng',
      rented: 'đang thuê',
      activeRevenue: 'Doanh thu đang hoạt động',
      monthlyRecurring: 'Doanh thu định kỳ hàng tháng',
      totalClients: 'Tổng số khách hàng',
      activeContracts: 'hợp đồng đang hoạt động',
      avgBattery: 'Pin trung bình',
      fleetBatteryHealth: 'Tình trạng pin đội xe',
      avgFuel: 'Nhiên liệu trung bình',
      fuel: 'nhiên liệu',
      electric: 'điện',
      fleetROI: 'ROI đội xe',
      rev: 'doanh thu',
      cost: 'chi phí',
      inMaintenance: 'Đang bảo trì',
      newClientRegistered: 'Khách hàng mới đã đăng ký',
      registered: 'đã đăng ký'
    },
    users: {
      title: 'Người dùng',
      errorLoading: 'Không thể tải người dùng.',
      id: 'ID',
      email: 'Email',
      role: 'Vai trò',
      username: 'Tên đăng nhập',
      org: 'Tổ chức',
      roleSuperadmin: 'Quản trị tối cao',
      roleAdmin: 'Quản trị viên',
      roleStaff: 'Nhân viên'
    },
    management: {
      title: 'Quản lý tổ chức',
      errorLoading: 'Lỗi khi tải cài đặt.',
      timeFormat: 'Định dạng thời gian',
      defaultCurrency: 'Tiền tệ mặc định',
      defaultCountryCode: 'Mã quốc gia mặc định',
      defaultDailyPrice: 'Giá theo ngày mặc định',
      defaultDeposit: 'Tiền đặt cọc hợp đồng mặc định',
      defaultRentLength: 'Thời hạn thuê mặc định (Ngày)',
      defaultPaymentMethod: 'Phương thức thanh toán mặc định',
      buttonStyle: 'Kiểu nút',
      buttonStyleDesc: 'Chọn kiểu hiển thị cho tất cả các nút trong ứng dụng.',
      countryCodeHint: '1 cho Hoa Kỳ, 44 cho Anh, v.v.',
      countryCodeExplain: 'Chỉ chữ số. Sẽ thêm tiền tố vào số điện thoại trong biểu mẫu khách hàng.',
      saving: 'Đang lưu...',
      saveSettings: 'Lưu cài đặt',
      saved: 'Đã lưu',
      modernStyle: 'Kiểu hiện đại',
      classicStyle: 'Kiểu cổ điển',
      originalStyle: 'Kiểu gốc',
      btnPrimary: 'Chính',
      btnSecondary: 'Phụ',
      btnGhost: 'Trong suốt',
      btnDanger: 'Nguy hiểm',
      modernDesc: 'Nền chuyển sắc với hiệu ứng vi tương tác và góc bo tròn',
      classicDesc: 'Thiết kế gọn gàng, tối giản với màu sắc theo chủ đề và viền tinh tế',
      originalDesc: 'Thiết kế nút ban đầu bạn thích trước khi tái cấu trúc',
      timeFormatExample1: '15 Tháng 9 2025 6:09 CH',
      timeFormatExample2: 'Tháng 9 15 2025 6:09 CH',
      timeFormatExample3: '15 Tháng 9 2025 18:09',
      timeFormatExample4: 'Tháng 9 15 2025 18:09'
    },
    common: {
      language: 'Ngôn ngữ',
      phoneTooShort: 'Số điện thoại phải có ít nhất 7 chữ số',
      phoneTooLong: 'Số điện thoại quá dài (tối đa 15 chữ số)',
      theme: 'Giao diện',
      save: 'Lưu',
      cancel: 'Hủy',
      logout: 'Đăng xuất',
      loading: 'Đang tải...',
      reset: 'Đặt lại',
      close: 'Đóng',
      back: 'Quay lại',
      delete: 'Xóa',
      clearAll: 'Xóa tất cả',
      upload: 'Tải lên',
      uploading: 'Đang tải lên...',
      selectPhoto: 'Chọn ảnh',
      addNotes: 'Thêm ghi chú...',
      notes: 'Ghi chú',
      optional: 'Tùy chọn',
      retake: 'Chụp lại',
      usePhoto: 'Dùng ảnh',
      viewAll: 'Xem tất cả ảnh',
      confirmDelete: 'Bạn có chắc muốn xóa ảnh này không?',
      add: 'Thêm',
      copied: 'Đã sao chép',
      expand: 'Mở rộng',
      collapse: 'Thu gọn',
      updateFailed: 'Cập nhật thất bại',
      updateFailedWithMessage: 'Cập nhật thất bại: {{message}}',
      failedToSendEmail: 'Gửi email thất bại',
      statusChangeNotAllowed: 'Không cho phép thay đổi trạng thái này',
      expenseAddedSuccessfully: 'Đã thêm chi phí thành công',
      edit: 'Chỉnh sửa'
    },
    toasts: {
      contractCreated: 'Tạo hợp đồng thành công',
      contractUpdated: 'Cập nhật hợp đồng thành công',
      contractDeleted: 'Xóa hợp đồng thành công',
      contractCreateFailed: 'Không thể tạo hợp đồng',
      contractUpdateFailed: 'Không thể cập nhật hợp đồng',
      contractDeleteFailed: 'Không thể xóa hợp đồng',
      scooterCreated: 'Tạo xe scooter thành công',
      scooterUpdated: 'Cập nhật xe scooter thành công',
      scooterDeleted: 'Xóa xe scooter thành công',
      scooterStatusUpdated: 'Cập nhật trạng thái xe thành công',
      scooterStatusUpdateFailed: 'Không thể cập nhật trạng thái xe',
      clientCreated: 'Tạo khách hàng thành công',
      clientUpdated: 'Cập nhật khách hàng thành công',
      clientDeleted: 'Xóa khách hàng thành công',
      settingsUpdated: 'Cập nhật cài đặt thành công!',
      scooterStatusChangedAuto: 'Trạng thái xe đã thay đổi tự động',
      scooterStatusChangeDesc: '{{scooterName}} đã được chuyển từ {{fromStatus}} sang {{toStatus}} {{reason}}.',
      becauseContractActivated: 'vì hợp đồng đã được kích hoạt',
      becauseContractDeactivated: 'vì hợp đồng đã được đóng/lưu trữ',
      automatically: 'tự động',
      viewScooter: 'Xem xe',
      scooterOpenedFromNotif: 'Đã mở chi tiết {{scooterName}}',
      scooterOpenedFromNotifDesc: 'Xe này được mở tự động từ thông báo thay đổi trạng thái.',
      closedContractsMoveRestriction: 'Hợp đồng đã đóng chỉ có thể chuyển sang Lưu trữ.'
    },
    login: {
      title: 'Đăng nhập',
      email: 'Email',
      username: 'Tên đăng nhập',
      password: 'Mật khẩu',
      signIn: 'Đăng nhập',
      signingIn: 'Đang đăng nhập...',
      login: 'Đăng nhập',
      loggingIn: 'Đang đăng nhập...',
      loginFailed: 'Đăng nhập thất bại',
      language: 'Ngôn ngữ'
    },
    sidebar: {
      scooters: 'Xe scooter',
      clients: 'Khách hàng',
      contracts: 'Hợp đồng',
      finance: 'Tài chính',
      users: 'Người dùng',
      activity: 'Nhật ký hoạt động',
      management: 'Quản lý',
      howItWorks: 'Cách hoạt động',
      signIn: 'Đăng nhập',
      expand: 'Mở rộng thanh bên',
      collapse: 'Thu gọn thanh bên',
      logoutConfirm: 'Bạn có chắc muốn đăng xuất không?'
    },
    toolbar: {
      searchPlaceholder: 'Tìm theo ID, mẫu xe hoặc trạng thái…',
      clear: 'Xóa',
      listView: 'Dạng danh sách',
      kanbanView: 'Dạng Kanban',
      calendarView: 'Lịch',
      gridView: 'Dạng lưới',
      mapView: 'Dạng bản đồ',
      filter: 'Lọc',
      export: 'Xuất',
      total: 'Tổng: {{count}}',
      queryLabel: 'truy vấn',
      clearQuery: 'Xóa truy vấn',
      removeFilter: 'Xóa {{filter}}'
    },
    ui: {
      zoom: 'Thu phóng'
    },
    calendar: {
      week: 'Tuần',
      month: 'Tháng',
      all: 'Tất cả',
      plusMore: '+{{count}} nữa',
      scrollHint: 'Shift+Cuộn để di chuyển ngang',
      hideEmpty: 'Ẩn xe không có hợp đồng sắp tới'
    },
    status: {
      available: 'Sẵn sàng',
      rented: 'Đang thuê',
      maintenance: 'Bảo trì',
      retired: 'Ngừng sử dụng',
      draft: 'Bản nháp',
      active: 'Hoạt động',
      closed: 'Đã đóng',
      archived: 'Đã lưu trữ'
    },
    clients: {
      id: 'Mã khách hàng',
      name: 'Tên',
      messenger: 'WhatsApp',
      newClient: 'Khách hàng mới',
      creating: 'Đang tạo khách hàng...',
      fullName: 'Họ và tên',
      fullNamePlaceholder: 'Nhập họ và tên',
      email: 'Email',
      emailPlaceholder: 'Nhập địa chỉ email',
      localPhone: 'Số điện thoại địa phương',
      localPhonePlaceholder: 'Nhập số điện thoại địa phương (tùy chọn)',
      messengerNumber: 'Số WhatsApp',
      countryCodePlaceholder: '1',
      messengerNumberPlaceholder: 'Nhập số WhatsApp (tùy chọn)',
      lineId: 'LINE ID',
      lineIdPlaceholder: 'Nhập LINE ID (tùy chọn)',
      whatsappButton: 'WhatsApp',
      lineButton: 'LINE',
      contactViaWhatsApp: 'Liên hệ qua WhatsApp',
      contactViaLINE: 'Liên hệ qua LINE',
      notes: 'Ghi chú',
      notesPlaceholder: 'Ghi chú bổ sung về khách hàng này (ví dụ: số điện thoại quốc gia khác)',
      addClient: 'Khách hàng mới',
      detailsTitle: 'Chi tiết khách hàng',
      created: 'Đã tạo',
      clientId: 'Mã khách hàng',
      edit: 'Sửa khách hàng',
      previousContracts: 'Hợp đồng trước đây:',
      noCurrentContracts: 'Không có hợp đồng hiện tại',
      editClient: 'Sửa khách hàng',
      newContractBtn: 'Hợp đồng mới',
      close: 'Đóng',
      statusHistory: 'Lịch sử trạng thái',
      unknownClient: 'Khách hàng không xác định'
    },
    scooters: {
      id: 'Mã xe',
      modelRequired: 'Bắt buộc nhập mẫu xe',
      batteryRange: 'Pin phải từ 0–100',
      odometerNegative: 'Số km không thể âm',
      fuelRange: 'Nhiên liệu % phải từ 0–100',
      incompatibleEnergy: 'Xe điện không thể có nhiên liệu; xe nhiên liệu không thể có pin',
      editScooter: 'Sửa xe',
      newScooter: 'Xe mới',
      updating: 'Đang cập nhật xe...',
      creating: 'Đang tạo xe...',
      model: 'Mẫu xe',
      modelPlaceholder: 'vd. Ninebot Max',
      status: 'Trạng thái',
      typeLevel: 'Loại & Mức',
      odometer: 'Đồng hồ km',
      qrCode: 'Mã QR',
      qrPlaceholder: 'Mã QR tùy chọn',
      plateNumber: 'Biển số',
      platePlaceholder: 'Biển số tùy chọn',
      nickname: 'Biệt danh',
      nicknamePlaceholder: 'vd. Honda Đỏ #1',
      nicknameOptional: 'Biệt danh (tùy chọn)',
      brand: 'Hãng',
      brandPlaceholder: 'Hãng tùy chọn',
      vehicleType: 'Loại xe',
      electric: 'Điện',
      fuel: 'Nhiên liệu',
      batteryPct: 'Pin %',
      fuelPct: 'Nhiên liệu %',
      odometerKm: 'Đồng hồ km (km)',
      updateScooter: 'Cập nhật xe',
      addScooter: 'Xe mới',
      details: 'Chi tiết',
      edit: 'Sửa',
      created: 'Đã tạo',
      statusHistory: 'Lịch sử trạng thái xe',
      columnScooterId: 'Mã xe',
      columnBrand: 'Hãng',
      columnModel: 'Mẫu xe',
      columnPlateNumber: 'Biển số',
      columnNickname: 'Biệt danh',
      columnStatus: 'Trạng thái',
      columnVehicleStatus: 'Loại & Mức',
      columnOdometer: 'Đồng hồ km',
      pictures: 'Hình ảnh',
      noPhotos: 'Chưa có ảnh',
      noPhotosYet: 'Chưa có ảnh',
      allPhotos: 'Tất cả ảnh',
      viewAllPhotos: 'Xem tất cả ảnh',
      damage: 'Hư hỏng',
      damageOnly: 'Hư hỏng',
      showingDamage: 'Đang hiển thị hư hỏng',
      noDamagePhotos: 'Không có ảnh hư hỏng',
      markAsDamage: 'Đánh dấu hư hỏng',
      markDamage: 'Đánh dấu hư hỏng',
      removeDamage: 'Bỏ đánh dấu hư hỏng',
      markNoDamage: 'Đánh dấu không hư hỏng',
      damageDetected: 'Phát hiện hư hỏng',
      scooterPhoto: 'Ảnh xe',
      selectImageFile: 'Vui lòng chọn tệp hình ảnh',
      fileSizeLimit: 'Kích thước tệp phải nhỏ hơn 10MB',
      photoUploaded: 'Tải ảnh lên thành công',
      photoDeleted: 'Đã xóa ảnh',
      photoDeleteFailed: 'Không thể xóa ảnh',
      photoUpdateFailed: 'Không thể cập nhật ảnh',
      photoUploadFailed: 'Không thể tải ảnh lên',
      photoStorageNotConfigured: 'Bộ lưu trữ ảnh chưa được cấu hình. Vui lòng liên hệ quản trị viên.',
      deletePhoto: 'Xóa ảnh',
      drawCircle: 'Vẽ vòng tròn',
      stopDrawing: 'Dừng vẽ',
      undo: 'Hoàn tác',
      redo: 'Làm lại',
      capture: 'Chụp',
      takePhoto: 'Chụp ảnh',
      chooseFromGallery: 'Chọn từ thư viện',
      addPhoto: 'Thêm ảnh',
      photoPreview: 'Xem trước ảnh',
      cameraError: 'Không thể truy cập máy ảnh',
      cameraPermissionDenied: 'Quyền truy cập máy ảnh bị từ chối'
    },
    pages: {
      manageColumns: 'Quản lý cột',
      columns: 'Cột',
      retiredHidden: 'Xe ngừng sử dụng đã được ẩn',
      failedLoadScooters: 'Không thể tải xe',
      checkConnection: 'Vui lòng kiểm tra kết nối và thử lại.',
      retry: 'Thử lại',
      clearFilters: 'Xóa bộ lọc',
      addScooter: 'Xe mới',
      noScootersYet: 'Chưa có xe',
      noScootersMatch: 'Không có xe nào khớp với bộ lọc',
      addFirstScooter: 'Thêm xe đầu tiên để bắt đầu.',
      tryAdjusting: 'Thử điều chỉnh hoặc xóa bộ lọc.',
      rowsPerPage: 'Số hàng mỗi trang:',
      pageOf: 'Trang {{current}} / {{total}}',
      prevShort: 'Trước',
      nextShort: 'Sau',
      prev: 'Trang trước',
      next: 'Trang sau'
    },
    contractsPage: {
      newContract: 'Hợp đồng mới',
      showArchived: 'Hiển thị hợp đồng đã lưu trữ',
      newContractSubtitle: 'Nhanh chóng tạo và xem lại hợp đồng thuê mới',
      createdDate: 'Ngày tạo',
      recentlyUpdated: 'Cập nhật gần đây',
      recentlyClosed: 'Đóng gần đây',
      allClosed: 'Tất cả đã đóng',
      showAll: 'Hiển thị tất cả',
      showRecent: 'Hiển thị gần đây',
      sortBy: 'Sắp xếp theo...',
      archivedOnlyToDraft: 'Hợp đồng đã lưu trữ chỉ có thể chuyển sang Bản nháp.'
    },
    activity: {
      title: 'Nhật ký hoạt động',
      subtitle: 'Theo dõi hoạt động của nhân viên và các thay đổi hệ thống. Xem nhật ký dễ hiểu để khắc phục sự cố và kiểm toán.',
      filterActivity: 'Lọc hoạt động',
      timePeriod: 'Khoảng thời gian',
      lastHour: 'Giờ qua',
      today: 'Hôm nay',
      thisWeek: 'Tuần này',
      thisMonth: 'Tháng này',
      custom: 'Tùy chỉnh',
      from: 'Từ',
      to: 'Đến',
      employee: 'Nhân viên',
      type: 'Loại hoạt động',
      allEmployees: 'Tất cả nhân viên',
      allActivities: 'Tất cả hoạt động',
      scooterMgmt: 'Quản lý xe',
      contractMgmt: 'Quản lý hợp đồng',
      clientMgmt: 'Quản lý khách hàng',
      orgSettings: 'Cài đặt tổ chức',
      searchEverything: 'Tìm kiếm mọi thứ',
      searchPlaceholder: 'Tìm tên, ID, giá, trạng thái...'
    },
    contracts: {
      filters: 'Bộ lọc',
      editContract: 'Sửa hợp đồng',
      dates: 'Ngày',
      for: 'cho',
      scooter: 'Xe',
      start: 'Bắt đầu',
      end: 'Kết thúc',
      duration: 'Thời lượng',
      days: 'ngày',
      weeks: 'tuần',
      months: 'tháng',
      estimatedTotal: 'Tổng ước tính',
      fetchingData: 'Đang tải khách hàng và xe',
      scooterCurrentlyRented: 'ℹ️ Xe này hiện đang được thuê{{until}}.',
      until: 'đến',
      futureReservationNote: 'Bạn vẫn có thể tạo hợp đồng cho đặt chỗ trong tương lai - chỉ cần đảm bảo các ngày không trùng nhau.',
      searchClients: 'Tìm khách hàng theo tên/email/id',
      noMatches: 'Không có kết quả',
      selected: 'Đã chọn:',
      scooterQuery: 'Xe (ID hoặc mẫu chứa)',
      scooterPlaceholder: 'vd. Ninebot hoặc UUID',
      dailyRateRange: 'Giá theo ngày (tối thiểu/tối đa)',
      min: 'Tối thiểu',
      max: 'Tối đa',
      minExceedsMax: 'Tối thiểu không thể vượt quá Tối đa',
      endAfterStart: 'Thời gian kết thúc phải sau thời gian bắt đầu',
      clientRequired: 'Bắt buộc chọn khách hàng',
      scooterRequired: 'Bắt buộc chọn xe',
      startTimeRequired: 'Bắt buộc nhập thời gian bắt đầu',
      dailyRatePositive: 'Giá theo ngày phải > 0',
      addContract: 'Hợp đồng mới',
      client: 'Khách hàng',
      selectClient: 'Chọn khách hàng',
      searchScooters: 'Tìm xe...',
      startTime: 'Thời gian bắt đầu',
      status: 'Trạng thái',
      dailyRate: 'Giá theo ngày',
      deposit: 'Tiền đặt cọc',
      endTime: 'Ngày kết thúc',
      contractId: 'Mã hợp đồng',
      paymentMethod: 'Phương thức thanh toán',
      cash: 'Tiền mặt',
      local_bank_transfer: 'Chuyển khoản ngân hàng nội địa',
      international_bank_transfer: 'Chuyển khoản ngân hàng quốc tế',
      notes: 'Ghi chú',
      notesPlaceholder: 'Ghi chú tùy chọn về hợp đồng này',
      createDraftForScooter: 'Tạo bản nháp hợp đồng mới cho xe này.',
      saveAsDraftHint: 'Lưu làm bản nháp để kích hoạt sau',
      startImmediatelyHint: 'Bắt đầu hợp đồng ngay lập tức',
      creating: 'Đang tạo...',
      createDraft: 'Tạo bản nháp',
      createActive: 'Tạo hoạt động',
      day: 'ngày',
      forDays: 'trong {{count}} ngày',
      forDays_plural: 'trong {{count}} ngày',
      total: 'Tổng:',
      dayOf: 'ngày {{day}} / {{total}}'
    },
    contractsUi: {
      renting: 'đang thuê',
      statusHistory: 'Lịch sử trạng thái',
      noHistory: 'Không có lịch sử',
      created: 'Đã tạo',
      overdue: 'Quá hạn',
      overdueTitle: 'Hợp đồng đã quá hạn',
      remainingTitle: 'Thời gian còn lại đến khi kết thúc hợp đồng',
      remaining: 'còn lại',
      unitDayShort: 'n',
      unitHourShort: 'g',
      unitMinuteShort: 'p',
      clientDetails: 'Chi tiết khách hàng',
      scooterDetails: 'Chi tiết xe',
      clientNotFound: 'Không tìm thấy khách hàng',
      scooterNotFound: 'Không tìm thấy xe',
      fullName: 'Họ và tên',
      localPhone: 'Điện thoại địa phương',
      messenger: 'WhatsApp',
      brand: 'Hãng',
      vehicleType: 'Loại xe',
      battery: 'Pin',
      qrCode: 'Mã QR',
      scooterStatus: 'Trạng thái xe',
      noStatusChanges: 'Chưa có thay đổi trạng thái',
      loadingHistory: 'Đang tải lịch sử...',
      characters: 'ký tự',
      back: 'Quay lại',
      startDate: 'Ngày bắt đầu',
      endDate: 'Ngày kết thúc',
      contractId: 'Mã hợp đồng',
      editContract: 'Sửa hợp đồng',
      duplicate: 'Nhân bản',
      duplicateContract: 'Nhân bản hợp đồng',
      duplicateSubtitle: 'Chọn ngày bắt đầu, thời lượng và kết thúc cho bản nháp mới',
      endTimeMustBeAfter: 'Thời gian kết thúc phải sau thời gian bắt đầu',
      changeStatus: 'Đổi trạng thái',
      currentStatus: 'hiện tại',
      changeScooterStatus: 'Đổi trạng thái xe',
      currentScooterStatus: 'hiện tại',
      showingRange: 'Hiển thị {{start}}–{{end}} trong {{total}}',
      contractCreatedTitle: 'Đã tạo hợp đồng',
      contractCreatedMessage: 'Bạn có muốn mở hợp đồng vừa tạo không?',
      contractLabel: 'Hợp đồng',
      openContract: 'Mở hợp đồng',
      stayHere: 'Ở lại đây',
      scooterStatusQuestion: 'Trạng thái của xe "{{model}}" nên là gì?',
      currentStatusLabel: 'Trạng thái hiện tại:',
      statusChangeHelp: 'Điều này sẽ giúp bạn quản lý tình trạng sẵn sàng của xe sau khi thay đổi trạng thái hợp đồng.',
      working: 'Đang xử lý…',
      moveContractAndUpdate: 'Chuyển hợp đồng & Cập nhật xe',
      enterToConfirm: 'Enter để xác nhận · Esc để hủy',
      statusAvailable: 'Sẵn sàng',
      statusAvailableDesc: 'Xe sẵn sàng cho thuê',
      statusRented: 'Đang thuê',
      statusRentedDesc: 'Xe hiện đang được thuê',
      statusMaintenance: 'Bảo trì',
      statusMaintenanceDesc: 'Xe cần bảo trì',
      statusRetired: 'Ngừng sử dụng',
      statusRetiredDesc: 'Xe không còn hoạt động',
      statusCurrent: 'Hiện tại',
      moveContractTitle: 'Chuyển hợp đồng',
      moveContractMessage: 'Vui lòng chọn trạng thái mới cho xe khi chuyển hợp đồng này.',
      fromLabel: 'Từ',
      toLabel: 'Đến',
      clientLabel: 'Khách hàng',
      scooterLabel: 'Xe',
      currentContract: 'Hợp đồng hiện tại:',
      viewContract: 'Xem hợp đồng'
    },
    contractDocument: {
      print: 'In',
      saveAsPDF: 'Lưu dưới dạng PDF',
      email: 'Email',
      addSignature: 'Thêm chữ ký',
      emailRequired: 'Bắt buộc nhập địa chỉ email',
      emailSent: 'Gửi hợp đồng thành công',
      enterSignatureName: 'Nhập tên người ký để thêm vào tài liệu',
      loading: 'Đang tải hợp đồng...',
      preparingPDF: 'Đang chuẩn bị PDF...',
      downloadSuccess: 'Tải PDF thành công',
      downloadFailed: 'Không thể tạo PDF',
      retry: 'Thử lại',
      emailContract: 'Gửi hợp đồng qua email',
      recipientEmail: 'Email người nhận',
      sending: 'Đang gửi...',
      send: 'Gửi'
    },
    businessDetails: {
      title: 'Thông tin doanh nghiệp',
      settingsUpdated: 'Cập nhật thông tin doanh nghiệp thành công',
      errorLoading: 'Lỗi khi tải thông tin doanh nghiệp.',
      helpText: 'Điền thông tin doanh nghiệp của bạn bên dưới. Các chi tiết này sẽ xuất hiện trên hợp đồng thuê. Để trống nếu bạn muốn điền bằng tay trên hợp đồng in.',
      businessName: 'Tên doanh nghiệp',
      businessNamePlaceholder: 'Nhập tên doanh nghiệp',
      businessAddress: 'Địa chỉ doanh nghiệp',
      businessAddressPlaceholder: 'Nhập địa chỉ doanh nghiệp',
      businessPhone: 'Điện thoại doanh nghiệp',
      businessPhonePlaceholder: '+66 2 123 4567',
      businessEmail: 'Email doanh nghiệp',
      businessEmailPlaceholder: 'vd. business@example.com',
      taxId: 'Mã số thuế / Đăng ký kinh doanh',
      taxIdPlaceholder: '1234567890123',
      representativeName: 'Tên người đại diện được ủy quyền',
      representativeNamePlaceholder: 'John Smith',
      representativeIdNumber: 'Số CMND người đại diện',
      representativeIdNumberPlaceholder: '1234567890123',
      saving: 'Đang lưu...',
      saveDetails: 'Lưu thông tin doanh nghiệp',
      saved: 'Đã lưu'
    },
    messaging: {
      title: 'Liên hệ khách hàng',
      scanQR: 'Quét mã QR',
      orClickLink: 'Hoặc nhấp vào liên kết bên dưới',
      openApp: 'Mở ứng dụng nhắn tin',
      selectTemplate: 'Chọn mẫu tin nhắn',
      customMessage: 'Tin nhắn tùy chỉnh',
      messageEditor: 'Trình soạn tin nhắn',
      thankYou: 'Cảm ơn',
      reminder: 'Nhắc nhở',
      payment: 'Nhắc thanh toán',
      templateThankYou: 'Cảm ơn bạn đã chọn {{businessName}}! Chúng tôi hy vọng bạn đã có trải nghiệm thuê xe scooter tuyệt vời. Mong được phục vụ bạn lần nữa!',
      templateReminder: 'Xin chào {{clientName}}! Đây là {{businessName}}. Nhắc nhở: Thời gian thuê của bạn kết thúc vào ngày mai. Vui lòng trả xe.',
      templatePayment: 'Xin chào {{clientName}}! Đây là {{businessName}}. Nhắc thanh toán cho hợp đồng #{{contractId}}.',
      noWhatsApp: 'Không có số WhatsApp',
      noLINE: 'Không có LINE ID',
      close: 'Đóng'
    },
    finance: {
      title: 'Tổng quan tài chính',
      exportCSV: 'Xuất CSV',
      timeRange: 'Khoảng thời gian',
      day: 'Ngày',
      week: 'Tuần',
      month: 'Tháng',
      year: 'Năm',
      all: 'Tất cả',
      searchPlaceholder: 'Tìm xe...',
      showSoldOnly: 'Chỉ hiển thị đã bán',
      totalPurchase: 'Tổng mua',
      totalMaintenance: 'Tổng bảo trì',
      totalIncome: 'Tổng thu nhập',
      netProfit: 'Lợi nhuận ròng',
      topROIScooter: 'Xe có ROI cao nhất',
      bestBrandModel: 'Hãng/Mẫu tốt nhất',
      scooters: 'xe',
      scooter: 'Xe',
      purchase: 'Mua',
      maintenance: 'Bảo trì',
      income: 'Thu nhập',
      sale: 'Bán',
      net: 'Ròng',
      roi: 'ROI',
      status: 'Trạng thái',
      profitable: 'Có lãi',
      breakingEven: 'Hòa vốn',
      lossMaker: 'Thua lỗ',
      totals: 'Tổng cộng',
      recentExpenses: 'Chi phí gần đây',
      noExpenses: 'Chưa ghi nhận chi phí',
      loadingExpenses: 'Đang tải chi phí...',
      noExpensesInRange: 'Không có chi phí trong khoảng thời gian đã chọn',
      expenseTotal: 'Tổng',
      categoryOilChange: 'Thay dầu',
      categoryTires: 'Lốp xe',
      categoryBrakes: 'Phanh',
      categoryEngine: 'Động cơ',
      categoryBattery: 'Pin',
      categoryBodyParts: 'Phụ tùng thân xe',
      categoryCleaning: 'Vệ sinh',
      categoryRegistration: 'Đăng ký',
      categoryInsurance: 'Bảo hiểm',
      categoryMiscParts: 'Phụ tùng khác',
      categoryCustom: 'Tùy chỉnh',
      affectsExpenseDetails: 'ảnh hưởng đến chi tiết chi phí',
      tableView: 'Bảng',
      chartsView: 'Biểu đồ',
      toggleKPIs: 'Bật/tắt KPI',
      toggleInsights: 'Bật/tắt phân tích',
      toggleAdditionalTables: 'Bật/tắt bảng bổ sung',
      showOperational: 'Hiển thị chế độ vận hành',
      showFull: 'Hiển thị chế độ đầy đủ',
      operationalView: 'Chế độ vận hành',
      fullView: 'Chế độ đầy đủ',
      operationalNet: 'Ròng vận hành',
      date: 'Ngày',
      category: 'Danh mục',
      amount: 'Số tiền',
      note: 'Ghi chú',
      actions: 'Hành động',
      edit: 'Sửa',
      delete: 'Xóa',
      editExpense: 'Sửa chi phí',
      customLabel: 'Nhãn tùy chỉnh',
      confirmDeleteExpense: 'Bạn có chắc muốn xóa chi phí này không?',
      expenseDeleted: 'Đã xóa chi phí',
      deleteFailed: 'Không thể xóa chi phí',
      expenseUpdated: 'Đã cập nhật chi phí',
      updateFailed: 'Không thể cập nhật chi phí',
      expenseCreated: 'Đã tạo chi phí',
      addExpense: 'Thêm chi phí',
      brandModelPerformance: 'Hiệu suất Hãng/Mẫu',
      brand: 'Hãng',
      model: 'Mẫu',
      count: 'Số lượng',
      avgROI: 'ROI trung bình',
      topExpenses: 'Chi phí hàng đầu theo danh mục',
      noData: 'Không có dữ liệu',
      monthlyTrends: 'Xu hướng tài chính hàng tháng',
      expenseBreakdown: 'Phân tích danh mục chi phí',
      maintenanceEfficiency: 'Hiệu quả bảo trì',
      topPerformers: 'Hiệu suất hàng đầu',
      needsAttention: 'Cần chú ý',
      fleetHealth: 'Tổng quan tình trạng đội xe',
      totalScooters: 'Tổng số xe',
      revenueVsMaintenance: 'Doanh thu so với bảo trì theo xe',
      profitTrend: 'Xu hướng lợi nhuận theo xe',
      statusDistribution: 'Phân bố trạng thái',
      exportBasic: 'Xuất tóm tắt cơ bản',
      exportDetailed: 'Xuất chi phí chi tiết',
      exportingDetailed: 'Đang xuất chi phí chi tiết...',
      exportCompleted: 'Xuất hoàn tất',
      exportFailed: 'Không thể xuất'
    }
  },
  ja: {
    howItWorks: {
      title: 'OurScooterの使い方（スタッフ向け）',
      subtitle: '正確かつ効率的に業務を行うための、簡単な手順、明確なルール、役立つヒント。',
      categories: 'カテゴリー',
      whyTitle: 'なぜOurScooter：目的とメリット',
      whyDescription: 'OurScooterは、スタッフが日々のスクーターレンタル業務を効率的に運営するのに役立ちます：クリック数が少なく、ミスが少なく、記録が明確です。',
      whySpeed: 'スピード：数秒で契約を作成し、複製で詳細を再利用できます。',
      whyClarity: '明確さ：ステータスで各レンタルの状況が一目でわかります。',
      whyControl: 'コントロール：作業に合わせてフィルター、検索、ビュー切替（テーブル/カンバン）ができます。',
      whyAccuracy: '正確さ：ガードレールが無効な操作や重複したレンタルを防ぎます。',
      signingInTitle: 'サインインとサインイン状態の維持',
      signingInToken: 'ユーザー名＋パスワード → サーバーが検証し、アクセス＋リフレッシュトークンを返します。',
      signingInAutoRefresh: 'アクセストークンは短命です。リフレッシュトークンを使って自動的に静かに更新します。',
      signingInRoles: 'ロール：管理者とスタッフ。組織コンテキストはサイドバーで選択します。',
      signingInSession: 'セッション期間：非アクティブでも最大1日間サインイン状態を維持します。アプリは短い15分のアクセストークンを使用し、バックグラウンドで更新します。アプリを1日使用しない（またはクッキーが消去された）場合、再度サインインを求められます。',
      scootersTitle: 'スクーター：追加と編集',
      scootersOpen: '「Add Scooter」を開いて新しい車両を登録します。詳細は簡潔かつ正確に。',
      scootersRequired: '必須：ブランド/モデル、ステータス。',
      scootersOptional: '任意：購入費用、燃料/バッテリーの詳細、メモ。',
      scootersTip: 'ヒント：「maintenance」ステータスは、返却されるまでスクーターを利用可能リストから外します。',
      clientsTitle: '顧客：追加と編集',
      clientsOpen: '「New Client」を開きます。後で見つけやすい情報を記録しましょう。',
      clientsRequired: '必須：氏名。',
      clientsOptional: '任意：電話、メール、ID/パスポート、メモ。',
      contractsTitle: '契約：作成と管理',
      contractsWaysTitle: '契約を作成する方法（4つの選択肢）',
      contractsFromContracts: 'Contractsから：「Add Contract」を押します。',
      contractsFromScooters: 'Scootersから：スクーターを開く → 「New Contract」。',
      contractsFromClients: 'Clientsから：顧客を開く → 「New Contract」。',
      contractsDuplicate: '複製：既存の契約を開く → 「Duplicate」で詳細を安全に再利用します。',
      contractsRequiredTitle: '必須フィールド',
      contractsRequiredClient: '顧客とスクーター。',
      contractsRequiredDate: '開始日時。',
      contractsRequiredRate: '日額料金。（デポジット/支払方法は任意。）',
      contractsStatusTitle: 'ステータスとワークフロー（許可される操作）',
      contractsStatusFlow: 'Draft → Active → Closed → Archived。',
      contractsStatusActivate: '必須フィールドが設定され、スクーターが利用可能な場合のみアクティブ化できます。',
      contractsStatusClosed: 'ClosedからはArchivedにのみ移動できます（Activeには戻せません）。',
      contractsStatusForbidden: '禁止：同じスクーターに対する重複するActive契約。',
      contractsStatusArchived: 'Archived契約はデフォルトのビューから消えます。「Include archived」を切り替えて表示します。',
      contractsAutoTitle: 'スクーターステータスの自動更新',
      contractsAutoCreate: '契約の作成はスクーターのステータスを変更しません（契約ステータスに関係なく）。',
      contractsAutoActive: '契約がActiveになると → スクーターは自動的に「rented」になります。',
      contractsAutoClosed: 'Active契約がClose/Archiveされると → スクーターは自動的に「available」になります。',
      contractsAutoDraft: 'Active契約がDraftに戻されると → スクーターは自動的に「available」になります。',
      contractsAutoPrevent: 'これにより二重予約を防ぎ、スクーターの利用可能状況を正確に保ちます。',
      contractsTimeTitle: 'Active契約の時間追跡',
      contractsTimeRemaining: 'Active契約は終了日までの残り時間を表示します（例：「2h 30m left」）。',
      contractsTimeOverdue: '期限切れの契約は経過時間を表示します（例：「Overdue 1d 5h」）。',
      contractsTimeColors: '色は緊急度を示します：緑（4時間以上）、黄（1〜4時間）、オレンジ（1時間未満）、赤（期限切れ）。',
      contractsTimeKanban: 'カンバンビューでは：Active契約にはタイマーのみ表示されます。',
      contractsTimeList: 'リストビューでは：Active契約にステータスバッジとタイマーの両方が表示されます。',
      contractsViewsTitle: '契約：ビュー',
      contractsViewsTable: 'テーブルビュー：行を素早くスキャン、フィルター、エクスポートします。',
      contractsViewsKanban: 'カンバンボード：ステータス別の視覚的ワークフロー（Draft、Active、Closed、Archived）。',
      contractsViewsToggle: 'Contractsツールバーのビュー切替を使ってビューを切り替えます。',
      contractsKanbanTitle: 'カンバンボードでの作業',
      contractsKanbanDrag: 'カードをドラッグ＆ドロップして、次に許可されたステータスに移動します。',
      contractsKanbanEdit: 'カードを開いてEditを押すと、詳細やステータスを直接変更できます。',
      contractsKanbanRules: 'ルールが適用されます：ClosedからActiveには戻せません。Archivedは最終です。',
      contractsKanbanBlocked: '利用できない移動はブロックされます（例：スクーターが別の場所で既にActive）。',
      contractsKanbanFilters: 'フィルターを使って1人の顧客に絞り込んだり、必要に応じてArchived項目を含めたりします。',
      findingTitle: 'すばやい検索（フィルターと検索）',
      findingScooters: 'スクーター：ステータス、ブランド、モデルでフィルター。検索ボックスで素早くテキスト一致。',
      findingClients: '顧客/ユーザー：名前/メールで検索。ページサイズを調整してより多くの行を表示。',
      findingContracts: '契約：ステータスでフィルター。必要に応じてアーカイブ済みを含めます。',
      rowsTitle: '表示する行数は？',
      rowsScooters: 'スクーター：ページ単位で読み込み、必要に応じてさらに取得（1回の読み込みで最大100）。',
      rowsClients: '顧客/ユーザー：1ページあたり10/20/30/40/50行を選べます。',
      rowsContracts: '契約：現在の組織の契約を表示。アーカイブ済みを含めてリストを拡張。',
      themesTitle: 'テーマとデフォルト',
      themesChange: '左下のサイドバーでテーマ（Dark/Light/Blue/Wood）を変更します。',
      themesAdmin: '管理者はManagementで組織のデフォルト（例：通貨、料金）を設定できます。',
      usersTitle: 'ユーザーと管理（管理者）',
      usersStaff: 'スタッフ：日々の業務、契約の閲覧と作成、スクーター/顧客の管理。',
      usersAdmin: '管理者：スタッフができるすべて、加えて組織レベルの設定と削除。',
      tipsTitle: 'ヒントとトラブルシューティング',
      tipsActivate: 'アクティブ化できない？スクーターのステータス（利用可能であること）と必須フィールドを確認してください。',
      tipsContract: '契約が見えない？アーカイブされている可能性があります。「Include archived」を有効にしてください。',
      tipsSession: 'セッションが終了した？1日以上経過している場合は、もう一度サインインするだけです。'
    },
    booking: {
      orgNotFound: '店舗が見つかりません',
      orgNotFoundDesc: 'お探しの予約ページは存在しません。考えられる原因:',
      orgNotFoundReason1: '店舗のスラッグが正しくありません',
      orgNotFoundReason2: '店舗がまだ予約ページを設定していません',
      orgNotFoundReason3: '予約ページが削除されました',
      orgNotFoundOwner: '店舗オーナーの方は、Management → Technical Details で店舗のスラッグを設定したかご確認ください。',
      requestReceived: 'リクエストを受け付けました！',
      requestReceivedDesc: '予約リクエストを受け付けました。これは確定した予約ではありません。',
      willContact: '{{business}} がまもなくご連絡し、レンタルを確認します。',
      requestIdLabel: 'リクエストID:',
      contactLabel: '連絡先:',
      showroomBadge: 'スクーターショールーム',
      bookBadge: 'スクーターを予約',
      showroomIntro: 'スクーターをご覧になり、お好きな一台を選んで予約リクエストを送信してください。まもなく確認のご連絡をいたします。',
      bookIntro: '以下から予約リクエストを送信してください。まもなく確認のご連絡をいたします。',
      yourDetailsStep: '3. お客様情報',
      yourInformation: 'お客様情報',
      fullName: '氏名',
      fullNamePlaceholder: '氏名を入力',
      email: 'メール',
      emailPlaceholder: 'メールアドレスを入力',
      localPhone: '現地の電話番号',
      phonePlaceholder: '電話番号',
      messengerNumber: 'メッセンジャー番号（WhatsApp など）',
      messengerPlaceholder: 'WhatsApp番号（任意）',
      sameAsPhone: '電話番号と同じ',
      lineId: 'LINE ID',
      lineIdPlaceholder: 'LINE ID（任意）',
      passportNumber: 'パスポート番号',
      passportPlaceholder: 'パスポート番号（任意）',
      nationality: '国籍',
      nationalityPlaceholder: '国籍（任意）',
      localAddress: '現地の住所',
      localAddressPlaceholder: '現地の住所（任意）',
      rentalDatesStep: '1. レンタル日',
      bookingDetails: '予約の詳細',
      startDate: '開始日',
      duration: '期間',
      endDate: '終了日',
      dayUnit: '日',
      daysUnit: '日',
      weekUnit: '週間',
      weeksUnit: '週間',
      monthUnit: 'か月',
      monthsUnit: 'か月',
      scooterUnit: '台',
      scootersUnit: '台',
      pricingSummary: '料金概要',
      lineDailyRate: '{{count}}{{unit}} × 日額:',
      lineWeeklyRate: '{{count}}{{unit}} × 週額:',
      lineDailyFromWeekly: '{{count}}{{unit}} × 日額（週額より）:',
      lineMonthlyRate: '{{count}}{{unit}} × 月額:',
      lineDailyFromMonthly: '{{count}}{{unit}} × 日額（月額より）:',
      subtotal: '小計:',
      deposit: '保証金:',
      total: '合計:',
      selectToSeePricingShowroom: '日付とスクーターを選択すると料金が表示されます',
      selectToSeePricingGroup: '日付と料金グループを選択すると料金が表示されます',
      selectPricingGroup: '料金グループを選択',
      perDay: '/日',
      perWeek: '/週',
      perMonth: '/月',
      specialPromotions: '特別プロモーション:',
      scooterPreference: '希望のスクーター',
      noPreference: '指定なし',
      scooterFallback: 'スクーター',
      chooseScooterStep: '2. スクーターを選択',
      checkingAvailability: 'ご指定の日付の空き状況を確認中…',
      availableForDates: 'ご指定の日付で {{total}} 台中 {{available}} 台が利用可能',
      fleetCount: 'フリートに{{count}}{{unit}}',
      lockedScooterNote: 'このスクーターは予約リンクを通じてあなたのために確保されています',
      noScootersAvailable: '現在利用可能なスクーターはありません。直接お問い合わせください。',
      available: '利用可能',
      unavailable: '利用不可',
      photosCount: '{{count}}枚の写真',
      viewPhoto: '表示',
      viewPhotosAria: '{{title}} の写真を表示',
      additionalNotes: '追加メモ',
      notesPlaceholder: '特別なご要望や情報があればご記入ください...',
      charactersCount: '{{count}}/255文字',
      preSignTitle: '⚡ 契約に事前署名（推奨）',
      preSignDesc: '受け取り手続きをスピードアップ！事前に契約へ署名すると、到着時の時間を節約できます。',
      preSignBenefits: '支払い不要 • 義務ではありません • 受け取りが速くなるだけ',
      completeFieldsToSign: '契約の事前署名を有効にするには、上記の必須項目をすべて入力してください。',
      missingLabel: '未入力:',
      signatureSaved: '署名を保存しました: {{name}}',
      submitting: '送信中...',
      submitRequest: '予約リクエストを送信',
      disclaimer: 'このフォームを送信することで、これは確定予約ではなくリクエストであることをご了承いただきます。空き状況の確認と予約完了のため、店舗からご連絡いたします。',
      selectDatesForPrice: '日付を選択すると料金が表示されます',
      depositShort: '保証金',
      requestToBook: '予約をリクエスト',
      continueBtn: '続ける',
      closeBtn: '閉じる',
      prevPhoto: '前の写真',
      nextPhoto: '次の写真',
      langLabel: '言語',
      toastLoadFailed: '予約ページの読み込みに失敗しました',
      toastVersionFailed: '契約バージョンの生成に失敗しました。もう一度お試しください。',
      toastPreviewFailed: '契約プレビューの読み込みに失敗しました',
      toastEnterName: '氏名を入力してください',
      toastEnterEmail: 'メールアドレスを入力してください',
      toastEnterPhone: '電話番号を入力してください',
      toastSelectStart: '開始日を選択してください',
      toastSelectScooter: 'スクーターを選択してください',
      toastSelectPricingGroup: '料金グループを選択してください',
      toastPricingFailed: '料金を計算できません',
      toastSubmitFailed: '予約リクエストの送信に失敗しました。もう一度お試しください。',
      errFullNameRequired: '氏名は必須です',
      errEmailRequired: 'メールは必須です',
      errEmailInvalid: 'メールが無効です',
      errPhoneRequired: '電話番号は必須です',
      mfFullName: '氏名',
      mfEmail: 'メール',
      mfPhone: '電話番号',
      mfStartDate: '開始日',
      mfScooter: 'スクーター',
      mfPricingGroup: '料金グループ',
      mfPricingInfo: '料金情報'
    },
    theme: {
      dark: 'ダーク',
      light: 'ライト',
      blue: 'ブルー',
      wood: 'ウッド'
    },
    home: {
      quickActions: 'クイック操作',
      recentActivity: '最近のアクティビティ',
      fleetStatusOverview: '車両ステータス概要',
      welcomeTitle: 'OurScootersへようこそ',
      welcomeSubtitle: '総合的なスクーター車両管理ダッシュボード',
      addScooter: '新規スクーター',
      registerScooter: '新しいスクーターを登録',
      addClient: '新規顧客',
      registerClient: '新しい顧客を登録',
      newContract: '新規契約',
      createAgreement: 'レンタル契約を作成',
      org: '組織',
      allOrgs: 'すべての組織',
      availableScooters: '利用可能なスクーター',
      readyForRental: 'レンタル可能',
      rentedScooters: '貸出中のスクーター',
      currentlyInUse: '使用中',
      maintenance: 'メンテナンス',
      underRepair: '修理中',
      fleetOverview: '車両概要',
      available: '利用可能',
      rented: '貸出中',
      activeRevenue: 'アクティブ収益',
      monthlyRecurring: '月次経常収益',
      totalClients: '総顧客数',
      activeContracts: '有効な契約',
      avgBattery: '平均バッテリー',
      fleetBatteryHealth: '車両バッテリー状態',
      avgFuel: '平均燃料',
      fuel: '燃料',
      electric: '電動',
      fleetROI: '車両ROI',
      rev: '収益',
      cost: 'コスト',
      inMaintenance: 'メンテナンス中',
      newClientRegistered: '新規顧客が登録されました',
      registered: '登録済み'
    },
    users: {
      title: 'ユーザー',
      errorLoading: 'ユーザーの読み込みに失敗しました。',
      id: 'ID',
      email: 'メール',
      role: '役割',
      username: 'ユーザー名',
      org: '組織',
      roleSuperadmin: 'スーパー管理者',
      roleAdmin: '管理者',
      roleStaff: 'スタッフ'
    },
    management: {
      title: '組織管理',
      errorLoading: '設定の読み込みエラー。',
      timeFormat: '時刻形式',
      defaultCurrency: 'デフォルト通貨',
      defaultCountryCode: 'デフォルト国コード',
      defaultDailyPrice: 'デフォルト日額料金',
      defaultDeposit: 'デフォルト契約保証金',
      defaultRentLength: 'デフォルトレンタル期間（日）',
      defaultPaymentMethod: 'デフォルト支払方法',
      buttonStyle: 'ボタンスタイル',
      buttonStyleDesc: 'アプリ全体のすべてのボタンの表示スタイルを選択します。',
      countryCodeHint: '米国は1、英国は44など。',
      countryCodeExplain: '数字のみ。顧客フォームの電話番号の先頭に付加されます。',
      saving: '保存中...',
      saveSettings: '設定を保存',
      saved: '保存しました',
      modernStyle: 'モダンスタイル',
      classicStyle: 'クラシックスタイル',
      originalStyle: 'オリジナルスタイル',
      btnPrimary: 'プライマリ',
      btnSecondary: 'セカンダリ',
      btnGhost: 'ゴースト',
      btnDanger: '危険',
      modernDesc: 'グラデーション背景、マイクロインタラクション、角丸',
      classicDesc: 'テーマ対応の色と繊細な枠線を備えたクリーンでミニマルなデザイン',
      originalDesc: 'リファクタリング前に気に入っていた元のボタンデザイン',
      timeFormatExample1: '2025年9月15日 午後6:09',
      timeFormatExample2: '9月15日 2025年 午後6:09',
      timeFormatExample3: '2025年9月15日 18:09',
      timeFormatExample4: '9月15日 2025年 18:09'
    },
    common: {
      language: '言語',
      phoneTooShort: '電話番号は7桁以上必要です',
      phoneTooLong: '電話番号が長すぎます（最大15桁）',
      theme: 'テーマ',
      save: '保存',
      cancel: 'キャンセル',
      logout: 'ログアウト',
      loading: '読み込み中...',
      reset: 'リセット',
      close: '閉じる',
      back: '戻る',
      delete: '削除',
      clearAll: 'すべてクリア',
      upload: 'アップロード',
      uploading: 'アップロード中...',
      selectPhoto: '写真を選択',
      addNotes: 'メモを追加...',
      notes: 'メモ',
      optional: '任意',
      retake: '撮り直す',
      usePhoto: 'この写真を使用',
      viewAll: 'すべての写真を表示',
      confirmDelete: 'この写真を削除してもよろしいですか？',
      add: '追加',
      copied: 'コピーしました',
      expand: '展開',
      collapse: '折りたたむ',
      updateFailed: '更新に失敗しました',
      updateFailedWithMessage: '更新に失敗しました: {{message}}',
      failedToSendEmail: 'メールの送信に失敗しました',
      statusChangeNotAllowed: 'このステータス変更は許可されていません',
      expenseAddedSuccessfully: '経費が正常に追加されました',
      edit: '編集'
    },
    toasts: {
      contractCreated: '契約が正常に作成されました',
      contractUpdated: '契約が正常に更新されました',
      contractDeleted: '契約が正常に削除されました',
      contractCreateFailed: '契約の作成に失敗しました',
      contractUpdateFailed: '契約の更新に失敗しました',
      contractDeleteFailed: '契約の削除に失敗しました',
      scooterCreated: 'スクーターが正常に作成されました',
      scooterUpdated: 'スクーターが正常に更新されました',
      scooterDeleted: 'スクーターが正常に削除されました',
      scooterStatusUpdated: 'スクーターのステータスが正常に更新されました',
      scooterStatusUpdateFailed: 'スクーターのステータス更新に失敗しました',
      clientCreated: '顧客が正常に作成されました',
      clientUpdated: '顧客が正常に更新されました',
      clientDeleted: '顧客が正常に削除されました',
      settingsUpdated: '設定が正常に更新されました！',
      scooterStatusChangedAuto: 'スクーターのステータスが自動的に変更されました',
      scooterStatusChangeDesc: '{{scooterName}} は {{fromStatus}} から {{toStatus}} に{{reason}}移動されました。',
      becauseContractActivated: '契約が有効化されたため',
      becauseContractDeactivated: '契約が終了/アーカイブされたため',
      automatically: '自動的に',
      viewScooter: 'スクーターを表示',
      scooterOpenedFromNotif: '{{scooterName}} の詳細を開きました',
      scooterOpenedFromNotifDesc: 'このスクーターはステータス変更通知から自動的に開かれました。',
      closedContractsMoveRestriction: '終了した契約はアーカイブにのみ移動できます。'
    },
    login: {
      title: 'ログイン',
      email: 'メール',
      username: 'ユーザー名',
      password: 'パスワード',
      signIn: 'サインイン',
      signingIn: 'サインイン中...',
      login: 'ログイン',
      loggingIn: 'ログイン中...',
      loginFailed: 'ログインに失敗しました',
      language: '言語'
    },
    sidebar: {
      scooters: 'スクーター',
      clients: '顧客',
      contracts: '契約',
      finance: '財務',
      users: 'ユーザー',
      activity: 'アクティビティログ',
      management: '管理',
      howItWorks: '使い方',
      signIn: 'サインイン',
      expand: 'サイドバーを展開',
      collapse: 'サイドバーを折りたたむ',
      logoutConfirm: 'ログアウトしてもよろしいですか？'
    },
    toolbar: {
      searchPlaceholder: 'ID、モデル、ステータスで検索…',
      clear: 'クリア',
      listView: 'リスト表示',
      kanbanView: 'カンバン表示',
      calendarView: 'カレンダー',
      gridView: 'グリッド表示',
      mapView: 'マップ表示',
      filter: 'フィルター',
      export: 'エクスポート',
      total: '合計: {{count}}',
      queryLabel: 'クエリ',
      clearQuery: 'クエリをクリア',
      removeFilter: '{{filter}} を削除'
    },
    ui: {
      zoom: 'ズーム'
    },
    calendar: {
      week: '週',
      month: '月',
      all: 'すべて',
      plusMore: '他 {{count}} 件',
      scrollHint: 'Shift+スクロールで横移動',
      hideEmpty: '今後の契約がないスクーターを非表示'
    },
    status: {
      available: '利用可能',
      rented: '貸出中',
      maintenance: 'メンテナンス',
      retired: '廃車',
      draft: '下書き',
      active: '有効',
      closed: '終了',
      archived: 'アーカイブ済み'
    },
    clients: {
      id: '顧客ID',
      name: '名前',
      messenger: 'WhatsApp',
      newClient: '新規顧客',
      creating: '顧客を作成中...',
      fullName: '氏名',
      fullNamePlaceholder: '氏名を入力',
      email: 'メール',
      emailPlaceholder: 'メールアドレスを入力',
      localPhone: '現地電話番号',
      localPhonePlaceholder: '現地電話番号を入力（任意）',
      messengerNumber: 'WhatsApp番号',
      countryCodePlaceholder: '1',
      messengerNumberPlaceholder: 'WhatsApp番号を入力（任意）',
      lineId: 'LINE ID',
      lineIdPlaceholder: 'LINE IDを入力（任意）',
      whatsappButton: 'WhatsApp',
      lineButton: 'LINE',
      contactViaWhatsApp: 'WhatsAppで連絡',
      contactViaLINE: 'LINEで連絡',
      notes: 'メモ',
      notesPlaceholder: 'この顧客に関する追加メモ（例：別の国の電話番号）',
      addClient: '新規顧客',
      detailsTitle: '顧客詳細',
      created: '作成日',
      clientId: '顧客ID',
      edit: '顧客を編集',
      previousContracts: '過去の契約:',
      noCurrentContracts: '現在の契約なし',
      editClient: '顧客を編集',
      newContractBtn: '新規契約',
      close: '閉じる',
      statusHistory: 'ステータス履歴',
      unknownClient: '不明な顧客'
    },
    scooters: {
      id: 'スクーターID',
      modelRequired: 'モデルは必須です',
      batteryRange: 'バッテリーは0〜100である必要があります',
      odometerNegative: '走行距離は負の値にできません',
      fuelRange: '燃料%は0〜100である必要があります',
      incompatibleEnergy: '電動車に燃料は設定できません。燃料車にバッテリーは設定できません',
      editScooter: 'スクーターを編集',
      newScooter: '新規スクーター',
      updating: 'スクーターを更新中...',
      creating: 'スクーターを作成中...',
      model: 'モデル',
      modelPlaceholder: '例: Ninebot Max',
      status: 'ステータス',
      typeLevel: 'タイプ＆レベル',
      odometer: '走行距離計',
      qrCode: 'QRコード',
      qrPlaceholder: '任意のQRコード',
      plateNumber: 'ナンバープレート',
      platePlaceholder: '任意のナンバープレート',
      nickname: 'ニックネーム',
      nicknamePlaceholder: '例: 赤いHonda #1',
      nicknameOptional: 'ニックネーム（任意）',
      brand: 'ブランド',
      brandPlaceholder: '任意のブランド',
      vehicleType: '車両タイプ',
      electric: '電動',
      fuel: '燃料',
      batteryPct: 'バッテリー%',
      fuelPct: '燃料%',
      odometerKm: '走行距離（km）',
      updateScooter: 'スクーターを更新',
      addScooter: '新規スクーター',
      details: '詳細',
      edit: '編集',
      created: '作成日',
      statusHistory: 'スクーターステータス履歴',
      columnScooterId: 'スクーターID',
      columnBrand: 'ブランド',
      columnModel: 'モデル',
      columnPlateNumber: 'ナンバープレート',
      columnNickname: 'ニックネーム',
      columnStatus: 'ステータス',
      columnVehicleStatus: 'タイプ＆レベル',
      columnOdometer: '走行距離計',
      pictures: '写真',
      noPhotos: 'まだ写真がありません',
      noPhotosYet: 'まだ写真がありません',
      allPhotos: 'すべての写真',
      viewAllPhotos: 'すべての写真を表示',
      damage: '損傷',
      damageOnly: '損傷',
      showingDamage: '損傷を表示中',
      noDamagePhotos: '損傷のある写真はありません',
      markAsDamage: '損傷としてマーク',
      markDamage: '損傷をマーク',
      removeDamage: '損傷を解除',
      markNoDamage: '損傷なしとマーク',
      damageDetected: '損傷を検出',
      scooterPhoto: 'スクーターの写真',
      selectImageFile: '画像ファイルを選択してください',
      fileSizeLimit: 'ファイルサイズは10MB未満である必要があります',
      photoUploaded: '写真が正常にアップロードされました',
      photoDeleted: '写真を削除しました',
      photoDeleteFailed: '写真の削除に失敗しました',
      photoUpdateFailed: '写真の更新に失敗しました',
      photoUploadFailed: '写真のアップロードに失敗しました',
      photoStorageNotConfigured: '写真ストレージが設定されていません。管理者にお問い合わせください。',
      deletePhoto: '写真を削除',
      drawCircle: '円を描く',
      stopDrawing: '描画を停止',
      undo: '元に戻す',
      redo: 'やり直す',
      capture: 'キャプチャ',
      takePhoto: '写真を撮る',
      chooseFromGallery: 'ギャラリーから選択',
      addPhoto: '写真を追加',
      photoPreview: '写真プレビュー',
      cameraError: 'カメラへのアクセスに失敗しました',
      cameraPermissionDenied: 'カメラの許可が拒否されました'
    },
    pages: {
      manageColumns: '列を管理',
      columns: '列',
      retiredHidden: '廃車のスクーターは非表示です',
      failedLoadScooters: 'スクーターの読み込みに失敗しました',
      checkConnection: '接続を確認して再試行してください。',
      retry: '再試行',
      clearFilters: 'フィルターをクリア',
      addScooter: '新規スクーター',
      noScootersYet: 'まだスクーターがありません',
      noScootersMatch: 'フィルターに一致するスクーターがありません',
      addFirstScooter: '最初のスクーターを追加して始めましょう。',
      tryAdjusting: 'フィルターを調整またはクリアしてみてください。',
      rowsPerPage: '1ページあたりの行数:',
      pageOf: '{{total}} ページ中 {{current}} ページ',
      prevShort: '前へ',
      nextShort: '次へ',
      prev: '前のページ',
      next: '次のページ'
    },
    contractsPage: {
      newContract: '新規契約',
      showArchived: 'アーカイブされた契約を表示',
      newContractSubtitle: '新しいレンタル契約を素早く作成・確認',
      createdDate: '作成日',
      recentlyUpdated: '最近更新',
      recentlyClosed: '最近終了',
      allClosed: 'すべて終了',
      showAll: 'すべて表示',
      showRecent: '最近を表示',
      sortBy: '並べ替え...',
      archivedOnlyToDraft: 'アーカイブされた契約は下書きにのみ移動できます。'
    },
    activity: {
      title: 'アクティビティログ',
      subtitle: '従業員のアクティビティとシステムの変更を監視します。トラブルシューティングと監査のためのわかりやすいログを表示します。',
      filterActivity: 'アクティビティをフィルター',
      timePeriod: '期間',
      lastHour: '過去1時間',
      today: '今日',
      thisWeek: '今週',
      thisMonth: '今月',
      custom: 'カスタム',
      from: '開始',
      to: '終了',
      employee: '従業員',
      type: 'アクティビティタイプ',
      allEmployees: 'すべての従業員',
      allActivities: 'すべてのアクティビティ',
      scooterMgmt: 'スクーター管理',
      contractMgmt: '契約管理',
      clientMgmt: '顧客管理',
      orgSettings: '組織設定',
      searchEverything: 'すべてを検索',
      searchPlaceholder: '名前、ID、価格、ステータスを検索...'
    },
    contracts: {
      filters: 'フィルター',
      editContract: '契約を編集',
      dates: '日付',
      for: '対象',
      scooter: 'スクーター',
      start: '開始',
      end: '終了',
      duration: '期間',
      days: '日',
      weeks: '週',
      months: '月',
      estimatedTotal: '推定合計',
      fetchingData: '顧客とスクーターを取得中',
      scooterCurrentlyRented: 'ℹ️ このスクーターは現在貸出中です{{until}}。',
      until: 'まで',
      futureReservationNote: '将来の予約のために契約を作成することもできます。日付が重ならないように注意してください。',
      searchClients: '名前/メール/IDで顧客を検索',
      noMatches: '一致なし',
      selected: '選択済み:',
      scooterQuery: 'スクーター（IDまたはモデルを含む）',
      scooterPlaceholder: '例: Ninebot または UUID',
      dailyRateRange: '日額料金（最小/最大）',
      min: '最小',
      max: '最大',
      minExceedsMax: '最小は最大を超えることはできません',
      endAfterStart: '終了時刻は開始時刻より後である必要があります',
      clientRequired: '顧客は必須です',
      scooterRequired: 'スクーターは必須です',
      startTimeRequired: '開始時刻は必須です',
      dailyRatePositive: '日額料金は0より大きい必要があります',
      addContract: '新規契約',
      client: '顧客',
      selectClient: '顧客を選択',
      searchScooters: 'スクーターを検索...',
      startTime: '開始時刻',
      status: 'ステータス',
      dailyRate: '日額料金',
      deposit: '保証金',
      endTime: '終了日',
      contractId: '契約ID',
      paymentMethod: '支払方法',
      cash: '現金',
      local_bank_transfer: '国内銀行振込',
      international_bank_transfer: '国際銀行振込',
      notes: 'メモ',
      notesPlaceholder: 'この契約に関する任意のメモ',
      createDraftForScooter: 'このスクーターの新しい契約下書きを作成します。',
      saveAsDraftHint: '後で有効化するために下書きとして保存',
      startImmediatelyHint: '契約をすぐに開始',
      creating: '作成中...',
      createDraft: '下書きを作成',
      createActive: '有効な契約を作成',
      day: '日',
      forDays: '{{count}} 日間',
      forDays_plural: '{{count}} 日間',
      total: '合計:',
      dayOf: '{{total}} 日中 {{day}} 日目'
    },
    contractsUi: {
      renting: 'レンタル中',
      statusHistory: 'ステータス履歴',
      noHistory: '履歴がありません',
      created: '作成日',
      overdue: '期限超過',
      overdueTitle: '契約が期限超過しています',
      remainingTitle: '契約終了までの残り時間',
      remaining: '残り',
      unitDayShort: '日',
      unitHourShort: '時',
      unitMinuteShort: '分',
      clientDetails: '顧客詳細',
      scooterDetails: 'スクーター詳細',
      clientNotFound: '顧客が見つかりません',
      scooterNotFound: 'スクーターが見つかりません',
      fullName: '氏名',
      localPhone: '現地電話',
      messenger: 'WhatsApp',
      brand: 'ブランド',
      vehicleType: '車両タイプ',
      battery: 'バッテリー',
      qrCode: 'QRコード',
      scooterStatus: 'スクーターステータス',
      noStatusChanges: 'まだステータス変更がありません',
      loadingHistory: '履歴を読み込み中...',
      characters: '文字',
      back: '戻る',
      startDate: '開始日',
      endDate: '終了日',
      contractId: '契約ID',
      editContract: '契約を編集',
      duplicate: '複製',
      duplicateContract: '契約を複製',
      duplicateSubtitle: '新しい下書きの開始、期間、終了を選択',
      endTimeMustBeAfter: '終了時刻は開始時刻より後である必要があります',
      changeStatus: 'ステータスを変更',
      currentStatus: '現在',
      changeScooterStatus: 'スクーターのステータスを変更',
      currentScooterStatus: '現在',
      showingRange: '{{total}} 件中 {{start}}〜{{end}} 件を表示',
      contractCreatedTitle: '契約が作成されました',
      contractCreatedMessage: '作成した契約を開きますか？',
      contractLabel: '契約',
      openContract: '契約を開く',
      stayHere: 'ここに留まる',
      scooterStatusQuestion: 'スクーター「{{model}}」のステータスはどうしますか？',
      currentStatusLabel: '現在のステータス:',
      statusChangeHelp: 'これにより、契約ステータス変更後のスクーターの利用可否を管理できます。',
      working: '処理中…',
      moveContractAndUpdate: '契約を移動してスクーターを更新',
      enterToConfirm: 'Enterで確認 · Escでキャンセル',
      statusAvailable: '利用可能',
      statusAvailableDesc: 'スクーターはレンタル可能です',
      statusRented: '貸出中',
      statusRentedDesc: 'スクーターは現在貸出中です',
      statusMaintenance: 'メンテナンス',
      statusMaintenanceDesc: 'スクーターはメンテナンスが必要です',
      statusRetired: '廃車',
      statusRetiredDesc: 'スクーターは使用されていません',
      statusCurrent: '現在',
      moveContractTitle: '契約を移動',
      moveContractMessage: 'この契約を移動する際のスクーターの新しいステータスを選択してください。',
      fromLabel: '移動元',
      toLabel: '移動先',
      clientLabel: '顧客',
      scooterLabel: 'スクーター',
      currentContract: '現在の契約:',
      viewContract: '契約を表示'
    },
    contractDocument: {
      print: '印刷',
      saveAsPDF: 'PDFとして保存',
      email: 'メール',
      addSignature: '署名を追加',
      emailRequired: 'メールアドレスは必須です',
      emailSent: '契約が正常に送信されました',
      enterSignatureName: '文書に追加する署名者名を入力',
      loading: '契約を読み込み中...',
      preparingPDF: 'PDFを準備中...',
      downloadSuccess: 'PDFが正常にダウンロードされました',
      downloadFailed: 'PDFの生成に失敗しました',
      retry: '再試行',
      emailContract: '契約をメールで送信',
      recipientEmail: '受信者のメール',
      sending: '送信中...',
      send: '送信'
    },
    businessDetails: {
      title: '事業者情報',
      settingsUpdated: '事業者情報が正常に更新されました',
      errorLoading: '事業者情報の読み込みエラー。',
      helpText: '以下に事業者情報を入力してください。これらの詳細はレンタル契約に表示されます。印刷した契約に手書きで記入する場合は空欄のままにしてください。',
      businessName: '事業者名',
      businessNamePlaceholder: '事業者名を入力',
      businessAddress: '事業者住所',
      businessAddressPlaceholder: '事業者住所を入力',
      businessPhone: '事業者電話番号',
      businessPhonePlaceholder: '+66 2 123 4567',
      businessEmail: '事業者メール',
      businessEmailPlaceholder: '例: business@example.com',
      taxId: '納税者番号／事業者登録',
      taxIdPlaceholder: '1234567890123',
      representativeName: '権限を持つ代表者名',
      representativeNamePlaceholder: 'John Smith',
      representativeIdNumber: '代表者ID番号',
      representativeIdNumberPlaceholder: '1234567890123',
      saving: '保存中...',
      saveDetails: '事業者情報を保存',
      saved: '保存しました'
    },
    messaging: {
      title: '顧客に連絡',
      scanQR: 'QRコードをスキャン',
      orClickLink: 'または下のリンクをクリック',
      openApp: 'メッセージアプリを開く',
      selectTemplate: 'メッセージテンプレートを選択',
      customMessage: 'カスタムメッセージ',
      messageEditor: 'メッセージエディタ',
      thankYou: 'お礼',
      reminder: 'リマインダー',
      payment: '支払いリマインダー',
      templateThankYou: '{{businessName}} をお選びいただきありがとうございます！スクーターレンタルをお楽しみいただけたなら幸いです。またのご利用をお待ちしております！',
      templateReminder: 'こんにちは {{clientName}} さん！{{businessName}} です。リマインダー：レンタル期間は明日終了します。スクーターをご返却ください。',
      templatePayment: 'こんにちは {{clientName}} さん！{{businessName}} です。契約 #{{contractId}} のお支払いのリマインダーです。',
      noWhatsApp: 'WhatsApp番号がありません',
      noLINE: 'LINE IDがありません',
      close: '閉じる'
    },
    finance: {
      title: '財務概要',
      exportCSV: 'CSVをエクスポート',
      timeRange: '期間',
      day: '日',
      week: '週',
      month: '月',
      year: '年',
      all: 'すべて',
      searchPlaceholder: 'スクーターを検索...',
      showSoldOnly: '販売済みのみ表示',
      totalPurchase: '総購入額',
      totalMaintenance: '総メンテナンス費',
      totalIncome: '総収入',
      netProfit: '純利益',
      topROIScooter: 'ROI最高のスクーター',
      bestBrandModel: '最高のブランド/モデル',
      scooters: '台',
      scooter: 'スクーター',
      purchase: '購入',
      maintenance: 'メンテナンス',
      income: '収入',
      sale: '販売',
      net: '純額',
      roi: 'ROI',
      status: 'ステータス',
      profitable: '黒字',
      breakingEven: '損益分岐',
      lossMaker: '赤字',
      totals: '合計',
      recentExpenses: '最近の経費',
      noExpenses: '記録された経費はありません',
      loadingExpenses: '経費を読み込み中...',
      noExpensesInRange: '選択した期間に経費はありません',
      expenseTotal: '合計',
      categoryOilChange: 'オイル交換',
      categoryTires: 'タイヤ',
      categoryBrakes: 'ブレーキ',
      categoryEngine: 'エンジン',
      categoryBattery: 'バッテリー',
      categoryBodyParts: 'ボディパーツ',
      categoryCleaning: '清掃',
      categoryRegistration: '登録',
      categoryInsurance: '保険',
      categoryMiscParts: 'その他の部品',
      categoryCustom: 'カスタム',
      affectsExpenseDetails: '経費の詳細に影響します',
      tableView: 'テーブル',
      chartsView: 'チャート',
      toggleKPIs: 'KPIを切り替え',
      toggleInsights: 'インサイトを切り替え',
      toggleAdditionalTables: '追加テーブルを切り替え',
      showOperational: '運用ビューを表示',
      showFull: 'フルビューを表示',
      operationalView: '運用ビュー',
      fullView: 'フルビュー',
      operationalNet: '運用純額',
      date: '日付',
      category: 'カテゴリ',
      amount: '金額',
      note: 'メモ',
      actions: '操作',
      edit: '編集',
      delete: '削除',
      editExpense: '経費を編集',
      customLabel: 'カスタムラベル',
      confirmDeleteExpense: 'この経費を削除してもよろしいですか？',
      expenseDeleted: '経費を削除しました',
      deleteFailed: '経費の削除に失敗しました',
      expenseUpdated: '経費を更新しました',
      updateFailed: '経費の更新に失敗しました',
      expenseCreated: '経費を作成しました',
      addExpense: '経費を追加',
      brandModelPerformance: 'ブランド/モデルのパフォーマンス',
      brand: 'ブランド',
      model: 'モデル',
      count: '数',
      avgROI: '平均ROI',
      topExpenses: 'カテゴリ別の主な経費',
      noData: 'データなし',
      monthlyTrends: '月次財務トレンド',
      expenseBreakdown: '経費カテゴリの内訳',
      maintenanceEfficiency: 'メンテナンス効率',
      topPerformers: 'トップパフォーマー',
      needsAttention: '要注意',
      fleetHealth: '車両状態の概要',
      totalScooters: '総スクーター数',
      revenueVsMaintenance: 'スクーター別の収益対メンテナンス',
      profitTrend: 'スクーター別の利益トレンド',
      statusDistribution: 'ステータス分布',
      exportBasic: '基本サマリーをエクスポート',
      exportDetailed: '詳細経費をエクスポート',
      exportingDetailed: '詳細経費をエクスポート中...',
      exportCompleted: 'エクスポートが完了しました',
      exportFailed: 'エクスポートに失敗しました'
    }
  },
  id: {
    howItWorks: {
      title: 'Cara Menggunakan OurScooter (Untuk Staf)',
      subtitle: 'Langkah sederhana, aturan jelas, dan tips bermanfaat untuk bekerja dengan benar dan efisien.',
      categories: 'Kategori',
      whyTitle: 'Mengapa OurScooter: tujuan dan manfaat',
      whyDescription: 'OurScooter membantu staf menjalankan operasi penyewaan skuter harian secara efisien: lebih sedikit klik, lebih sedikit kesalahan, dan catatan yang jelas.',
      whySpeed: 'Kecepatan: buat kontrak dalam hitungan detik, gunakan kembali detail melalui duplikasi.',
      whyClarity: 'Kejelasan: status menunjukkan posisi setiap sewa secara sekilas.',
      whyControl: 'Kontrol: filter, cari, dan ganti tampilan (tabel/kanban) sesuai tugas.',
      whyAccuracy: 'Akurasi: pengaman mencegah langkah tidak valid dan sewa yang tumpang tindih.',
      signingInTitle: 'Masuk dan tetap masuk',
      signingInToken: 'Nama pengguna + kata sandi → server memverifikasi dan mengembalikan access + refresh token.',
      signingInAutoRefresh: 'Access token berumur pendek; kami menyegarkannya secara diam-diam menggunakan refresh token.',
      signingInRoles: 'Peran: admin dan staf. Konteks organisasi dipilih di bilah samping.',
      signingInSession: 'Durasi sesi: Anda tetap masuk hingga 1 hari tanpa aktivitas. Aplikasi menggunakan access token singkat 15 menit dan memperbaruinya di latar belakang. Setelah 1 hari tidak menggunakan aplikasi (atau jika cookie dihapus), Anda akan diminta masuk lagi.',
      scootersTitle: 'Skuter: menambah dan mengedit',
      scootersOpen: 'Buka "Add Scooter" untuk mendaftarkan kendaraan baru. Buat detail singkat dan akurat.',
      scootersRequired: 'Wajib: merek/model, status.',
      scootersOptional: 'Opsional: biaya pembelian, detail bahan bakar/baterai, catatan.',
      scootersTip: 'Tips: status "maintenance" menghapus skuter dari ketersediaan sampai dikembalikan.',
      clientsTitle: 'Klien: menambah dan mengedit',
      clientsOpen: 'Buka "New Client". Catat hal yang membantu Anda menemukannya nanti.',
      clientsRequired: 'Wajib: nama lengkap.',
      clientsOptional: 'Opsional: telepon, email, KTP/paspor, catatan.',
      contractsTitle: 'Kontrak: membuat dan mengelola',
      contractsWaysTitle: 'Cara membuat kontrak (4 opsi)',
      contractsFromContracts: 'Dari Contracts: tekan "Add Contract".',
      contractsFromScooters: 'Dari Scooters: buka skuter → "New Contract".',
      contractsFromClients: 'Dari Clients: buka klien → "New Contract".',
      contractsDuplicate: 'Duplikat: buka kontrak yang ada → "Duplicate" untuk menggunakan kembali detail dengan aman.',
      contractsRequiredTitle: 'Bidang yang wajib',
      contractsRequiredClient: 'Klien dan Skuter.',
      contractsRequiredDate: 'Tanggal/waktu mulai.',
      contractsRequiredRate: 'Tarif harian. (Deposit/metode pembayaran bersifat opsional.)',
      contractsStatusTitle: 'Status dan alur kerja (apa yang diizinkan)',
      contractsStatusFlow: 'Draft → Active → Closed → Archived.',
      contractsStatusActivate: 'Anda hanya dapat mengaktifkan ketika bidang wajib sudah diisi dan skuter tersedia.',
      contractsStatusClosed: 'Dari Closed Anda hanya dapat pindah ke Archived (tidak bisa kembali ke Active).',
      contractsStatusForbidden: 'Tidak diizinkan: kontrak Active yang tumpang tindih untuk skuter yang sama.',
      contractsStatusArchived: 'Kontrak Archived hilang dari tampilan default. Aktifkan "Include archived" untuk melihatnya.',
      contractsAutoTitle: 'Pembaruan status skuter otomatis',
      contractsAutoCreate: 'Membuat kontrak tidak mengubah status skuter (terlepas dari status kontrak).',
      contractsAutoActive: 'Ketika kontrak menjadi Active → skuter otomatis menjadi "rented".',
      contractsAutoClosed: 'Ketika kontrak Active di-Close/Archive → skuter otomatis menjadi "available".',
      contractsAutoDraft: 'Ketika kontrak Active dikembalikan ke Draft → skuter otomatis menjadi "available".',
      contractsAutoPrevent: 'Ini mencegah pemesanan ganda dan menjaga ketersediaan skuter tetap akurat.',
      contractsTimeTitle: 'Pelacakan waktu untuk kontrak Active',
      contractsTimeRemaining: 'Kontrak Active menampilkan sisa waktu hingga tanggal berakhir (mis. "2h 30m left").',
      contractsTimeOverdue: 'Kontrak terlambat menampilkan waktu yang telah berlalu (mis. "Overdue 1d 5h").',
      contractsTimeColors: 'Warna menunjukkan urgensi: Hijau (4+ jam), Kuning (1-4 jam), Oranye (<1 jam), Merah (terlambat).',
      contractsTimeKanban: 'Di tampilan Kanban: hanya pengatur waktu yang ditampilkan untuk kontrak Active.',
      contractsTimeList: 'Di tampilan List: baik lencana status maupun pengatur waktu ditampilkan untuk kontrak Active.',
      contractsViewsTitle: 'Kontrak: tampilan',
      contractsViewsTable: 'Tampilan tabel: pindai, filter, dan ekspor baris dengan cepat.',
      contractsViewsKanban: 'Papan Kanban: alur kerja visual berdasarkan status (Draft, Active, Closed, Archived).',
      contractsViewsToggle: 'Gunakan tombol alih tampilan di bilah alat Contracts untuk berganti tampilan.',
      contractsKanbanTitle: 'Bekerja di papan Kanban',
      contractsKanbanDrag: 'Seret & lepas kartu untuk memindahkannya ke status berikutnya yang diizinkan.',
      contractsKanbanEdit: 'Buka kartu dan tekan Edit untuk mengubah detail atau status secara langsung.',
      contractsKanbanRules: 'Aturan berlaku: Anda tidak bisa pindah dari Closed kembali ke Active; Archived bersifat final.',
      contractsKanbanBlocked: 'Pemindahan yang tidak tersedia diblokir (mis. skuter sudah Active di tempat lain).',
      contractsKanbanFilters: 'Gunakan filter untuk fokus pada satu klien atau menyertakan item Archived bila perlu.',
      findingTitle: 'Pencarian cepat (filter & cari)',
      findingScooters: 'Skuter: filter berdasarkan status, merek, model. Gunakan kotak pencarian untuk pencocokan teks cepat.',
      findingClients: 'Klien/Pengguna: cari berdasarkan nama/email; sesuaikan ukuran halaman untuk melihat lebih banyak baris.',
      findingContracts: 'Kontrak: filter berdasarkan status; opsional sertakan yang diarsipkan.',
      rowsTitle: 'Berapa banyak baris yang kami tampilkan?',
      rowsScooters: 'Skuter: dimuat per halaman dan mengambil lebih banyak bila perlu (hingga 100 per pemuatan).',
      rowsClients: 'Klien/Pengguna: Anda dapat memilih 10/20/30/40/50 baris per halaman.',
      rowsContracts: 'Kontrak: menampilkan kontrak organisasi saat ini; sertakan yang diarsipkan untuk memperluas daftar.',
      themesTitle: 'Tema dan default',
      themesChange: 'Ubah tema (Dark/Light/Blue/Wood) di bilah samping kiri bawah.',
      themesAdmin: 'Admin dapat mengonfigurasi default organisasi di Management (mis. mata uang, tarif).',
      usersTitle: 'Pengguna & Manajemen (admin)',
      usersStaff: 'Staf: operasi harian, lihat dan buat kontrak, kelola skuter/klien.',
      usersAdmin: 'Admin: semua yang bisa dilakukan staf, plus pengaturan tingkat organisasi dan penghapusan.',
      tipsTitle: 'Tips & pemecahan masalah',
      tipsActivate: 'Tidak bisa mengaktifkan? Periksa status skuter (harus tersedia) dan bidang yang wajib.',
      tipsContract: 'Tidak bisa melihat kontrak? Mungkin diarsipkan; aktifkan "Include archived".',
      tipsSession: 'Sesi berakhir? Jika sudah lebih dari sehari, cukup masuk lagi.'
    },
    booking: {
      orgNotFound: 'Organisasi tidak ditemukan',
      orgNotFoundDesc: 'Halaman pemesanan yang Anda cari tidak ada. Ini bisa berarti:',
      orgNotFoundReason1: 'Slug organisasi salah',
      orgNotFoundReason2: 'Organisasi belum menyiapkan halaman pemesanan',
      orgNotFoundReason3: 'Halaman pemesanan telah dihapus',
      orgNotFoundOwner: 'Jika Anda pemilik organisasi, pastikan Anda telah mengonfigurasi slug organisasi di Management → Technical Details.',
      requestReceived: 'Permintaan Diterima!',
      requestReceivedDesc: 'Permintaan pemesanan Anda telah diterima. Ini belum merupakan pemesanan yang dikonfirmasi.',
      willContact: '{{business}} akan segera menghubungi Anda untuk mengonfirmasi penyewaan.',
      requestIdLabel: 'ID Permintaan:',
      contactLabel: 'Kontak:',
      showroomBadge: 'Showroom Skuter',
      bookBadge: 'Pesan Skuter',
      showroomIntro: 'Lihat skuter kami, pilih yang Anda suka, dan kirim permintaan pemesanan. Kami akan segera mengonfirmasi dengan Anda.',
      bookIntro: 'Kirim permintaan pemesanan Anda di bawah dan kami akan segera menghubungi Anda untuk konfirmasi.',
      yourDetailsStep: '3. Detail Anda',
      yourInformation: 'Informasi Anda',
      fullName: 'Nama Lengkap',
      fullNamePlaceholder: 'Masukkan nama lengkap',
      email: 'Email',
      emailPlaceholder: 'Masukkan alamat email',
      localPhone: 'Nomor Telepon Lokal',
      phonePlaceholder: 'Nomor telepon',
      messengerNumber: 'Nomor Messenger (WhatsApp, dll.)',
      messengerPlaceholder: 'Nomor WhatsApp (opsional)',
      sameAsPhone: 'Sama dengan telepon',
      lineId: 'LINE ID',
      lineIdPlaceholder: 'LINE ID (opsional)',
      passportNumber: 'Nomor Paspor',
      passportPlaceholder: 'Nomor paspor (opsional)',
      nationality: 'Kewarganegaraan',
      nationalityPlaceholder: 'Kewarganegaraan (opsional)',
      localAddress: 'Alamat Lokal',
      localAddressPlaceholder: 'Alamat lokal di negara ini (opsional)',
      rentalDatesStep: '1. Tanggal Sewa',
      bookingDetails: 'Detail Pemesanan',
      startDate: 'Tanggal Mulai',
      duration: 'Durasi',
      endDate: 'Tanggal Selesai',
      dayUnit: 'hari',
      daysUnit: 'hari',
      weekUnit: 'minggu',
      weeksUnit: 'minggu',
      monthUnit: 'bulan',
      monthsUnit: 'bulan',
      scooterUnit: 'skuter',
      scootersUnit: 'skuter',
      pricingSummary: 'Ringkasan Harga',
      lineDailyRate: '{{count}} {{unit}} × Tarif Harian:',
      lineWeeklyRate: '{{count}} {{unit}} × Tarif Mingguan:',
      lineDailyFromWeekly: '{{count}} {{unit}} × Tarif Harian (dari mingguan):',
      lineMonthlyRate: '{{count}} {{unit}} × Tarif Bulanan:',
      lineDailyFromMonthly: '{{count}} {{unit}} × Tarif Harian (dari bulanan):',
      subtotal: 'Subtotal:',
      deposit: 'Deposit:',
      total: 'Total:',
      selectToSeePricingShowroom: 'Pilih tanggal dan skuter untuk melihat harga',
      selectToSeePricingGroup: 'Pilih tanggal dan grup harga untuk melihat harga',
      selectPricingGroup: 'Pilih Grup Harga',
      perDay: '/hari',
      perWeek: '/minggu',
      perMonth: '/bulan',
      specialPromotions: 'Promosi Khusus:',
      scooterPreference: 'Preferensi Skuter',
      noPreference: 'Tidak ada preferensi',
      scooterFallback: 'Skuter',
      chooseScooterStep: '2. Pilih Skuter Anda',
      checkingAvailability: 'Memeriksa ketersediaan untuk tanggal Anda…',
      availableForDates: '{{available}} dari {{total}} tersedia untuk tanggal Anda',
      fleetCount: '{{count}} {{unit}} di armada kami',
      lockedScooterNote: 'Skuter ini telah dipesan untuk Anda melalui tautan pemesanan Anda',
      noScootersAvailable: 'Saat ini tidak ada skuter yang tersedia. Silakan hubungi kami langsung.',
      available: 'Tersedia',
      unavailable: 'Tidak tersedia',
      photosCount: '{{count}} foto',
      viewPhoto: 'Lihat',
      viewPhotosAria: 'Lihat foto {{title}}',
      additionalNotes: 'Catatan Tambahan',
      notesPlaceholder: 'Permintaan khusus atau informasi apa pun...',
      charactersCount: '{{count}}/255 karakter',
      preSignTitle: '⚡ Tanda Tangani Kontrak Lebih Awal (Disarankan)',
      preSignDesc: 'Percepat proses pengambilan Anda! Menandatangani kontrak lebih awal menghemat waktu saat Anda tiba.',
      preSignBenefits: 'Tanpa pembayaran • Bukan komitmen • Hanya mempercepat pengambilan',
      completeFieldsToSign: 'Lengkapi semua kolom wajib di atas untuk mengaktifkan penandatanganan kontrak lebih awal.',
      missingLabel: 'Belum diisi:',
      signatureSaved: 'Tanda tangan disimpan: {{name}}',
      submitting: 'Mengirim...',
      submitRequest: 'Kirim Permintaan Pemesanan',
      disclaimer: 'Dengan mengirimkan formulir ini, Anda memahami bahwa ini adalah permintaan, bukan pemesanan yang dikonfirmasi. Toko akan menghubungi Anda untuk mengonfirmasi ketersediaan dan menyelesaikan pemesanan.',
      selectDatesForPrice: 'Pilih tanggal Anda untuk melihat harga',
      depositShort: 'deposit',
      requestToBook: 'Minta Pesan',
      continueBtn: 'Lanjutkan',
      closeBtn: 'Tutup',
      prevPhoto: 'Foto sebelumnya',
      nextPhoto: 'Foto berikutnya',
      langLabel: 'Bahasa',
      toastLoadFailed: 'Gagal memuat halaman pemesanan',
      toastVersionFailed: 'Gagal membuat versi kontrak. Silakan coba lagi.',
      toastPreviewFailed: 'Gagal memuat pratinjau kontrak',
      toastEnterName: 'Silakan masukkan nama lengkap Anda',
      toastEnterEmail: 'Silakan masukkan alamat email Anda',
      toastEnterPhone: 'Silakan masukkan nomor telepon Anda',
      toastSelectStart: 'Silakan pilih tanggal mulai',
      toastSelectScooter: 'Silakan pilih skuter',
      toastSelectPricingGroup: 'Silakan pilih grup harga',
      toastPricingFailed: 'Tidak dapat menghitung harga',
      toastSubmitFailed: 'Gagal mengirim permintaan pemesanan. Silakan coba lagi.',
      errFullNameRequired: 'Nama lengkap wajib diisi',
      errEmailRequired: 'Email wajib diisi',
      errEmailInvalid: 'Email tidak valid',
      errPhoneRequired: 'Nomor telepon wajib diisi',
      mfFullName: 'Nama Lengkap',
      mfEmail: 'Email',
      mfPhone: 'Nomor Telepon',
      mfStartDate: 'Tanggal Mulai',
      mfScooter: 'Skuter',
      mfPricingGroup: 'Grup Harga',
      mfPricingInfo: 'Informasi harga'
    },
    theme: {
      dark: 'Gelap',
      light: 'Terang',
      blue: 'Biru',
      wood: 'Kayu'
    },
    home: {
      quickActions: 'Tindakan Cepat',
      recentActivity: 'Aktivitas Terbaru',
      fleetStatusOverview: 'Ikhtisar Status Armada',
      welcomeTitle: 'Selamat datang di OurScooters',
      welcomeSubtitle: 'Dasbor manajemen armada skuter Anda yang lengkap',
      addScooter: 'Skuter Baru',
      registerScooter: 'Daftarkan skuter baru',
      addClient: 'Klien Baru',
      registerClient: 'Daftarkan pelanggan baru',
      newContract: 'Kontrak Baru',
      createAgreement: 'Buat perjanjian sewa',
      org: 'Organisasi',
      allOrgs: 'Semua organisasi',
      availableScooters: 'Skuter Tersedia',
      readyForRental: 'Siap disewa',
      rentedScooters: 'Skuter Disewa',
      currentlyInUse: 'Sedang digunakan',
      maintenance: 'Pemeliharaan',
      underRepair: 'Dalam perbaikan',
      fleetOverview: 'Ikhtisar Armada',
      available: 'tersedia',
      rented: 'disewa',
      activeRevenue: 'Pendapatan Aktif',
      monthlyRecurring: 'Pendapatan berulang bulanan',
      totalClients: 'Total Klien',
      activeContracts: 'kontrak aktif',
      avgBattery: 'Rata-rata Baterai',
      fleetBatteryHealth: 'Kesehatan baterai armada',
      avgFuel: 'Rata-rata Bahan Bakar',
      fuel: 'bahan bakar',
      electric: 'listrik',
      fleetROI: 'ROI Armada',
      rev: 'pendapatan',
      cost: 'biaya',
      inMaintenance: 'Dalam pemeliharaan',
      newClientRegistered: 'Klien baru terdaftar',
      registered: 'terdaftar'
    },
    users: {
      title: 'Pengguna',
      errorLoading: 'Gagal memuat pengguna.',
      id: 'ID',
      email: 'Email',
      role: 'Peran',
      username: 'Nama pengguna',
      org: 'Organisasi',
      roleSuperadmin: 'Superadmin',
      roleAdmin: 'Admin',
      roleStaff: 'Staf'
    },
    management: {
      title: 'Manajemen Organisasi',
      errorLoading: 'Kesalahan memuat pengaturan.',
      timeFormat: 'Format Waktu',
      defaultCurrency: 'Mata Uang Default',
      defaultCountryCode: 'Kode Negara Default',
      defaultDailyPrice: 'Harga Harian Default',
      defaultDeposit: 'Deposit Kontrak Default',
      defaultRentLength: 'Durasi Sewa Default (Hari)',
      defaultPaymentMethod: 'Metode Pembayaran Default',
      buttonStyle: 'Gaya Tombol',
      buttonStyleDesc: 'Pilih gaya visual untuk semua tombol di seluruh aplikasi.',
      countryCodeHint: '1 untuk AS, 44 untuk Inggris, dll.',
      countryCodeExplain: 'Hanya angka. Akan menjadi awalan nomor telepon di formulir klien.',
      saving: 'Menyimpan...',
      saveSettings: 'Simpan Pengaturan',
      saved: 'Tersimpan',
      modernStyle: 'Gaya Modern',
      classicStyle: 'Gaya Klasik',
      originalStyle: 'Gaya Asli',
      btnPrimary: 'Primer',
      btnSecondary: 'Sekunder',
      btnGhost: 'Ghost',
      btnDanger: 'Bahaya',
      modernDesc: 'Latar gradien dengan interaksi mikro dan sudut membulat',
      classicDesc: 'Desain bersih dan minimal dengan warna sesuai tema dan batas halus',
      originalDesc: 'Desain tombol asli yang Anda sukai sebelum refaktor',
      timeFormatExample1: '15 September 2025 6:09 PM',
      timeFormatExample2: 'September 15 2025 6:09 PM',
      timeFormatExample3: '15 September 2025 18:09',
      timeFormatExample4: 'September 15 2025 18:09'
    },
    common: {
      language: 'Bahasa',
      phoneTooShort: 'Nomor telepon harus minimal 7 digit',
      phoneTooLong: 'Nomor telepon terlalu panjang (maksimal 15 digit)',
      theme: 'Tema',
      save: 'Simpan',
      cancel: 'Batal',
      logout: 'Keluar',
      loading: 'Memuat...',
      reset: 'Atur ulang',
      close: 'Tutup',
      back: 'Kembali',
      delete: 'Hapus',
      clearAll: 'Hapus Semua',
      upload: 'Unggah',
      uploading: 'Mengunggah...',
      selectPhoto: 'Pilih Foto',
      addNotes: 'Tambah catatan...',
      notes: 'Catatan',
      optional: 'Opsional',
      retake: 'Ambil ulang',
      usePhoto: 'Gunakan Foto',
      viewAll: 'Lihat Semua Foto',
      confirmDelete: 'Apakah Anda yakin ingin menghapus foto ini?',
      add: 'Tambah',
      copied: 'Disalin',
      expand: 'Perluas',
      collapse: 'Ciutkan',
      updateFailed: 'Pembaruan gagal',
      updateFailedWithMessage: 'Pembaruan gagal: {{message}}',
      failedToSendEmail: 'Gagal mengirim email',
      statusChangeNotAllowed: 'Perubahan status ini tidak diizinkan',
      expenseAddedSuccessfully: 'Pengeluaran berhasil ditambahkan',
      edit: 'Edit'
    },
    toasts: {
      contractCreated: 'Kontrak berhasil dibuat',
      contractUpdated: 'Kontrak berhasil diperbarui',
      contractDeleted: 'Kontrak berhasil dihapus',
      contractCreateFailed: 'Gagal membuat kontrak',
      contractUpdateFailed: 'Gagal memperbarui kontrak',
      contractDeleteFailed: 'Gagal menghapus kontrak',
      scooterCreated: 'Skuter berhasil dibuat',
      scooterUpdated: 'Skuter berhasil diperbarui',
      scooterDeleted: 'Skuter berhasil dihapus',
      scooterStatusUpdated: 'Status skuter berhasil diperbarui',
      scooterStatusUpdateFailed: 'Gagal memperbarui status skuter',
      clientCreated: 'Klien berhasil dibuat',
      clientUpdated: 'Klien berhasil diperbarui',
      clientDeleted: 'Klien berhasil dihapus',
      settingsUpdated: 'Pengaturan berhasil diperbarui!',
      scooterStatusChangedAuto: 'Status Skuter Berubah Otomatis',
      scooterStatusChangeDesc: '{{scooterName}} dipindahkan dari {{fromStatus}} ke {{toStatus}} {{reason}}.',
      becauseContractActivated: 'karena kontrak diaktifkan',
      becauseContractDeactivated: 'karena kontrak ditutup/diarsipkan',
      automatically: 'secara otomatis',
      viewScooter: 'Lihat Skuter',
      scooterOpenedFromNotif: 'Membuka detail {{scooterName}}',
      scooterOpenedFromNotifDesc: 'Skuter ini dibuka otomatis dari notifikasi perubahan status.',
      closedContractsMoveRestriction: 'Kontrak yang ditutup hanya dapat dipindahkan ke Arsip.'
    },
    login: {
      title: 'Masuk',
      email: 'Email',
      username: 'Nama pengguna',
      password: 'Kata sandi',
      signIn: 'Masuk',
      signingIn: 'Sedang masuk...',
      login: 'Masuk',
      loggingIn: 'Sedang masuk...',
      loginFailed: 'Gagal masuk',
      language: 'Bahasa'
    },
    sidebar: {
      scooters: 'Skuter',
      clients: 'Klien',
      contracts: 'Kontrak',
      finance: 'Keuangan',
      users: 'Pengguna',
      activity: 'Log Aktivitas',
      management: 'Manajemen',
      howItWorks: 'Cara Kerja',
      signIn: 'Masuk',
      expand: 'Perluas bilah sisi',
      collapse: 'Ciutkan bilah sisi',
      logoutConfirm: 'Apakah Anda yakin ingin keluar?'
    },
    toolbar: {
      searchPlaceholder: 'Cari berdasarkan ID, model, atau status…',
      clear: 'Hapus',
      listView: 'Tampilan daftar',
      kanbanView: 'Tampilan Kanban',
      calendarView: 'Kalender',
      gridView: 'Tampilan kisi',
      mapView: 'Tampilan peta',
      filter: 'Filter',
      export: 'Ekspor',
      total: 'Total: {{count}}',
      queryLabel: 'kueri',
      clearQuery: 'Hapus kueri',
      removeFilter: 'Hapus {{filter}}'
    },
    ui: {
      zoom: 'Zoom'
    },
    calendar: {
      week: 'Minggu',
      month: 'Bulan',
      all: 'Semua',
      plusMore: '+{{count}} lagi',
      scrollHint: 'Shift+Gulir untuk bergerak horizontal',
      hideEmpty: 'Sembunyikan skuter tanpa kontrak mendatang'
    },
    status: {
      available: 'Tersedia',
      rented: 'Disewa',
      maintenance: 'Pemeliharaan',
      retired: 'Pensiun',
      draft: 'Draf',
      active: 'Aktif',
      closed: 'Ditutup',
      archived: 'Diarsipkan'
    },
    clients: {
      id: 'ID Klien',
      name: 'Nama',
      messenger: 'WhatsApp',
      newClient: 'Klien baru',
      creating: 'Membuat klien...',
      fullName: 'Nama Lengkap',
      fullNamePlaceholder: 'Masukkan nama lengkap',
      email: 'Email',
      emailPlaceholder: 'Masukkan alamat email',
      localPhone: 'Nomor Telepon Lokal',
      localPhonePlaceholder: 'Masukkan nomor telepon lokal (opsional)',
      messengerNumber: 'Nomor WhatsApp',
      countryCodePlaceholder: '1',
      messengerNumberPlaceholder: 'Masukkan nomor WhatsApp (opsional)',
      lineId: 'LINE ID',
      lineIdPlaceholder: 'Masukkan LINE ID (opsional)',
      whatsappButton: 'WhatsApp',
      lineButton: 'LINE',
      contactViaWhatsApp: 'Hubungi via WhatsApp',
      contactViaLINE: 'Hubungi via LINE',
      notes: 'Catatan',
      notesPlaceholder: 'Catatan tambahan tentang klien ini (mis. nomor telepon negara lain)',
      addClient: 'Klien baru',
      detailsTitle: 'Detail Klien',
      created: 'Dibuat',
      clientId: 'ID Klien',
      edit: 'Edit Klien',
      previousContracts: 'Kontrak sebelumnya:',
      noCurrentContracts: 'Tidak ada kontrak saat ini',
      editClient: 'Edit Klien',
      newContractBtn: 'Kontrak Baru',
      close: 'Tutup',
      statusHistory: 'Riwayat status',
      unknownClient: 'Klien Tidak Dikenal'
    },
    scooters: {
      id: 'ID Skuter',
      modelRequired: 'Model wajib diisi',
      batteryRange: 'Baterai harus 0–100',
      odometerNegative: 'Odometer tidak boleh negatif',
      fuelRange: 'Bahan bakar % harus 0–100',
      incompatibleEnergy: 'Listrik tidak boleh memiliki bahan bakar; kendaraan bahan bakar tidak boleh memiliki baterai',
      editScooter: 'Edit Skuter',
      newScooter: 'Skuter baru',
      updating: 'Memperbarui skuter...',
      creating: 'Membuat skuter...',
      model: 'Model',
      modelPlaceholder: 'mis. Ninebot Max',
      status: 'Status',
      typeLevel: 'Tipe & Level',
      odometer: 'Odometer',
      qrCode: 'Kode QR',
      qrPlaceholder: 'Kode QR opsional',
      plateNumber: 'Nomor Pelat',
      platePlaceholder: 'Nomor pelat opsional',
      nickname: 'Nama panggilan',
      nicknamePlaceholder: 'mis. Honda Merah #1',
      nicknameOptional: 'Nama panggilan (opsional)',
      brand: 'Merek',
      brandPlaceholder: 'Merek opsional',
      vehicleType: 'Tipe kendaraan',
      electric: 'Listrik',
      fuel: 'Bahan bakar',
      batteryPct: 'Baterai %',
      fuelPct: 'Bahan bakar %',
      odometerKm: 'Odometer (km)',
      updateScooter: 'Perbarui skuter',
      addScooter: 'Skuter baru',
      details: 'Detail',
      edit: 'Edit',
      created: 'Dibuat',
      statusHistory: 'Riwayat status skuter',
      columnScooterId: 'ID Skuter',
      columnBrand: 'Merek',
      columnModel: 'Model',
      columnPlateNumber: 'Nomor Pelat',
      columnNickname: 'Nama panggilan',
      columnStatus: 'Status',
      columnVehicleStatus: 'Tipe & Level',
      columnOdometer: 'Odometer',
      pictures: 'Gambar',
      noPhotos: 'Belum ada foto',
      noPhotosYet: 'Belum ada foto',
      allPhotos: 'Semua Foto',
      viewAllPhotos: 'Lihat Semua Foto',
      damage: 'Kerusakan',
      damageOnly: 'Kerusakan',
      showingDamage: 'Menampilkan Kerusakan',
      noDamagePhotos: 'Tidak ada foto dengan kerusakan',
      markAsDamage: 'Tandai sebagai kerusakan',
      markDamage: 'Tandai Kerusakan',
      removeDamage: 'Hapus Kerusakan',
      markNoDamage: 'Tandai tanpa kerusakan',
      damageDetected: 'Kerusakan terdeteksi',
      scooterPhoto: 'Foto skuter',
      selectImageFile: 'Silakan pilih file gambar',
      fileSizeLimit: 'Ukuran file harus kurang dari 10MB',
      photoUploaded: 'Foto berhasil diunggah',
      photoDeleted: 'Foto dihapus',
      photoDeleteFailed: 'Gagal menghapus foto',
      photoUpdateFailed: 'Gagal memperbarui foto',
      photoUploadFailed: 'Gagal mengunggah foto',
      photoStorageNotConfigured: 'Penyimpanan foto belum dikonfigurasi. Silakan hubungi administrator Anda.',
      deletePhoto: 'Hapus foto',
      drawCircle: 'Gambar Lingkaran',
      stopDrawing: 'Berhenti Menggambar',
      undo: 'Urungkan',
      redo: 'Ulangi',
      capture: 'Ambil',
      takePhoto: 'Ambil Foto',
      chooseFromGallery: 'Pilih dari Galeri',
      addPhoto: 'Tambah Foto',
      photoPreview: 'Pratinjau Foto',
      cameraError: 'Gagal mengakses kamera',
      cameraPermissionDenied: 'Izin kamera ditolak'
    },
    pages: {
      manageColumns: 'Kelola kolom',
      columns: 'Kolom',
      retiredHidden: 'Skuter pensiun disembunyikan',
      failedLoadScooters: 'Gagal memuat skuter',
      checkConnection: 'Silakan periksa koneksi Anda dan coba lagi.',
      retry: 'Coba lagi',
      clearFilters: 'Hapus filter',
      addScooter: 'Skuter baru',
      noScootersYet: 'Belum ada skuter',
      noScootersMatch: 'Tidak ada skuter yang cocok dengan filter Anda',
      addFirstScooter: 'Tambahkan skuter pertama Anda untuk memulai.',
      tryAdjusting: 'Coba sesuaikan atau hapus filter Anda.',
      rowsPerPage: 'Baris per halaman:',
      pageOf: 'Halaman {{current}} dari {{total}}',
      prevShort: 'Sebelumnya',
      nextShort: 'Berikutnya',
      prev: 'Halaman sebelumnya',
      next: 'Halaman berikutnya'
    },
    contractsPage: {
      newContract: 'Kontrak Baru',
      showArchived: 'Tampilkan kontrak yang diarsipkan',
      newContractSubtitle: 'Buat dan tinjau perjanjian sewa baru dengan cepat',
      createdDate: 'Tanggal Dibuat',
      recentlyUpdated: 'Baru Diperbarui',
      recentlyClosed: 'Baru Ditutup',
      allClosed: 'Semua Ditutup',
      showAll: 'Tampilkan Semua',
      showRecent: 'Tampilkan Terbaru',
      sortBy: 'Urutkan menurut...',
      archivedOnlyToDraft: 'Kontrak yang diarsipkan hanya dapat dipindahkan ke Draf.'
    },
    activity: {
      title: 'Log Aktivitas',
      subtitle: 'Pantau aktivitas karyawan dan perubahan sistem. Lihat log yang mudah dipahami untuk pemecahan masalah dan audit.',
      filterActivity: 'Filter Aktivitas',
      timePeriod: 'Periode Waktu',
      lastHour: 'Jam Terakhir',
      today: 'Hari Ini',
      thisWeek: 'Minggu Ini',
      thisMonth: 'Bulan Ini',
      custom: 'Kustom',
      from: 'Dari',
      to: 'Sampai',
      employee: 'Karyawan',
      type: 'Tipe Aktivitas',
      allEmployees: 'Semua karyawan',
      allActivities: 'Semua aktivitas',
      scooterMgmt: 'Manajemen Skuter',
      contractMgmt: 'Manajemen Kontrak',
      clientMgmt: 'Manajemen Klien',
      orgSettings: 'Pengaturan Organisasi',
      searchEverything: 'Cari Semuanya',
      searchPlaceholder: 'Cari nama, ID, harga, status...'
    },
    contracts: {
      filters: 'Filter',
      editContract: 'Edit Kontrak',
      dates: 'Tanggal',
      for: 'untuk',
      scooter: 'Skuter',
      start: 'Mulai',
      end: 'Selesai',
      duration: 'Durasi',
      days: 'hari',
      weeks: 'minggu',
      months: 'bulan',
      estimatedTotal: 'Perkiraan total',
      fetchingData: 'Mengambil data klien dan skuter',
      scooterCurrentlyRented: 'ℹ️ Skuter ini sedang disewa{{until}}.',
      until: 'sampai',
      futureReservationNote: 'Anda masih dapat membuat kontrak untuk reservasi mendatang - pastikan saja tanggalnya tidak tumpang tindih.',
      searchClients: 'Cari klien berdasarkan nama/email/id',
      noMatches: 'Tidak ada yang cocok',
      selected: 'Dipilih:',
      scooterQuery: 'Skuter (ID atau model mengandung)',
      scooterPlaceholder: 'mis. Ninebot atau UUID',
      dailyRateRange: 'Tarif harian (min/maks)',
      min: 'Min',
      max: 'Maks',
      minExceedsMax: 'Min tidak boleh melebihi Maks',
      endAfterStart: 'Waktu selesai harus setelah waktu mulai',
      clientRequired: 'Klien wajib diisi',
      scooterRequired: 'Skuter wajib diisi',
      startTimeRequired: 'Waktu mulai wajib diisi',
      dailyRatePositive: 'Tarif harian harus > 0',
      addContract: 'Kontrak baru',
      client: 'Klien',
      selectClient: 'Pilih klien',
      searchScooters: 'Cari skuter...',
      startTime: 'Waktu mulai',
      status: 'Status',
      dailyRate: 'Tarif harian',
      deposit: 'Deposit',
      endTime: 'Tanggal Selesai',
      contractId: 'ID Kontrak',
      paymentMethod: 'Metode Pembayaran',
      cash: 'Tunai',
      local_bank_transfer: 'Transfer Bank Lokal',
      international_bank_transfer: 'Transfer Bank Internasional',
      notes: 'Catatan',
      notesPlaceholder: 'Catatan opsional tentang kontrak ini',
      createDraftForScooter: 'Buat draf kontrak baru untuk skuter ini.',
      saveAsDraftHint: 'Simpan sebagai draf untuk diaktifkan nanti',
      startImmediatelyHint: 'Mulai kontrak segera',
      creating: 'Membuat...',
      createDraft: 'Buat draf',
      createActive: 'Buat aktif',
      day: 'hari',
      forDays: 'selama {{count}} hari',
      forDays_plural: 'selama {{count}} hari',
      total: 'Total:',
      dayOf: 'hari {{day}} dari {{total}}'
    },
    contractsUi: {
      renting: 'menyewa',
      statusHistory: 'Riwayat status',
      noHistory: 'Tidak ada riwayat tersedia',
      created: 'Dibuat',
      overdue: 'Terlambat',
      overdueTitle: 'Kontrak terlambat',
      remainingTitle: 'Waktu tersisa hingga kontrak berakhir',
      remaining: 'tersisa',
      unitDayShort: 'h',
      unitHourShort: 'j',
      unitMinuteShort: 'm',
      clientDetails: 'Detail Klien',
      scooterDetails: 'Detail Skuter',
      clientNotFound: 'Klien tidak ditemukan',
      scooterNotFound: 'Skuter tidak ditemukan',
      fullName: 'Nama Lengkap',
      localPhone: 'Telepon Lokal',
      messenger: 'WhatsApp',
      brand: 'Merek',
      vehicleType: 'Tipe kendaraan',
      battery: 'Baterai',
      qrCode: 'Kode QR',
      scooterStatus: 'Status skuter',
      noStatusChanges: 'Belum ada perubahan status',
      loadingHistory: 'Memuat riwayat...',
      characters: 'karakter',
      back: 'Kembali',
      startDate: 'Tanggal Mulai',
      endDate: 'Tanggal Selesai',
      contractId: 'ID Kontrak',
      editContract: 'Edit Kontrak',
      duplicate: 'Duplikat',
      duplicateContract: 'Duplikat kontrak',
      duplicateSubtitle: 'Pilih mulai, durasi, dan selesai untuk draf baru',
      endTimeMustBeAfter: 'Waktu selesai harus setelah waktu mulai',
      changeStatus: 'Ubah status',
      currentStatus: 'saat ini',
      changeScooterStatus: 'Ubah status skuter',
      currentScooterStatus: 'saat ini',
      showingRange: 'Menampilkan {{start}}–{{end}} dari {{total}}',
      contractCreatedTitle: 'Kontrak dibuat',
      contractCreatedMessage: 'Apakah Anda ingin membuka kontrak yang dibuat?',
      contractLabel: 'Kontrak',
      openContract: 'Buka Kontrak',
      stayHere: 'Tetap di Sini',
      scooterStatusQuestion: 'Apa status skuter "{{model}}"?',
      currentStatusLabel: 'Status saat ini:',
      statusChangeHelp: 'Ini akan membantu Anda mengelola ketersediaan skuter setelah perubahan status kontrak.',
      working: 'Memproses…',
      moveContractAndUpdate: 'Pindahkan Kontrak & Perbarui Skuter',
      enterToConfirm: 'Enter untuk konfirmasi · Esc untuk batal',
      statusAvailable: 'Tersedia',
      statusAvailableDesc: 'Skuter siap disewa',
      statusRented: 'Disewa',
      statusRentedDesc: 'Skuter sedang disewa',
      statusMaintenance: 'Pemeliharaan',
      statusMaintenanceDesc: 'Skuter perlu pemeliharaan',
      statusRetired: 'Pensiun',
      statusRetiredDesc: 'Skuter tidak lagi beroperasi',
      statusCurrent: 'Saat ini',
      moveContractTitle: 'Pindahkan Kontrak',
      moveContractMessage: 'Silakan pilih status baru untuk skuter saat memindahkan kontrak ini.',
      fromLabel: 'Dari',
      toLabel: 'Ke',
      clientLabel: 'Klien',
      scooterLabel: 'Skuter',
      currentContract: 'Kontrak saat ini:',
      viewContract: 'Lihat Kontrak'
    },
    contractDocument: {
      print: 'Cetak',
      saveAsPDF: 'Simpan sebagai PDF',
      email: 'Email',
      addSignature: 'Tambah Tanda Tangan',
      emailRequired: 'Alamat email wajib diisi',
      emailSent: 'Kontrak berhasil dikirim',
      enterSignatureName: 'Masukkan nama penanda tangan untuk ditambahkan ke dokumen',
      loading: 'Memuat kontrak...',
      preparingPDF: 'Menyiapkan PDF...',
      downloadSuccess: 'PDF berhasil diunduh',
      downloadFailed: 'Gagal membuat PDF',
      retry: 'Coba lagi',
      emailContract: 'Email Kontrak',
      recipientEmail: 'Email Penerima',
      sending: 'Mengirim...',
      send: 'Kirim'
    },
    businessDetails: {
      title: 'Detail Bisnis',
      settingsUpdated: 'Detail bisnis berhasil diperbarui',
      errorLoading: 'Kesalahan memuat detail bisnis.',
      helpText: 'Isi informasi bisnis Anda di bawah ini. Detail ini akan muncul di kontrak sewa. Biarkan kosong jika Anda lebih suka mengisinya dengan tangan pada kontrak cetak.',
      businessName: 'Nama Bisnis',
      businessNamePlaceholder: 'Masukkan nama bisnis Anda',
      businessAddress: 'Alamat Bisnis',
      businessAddressPlaceholder: 'Masukkan alamat bisnis Anda',
      businessPhone: 'Telepon Bisnis',
      businessPhonePlaceholder: '+66 2 123 4567',
      businessEmail: 'Email Bisnis',
      businessEmailPlaceholder: 'mis. business@example.com',
      taxId: 'NPWP / Registrasi Bisnis',
      taxIdPlaceholder: '1234567890123',
      representativeName: 'Nama Perwakilan Resmi',
      representativeNamePlaceholder: 'John Smith',
      representativeIdNumber: 'Nomor ID Perwakilan',
      representativeIdNumberPlaceholder: '1234567890123',
      saving: 'Menyimpan...',
      saveDetails: 'Simpan Detail Bisnis',
      saved: 'Tersimpan'
    },
    messaging: {
      title: 'Hubungi Klien',
      scanQR: 'Pindai Kode QR',
      orClickLink: 'Atau klik tautan di bawah',
      openApp: 'Buka Aplikasi Pesan',
      selectTemplate: 'Pilih Template Pesan',
      customMessage: 'Pesan Kustom',
      messageEditor: 'Editor Pesan',
      thankYou: 'Terima Kasih',
      reminder: 'Pengingat',
      payment: 'Pengingat Pembayaran',
      templateThankYou: 'Terima kasih telah memilih {{businessName}}! Kami harap Anda menikmati pengalaman menyewa skuter. Kami menantikan untuk melayani Anda lagi!',
      templateReminder: 'Halo {{clientName}}! Ini {{businessName}}. Pengingat: Masa sewa Anda berakhir besok. Mohon kembalikan skuter.',
      templatePayment: 'Halo {{clientName}}! Ini {{businessName}}. Pengingat pembayaran untuk kontrak #{{contractId}}.',
      noWhatsApp: 'Tidak ada nomor WhatsApp',
      noLINE: 'Tidak ada LINE ID',
      close: 'Tutup'
    },
    finance: {
      title: 'Ikhtisar Keuangan',
      exportCSV: 'Ekspor CSV',
      timeRange: 'Rentang Waktu',
      day: 'Hari',
      week: 'Minggu',
      month: 'Bulan',
      year: 'Tahun',
      all: 'Semua',
      searchPlaceholder: 'Cari skuter...',
      showSoldOnly: 'Tampilkan hanya yang terjual',
      totalPurchase: 'Total Pembelian',
      totalMaintenance: 'Total Pemeliharaan',
      totalIncome: 'Total Pendapatan',
      netProfit: 'Laba Bersih',
      topROIScooter: 'Skuter ROI Teratas',
      bestBrandModel: 'Merek/Model Terbaik',
      scooters: 'skuter',
      scooter: 'Skuter',
      purchase: 'Pembelian',
      maintenance: 'Pemeliharaan',
      income: 'Pendapatan',
      sale: 'Penjualan',
      net: 'Bersih',
      roi: 'ROI',
      status: 'Status',
      profitable: 'Menguntungkan',
      breakingEven: 'Impas',
      lossMaker: 'Merugi',
      totals: 'Total',
      recentExpenses: 'Pengeluaran Terbaru',
      noExpenses: 'Tidak ada pengeluaran tercatat',
      loadingExpenses: 'Memuat pengeluaran...',
      noExpensesInRange: 'Tidak ada pengeluaran dalam rentang waktu yang dipilih',
      expenseTotal: 'Total',
      categoryOilChange: 'Ganti Oli',
      categoryTires: 'Ban',
      categoryBrakes: 'Rem',
      categoryEngine: 'Mesin',
      categoryBattery: 'Baterai',
      categoryBodyParts: 'Bodi',
      categoryCleaning: 'Pembersihan',
      categoryRegistration: 'Registrasi',
      categoryInsurance: 'Asuransi',
      categoryMiscParts: 'Suku Cadang Lain',
      categoryCustom: 'Kustom',
      affectsExpenseDetails: 'memengaruhi detail pengeluaran',
      tableView: 'Tabel',
      chartsView: 'Grafik',
      toggleKPIs: 'Alihkan KPI',
      toggleInsights: 'Alihkan Wawasan',
      toggleAdditionalTables: 'Alihkan Tabel Tambahan',
      showOperational: 'Tampilkan tampilan operasional',
      showFull: 'Tampilkan tampilan penuh',
      operationalView: 'Tampilan operasional',
      fullView: 'Tampilan penuh',
      operationalNet: 'Bersih operasional',
      date: 'Tanggal',
      category: 'Kategori',
      amount: 'Jumlah',
      note: 'Catatan',
      actions: 'Tindakan',
      edit: 'Edit',
      delete: 'Hapus',
      editExpense: 'Edit Pengeluaran',
      customLabel: 'Label kustom',
      confirmDeleteExpense: 'Apakah Anda yakin ingin menghapus pengeluaran ini?',
      expenseDeleted: 'Pengeluaran dihapus',
      deleteFailed: 'Gagal menghapus pengeluaran',
      expenseUpdated: 'Pengeluaran diperbarui',
      updateFailed: 'Gagal memperbarui pengeluaran',
      expenseCreated: 'Pengeluaran dibuat',
      addExpense: 'Tambah Pengeluaran',
      brandModelPerformance: 'Kinerja Merek/Model',
      brand: 'Merek',
      model: 'Model',
      count: 'Jumlah',
      avgROI: 'ROI Rata-rata',
      topExpenses: 'Pengeluaran Teratas berdasarkan Kategori',
      noData: 'Tidak ada data',
      monthlyTrends: 'Tren Keuangan Bulanan',
      expenseBreakdown: 'Rincian Kategori Pengeluaran',
      maintenanceEfficiency: 'Efisiensi Pemeliharaan',
      topPerformers: 'Berkinerja Terbaik',
      needsAttention: 'Perlu Perhatian',
      fleetHealth: 'Ikhtisar Kesehatan Armada',
      totalScooters: 'Total Skuter',
      revenueVsMaintenance: 'Pendapatan vs Pemeliharaan per Skuter',
      profitTrend: 'Tren Laba per Skuter',
      statusDistribution: 'Distribusi Status',
      exportBasic: 'Ekspor Ringkasan Dasar',
      exportDetailed: 'Ekspor Pengeluaran Detail',
      exportingDetailed: 'Mengekspor pengeluaran detail...',
      exportCompleted: 'Ekspor selesai',
      exportFailed: 'Gagal mengekspor'
    }
  },
  ban: {
    howItWorks: {
      title: 'Carane Nganggo OurScooter (Anggen Staf)',
      subtitle: 'Langkah sane dangan, aturan sane terang, lan tips mawiguna mangda makarya patut tur efisien.',
      categories: 'Kategori',
      whyTitle: 'Ngudiang OurScooter: tetujon lan kawigunan',
      whyDescription: 'OurScooter nulungin staf nglaksanayang operasi sewa skuter sawai-wai sane efisien: kirangan klik, kirangan iwang, lan catetan sane terang.',
      whySpeed: 'Gancang: ngaryanin kontrak sajeroning detik, nganggo malih detail antuk duplikasi.',
      whyClarity: 'Terang: status nyihnayang dija posisi nyabran sewa wantah aji adasa pangaksian.',
      whyControl: 'Kontrol: filter, ngrereh, lan ngentosin tampilan (tabel/kanban) manut tugas.',
      whyAccuracy: 'Patut: panyaga nyegah langkah sane iwang lan sewa sane matumpang tindih.',
      signingInTitle: 'Ngranjing lan ngjaga kantun ngranjing',
      signingInToken: 'Nama pangangge + sandi → server ngverifikasi lan ngwaliang access + refresh token.',
      signingInAutoRefresh: 'Access token mawatesan galah; tiang nyegerang otomatis sayan-sayan nganggo refresh token.',
      signingInRoles: 'Peran: admin lan staf. Konteks organisasi kapilih ring bilah samping.',
      signingInSession: 'Durasi sesi: Ragane kantun ngranjing nyantos 1 rahina tanpa aktivitas. Aplikasi nganggo access token cutet 15 menit lan nyegerang ring kuri layar. Sasampun 1 rahina nenten nganggo aplikasi (utawi yening cookie kakaonin), ragane jagi katunas ngranjing malih.',
      scootersTitle: 'Skuter: nambah lan ngedit',
      scootersOpen: 'Bukak "Add Scooter" anggen ngdaftarang kendaraan anyar. Jaga detail cutet tur patut.',
      scootersRequired: 'Wajib: merek/model, status.',
      scootersOptional: 'Opsional: prabea numbas, detail bahan bakar/baterai, catetan.',
      scootersTip: 'Tips: status "maintenance" ngaonin skuter saking ketersediaan kantos kawaliang.',
      clientsTitle: 'Klien: nambah lan ngedit',
      clientsOpen: 'Bukak "New Client". Catet sane nulungin ragane ngrereh ipun mangkin.',
      clientsRequired: 'Wajib: wasta jangkep.',
      clientsOptional: 'Opsional: telepon, email, KTP/paspor, catetan.',
      contractsTitle: 'Kontrak: ngaryanin lan ngatur',
      contractsWaysTitle: 'Cara ngaryanin kontrak (4 pilihan)',
      contractsFromContracts: 'Saking Contracts: pencet "Add Contract".',
      contractsFromScooters: 'Saking Scooters: bukak skuter → "New Contract".',
      contractsFromClients: 'Saking Clients: bukak klien → "New Contract".',
      contractsDuplicate: 'Duplikat: bukak kontrak sane wenten → "Duplicate" anggen nganggo malih detail aman.',
      contractsRequiredTitle: 'Kolom sane wajib',
      contractsRequiredClient: 'Klien lan Skuter.',
      contractsRequiredDate: 'Tanggal/galah ngawit.',
      contractsRequiredRate: 'Tarif sawai. (Deposit/cara mayah opsional.)',
      contractsStatusTitle: 'Status lan alur kerja (sane kadadosang)',
      contractsStatusFlow: 'Draft → Active → Closed → Archived.',
      contractsStatusActivate: 'Ragane wantah dados ngaktifang yening kolom wajib sampun kaisi lan skuter wenten.',
      contractsStatusClosed: 'Saking Closed ragane wantah dados ngalih ka Archived (nenten dados mawali ka Active).',
      contractsStatusForbidden: 'Nenten kadadosang: kontrak Active sane matumpang tindih anggen skuter sane pateh.',
      contractsStatusArchived: 'Kontrak Archived ical saking tampilan default. Aktifang "Include archived" mangda kacingak.',
      contractsAutoTitle: 'Update status skuter otomatis',
      contractsAutoCreate: 'Ngaryanin kontrak nenten ngubah status skuter (sapunapi status kontrak).',
      contractsAutoActive: 'Yening kontrak dados Active → skuter otomatis dados "rented".',
      contractsAutoClosed: 'Yening kontrak Active ka-Close/Archive → skuter otomatis dados "available".',
      contractsAutoDraft: 'Yening kontrak Active kawaliang ka Draft → skuter otomatis dados "available".',
      contractsAutoPrevent: 'Puniki nyegah pemesanan dobel lan ngjaga ketersediaan skuter tetep patut.',
      contractsTimeTitle: 'Nyacarin galah anggen kontrak Active',
      contractsTimeRemaining: 'Kontrak Active nyihnayang galah sane kantun nyantos tanggal puput (cth. "2h 30m left").',
      contractsTimeOverdue: 'Kontrak sane lintang galah nyihnayang galah sane sampun lintang (cth. "Overdue 1d 5h").',
      contractsTimeColors: 'Warna nyihnayang urgensi: Gadang (4+ jam), Kuning (1-4 jam), Oranye (<1 jam), Barak (lintang galah).',
      contractsTimeKanban: 'Ring tampilan Kanban: wantah timer sane kacingak anggen kontrak Active.',
      contractsTimeList: 'Ring tampilan List: lencana status lan timer kalih kacingak anggen kontrak Active.',
      contractsViewsTitle: 'Kontrak: tampilan',
      contractsViewsTable: 'Tampilan tabel: nyacarin, filter, lan ngekspor baris gancang.',
      contractsViewsKanban: 'Papan Kanban: alur kerja visual manut status (Draft, Active, Closed, Archived).',
      contractsViewsToggle: 'Nganggo tombol alih tampilan ring bilah alat Contracts anggen ngentosin tampilan.',
      contractsKanbanTitle: 'Makarya ring papan Kanban',
      contractsKanbanDrag: 'Seret & lepas kartu anggen ngalihang ka status salanturnyane sane kadadosang.',
      contractsKanbanEdit: 'Bukak kartu lan pencet Edit anggen ngubah detail utawi status langsung.',
      contractsKanbanRules: 'Aturan mamargi: ragane nenten dados ngalih saking Closed mawali ka Active; Archived puput.',
      contractsKanbanBlocked: 'Pamindahan sane nenten wenten kablokir (cth. skuter sampun Active ring genah lianan).',
      contractsKanbanFilters: 'Nganggo filter anggen fokus ring siki klien utawi nyertayang item Archived yening perlu.',
      findingTitle: 'Ngrereh gancang (filter & ngrereh)',
      findingScooters: 'Skuter: filter manut status, merek, model. Nganggo kotak ngrereh anggen cocok teks gancang.',
      findingClients: 'Klien/Pangangge: ngrereh manut wasta/email; setel ukuran kaca mangda ngecingakin baris akehan.',
      findingContracts: 'Kontrak: filter manut status; opsional nyertayang sane kaarsip.',
      rowsTitle: 'Kuda baris sane kasihnayang?',
      rowsScooters: 'Skuter: kamuat manut kaca lan ngambil akehan yening perlu (kantos 100 nyabran muat).',
      rowsClients: 'Klien/Pangangge: ragane dados milih 10/20/30/40/50 baris nyabran kaca.',
      rowsContracts: 'Kontrak: nyihnayang kontrak organisasi mangkin; sertayang sane kaarsip anggen ngalinggahang daftar.',
      themesTitle: 'Tema lan default',
      themesChange: 'Ubah tema (Dark/Light/Blue/Wood) ring bilah samping kiwa beten.',
      themesAdmin: 'Admin dados ngonfigurasi default organisasi ring Management (cth. mata uang, tarif).',
      usersTitle: 'Pangangge & Manajemen (admin)',
      usersStaff: 'Staf: operasi sawai-wai, ngecingak lan ngaryanin kontrak, ngatur skuter/klien.',
      usersAdmin: 'Admin: sami sane dados kalaksanayang staf, tur setelan tingkat organisasi lan ngusap.',
      tipsTitle: 'Tips & ngrereh pikobet',
      tipsActivate: 'Nenten dados ngaktifang? Cek status skuter (patut wenten) lan kolom sane wajib.',
      tipsContract: 'Nenten kacingak kontrak? Wenten kemungkinan kaarsip; aktifang "Include archived".',
      tipsSession: 'Sesi puput? Yening sampun langkungan saking arahina, wantah ngranjing malih.'
    },
    booking: {
      orgNotFound: 'Organisasi nénten kapanggih',
      orgNotFoundDesc: 'Kaca pemesenan sané ruruh ragane nénten wénten. Puniki prasida mateges:',
      orgNotFoundReason1: 'Slug organisasi iwang',
      orgNotFoundReason2: 'Organisasi durung nyiagayang kaca pemesenan',
      orgNotFoundReason3: 'Kaca pemesenan sampun kahapus',
      orgNotFoundOwner: 'Yéning ragane sané nuénang organisasi, pastiang ragane sampun ngatur slug organisasi ring Management → Technical Details.',
      requestReceived: 'Pinunas Katampi!',
      requestReceivedDesc: 'Pinunas pemesenan ragane sampun katampi. Puniki durung pemesenan sané kakonfirmasi.',
      willContact: '{{business}} jagi gelis ngubungin ragane buat ngonfirmasi sewan.',
      requestIdLabel: 'ID Pinunas:',
      contactLabel: 'Kontak:',
      showroomBadge: 'Showroom Skuter',
      bookBadge: 'Pesen Skuter',
      showroomIntro: 'Cingakin skuter tiang, pilih sané kasenengin ragane, raris kirim pinunas pemesenan. Tiang jagi gelis ngonfirmasi sareng ragane.',
      bookIntro: 'Kirim pinunas pemesenan ragane ring sor, tur tiang jagi gelis ngubungin ragane buat konfirmasi.',
      yourDetailsStep: '3. Détail Ragane',
      yourInformation: 'Informasi Ragane',
      fullName: 'Wasta Jangkep',
      fullNamePlaceholder: 'Ketik wasta jangkep',
      email: 'Email',
      emailPlaceholder: 'Ketik alamat email',
      localPhone: 'Nomor Telepon Lokal',
      phonePlaceholder: 'Nomor telepon',
      messengerNumber: 'Nomor Messenger (WhatsApp, msl.)',
      messengerPlaceholder: 'Nomor WhatsApp (opsional)',
      sameAsPhone: 'Pateh sareng telepon',
      lineId: 'LINE ID',
      lineIdPlaceholder: 'LINE ID (opsional)',
      passportNumber: 'Nomor Paspor',
      passportPlaceholder: 'Nomor paspor (opsional)',
      nationality: 'Kewarganegaraan',
      nationalityPlaceholder: 'Kewarganegaraan (opsional)',
      localAddress: 'Alamat Lokal',
      localAddressPlaceholder: 'Alamat lokal ring negara puniki (opsional)',
      rentalDatesStep: '1. Tanggal Sewa',
      bookingDetails: 'Détail Pemesenan',
      startDate: 'Tanggal Ngawit',
      duration: 'Durasi',
      endDate: 'Tanggal Puput',
      dayUnit: 'rahina',
      daysUnit: 'rahina',
      weekUnit: 'minggu',
      weeksUnit: 'minggu',
      monthUnit: 'sasih',
      monthsUnit: 'sasih',
      scooterUnit: 'skuter',
      scootersUnit: 'skuter',
      pricingSummary: 'Ringkesan Harga',
      lineDailyRate: '{{count}} {{unit}} × Tarif Saben Rahina:',
      lineWeeklyRate: '{{count}} {{unit}} × Tarif Saben Minggu:',
      lineDailyFromWeekly: '{{count}} {{unit}} × Tarif Saben Rahina (saking mingguan):',
      lineMonthlyRate: '{{count}} {{unit}} × Tarif Saben Sasih:',
      lineDailyFromMonthly: '{{count}} {{unit}} × Tarif Saben Rahina (saking bulanan):',
      subtotal: 'Subtotal:',
      deposit: 'Deposit:',
      total: 'Total:',
      selectToSeePricingShowroom: 'Pilih tanggal miwah skuter buat ngecingakin harga',
      selectToSeePricingGroup: 'Pilih tanggal miwah grup harga buat ngecingakin harga',
      selectPricingGroup: 'Pilih Grup Harga',
      perDay: '/rahina',
      perWeek: '/minggu',
      perMonth: '/sasih',
      specialPromotions: 'Promosi Khusus:',
      scooterPreference: 'Preferensi Skuter',
      noPreference: 'Nénten wénten preferensi',
      scooterFallback: 'Skuter',
      chooseScooterStep: '2. Pilih Skuter Ragane',
      checkingAvailability: 'Ngecek ketersediaan buat tanggal ragane…',
      availableForDates: '{{available}} saking {{total}} sayaga buat tanggal ragane',
      fleetCount: '{{count}} {{unit}} ring armada tiang',
      lockedScooterNote: 'Skuter puniki sampun kapesen buat ragane malarapan link pemesenan ragane',
      noScootersAvailable: 'Mangkin nénten wénten skuter sané sayaga. Ngiring ngubungin tiang langsung.',
      available: 'Sayaga',
      unavailable: 'Nénten sayaga',
      photosCount: '{{count}} foto',
      viewPhoto: 'Cingak',
      viewPhotosAria: 'Cingakin foto {{title}}',
      additionalNotes: 'Catetan Tambahan',
      notesPlaceholder: 'Pinunas khusus utawi informasi napi ja...',
      charactersCount: '{{count}}/255 karakter',
      preSignTitle: '⚡ Tandatangin Kontrak Dumun (Kasaranin)',
      preSignDesc: 'Gelisang proses ngambil ragane! Nandatangin kontrak dumun ngirit galah rikala ragane rauh.',
      preSignBenefits: 'Tanpa pembayaran • Nénten komitmen • Wantah ngegelisang ngambil',
      completeFieldsToSign: 'Jangkepin sami kolom wajib ring duur buat ngaktifang nandatangin kontrak dumun.',
      missingLabel: 'Durung kaisi:',
      signatureSaved: 'Tandatangan kasimpen: {{name}}',
      submitting: 'Ngirim...',
      submitRequest: 'Kirim Pinunas Pemesenan',
      disclaimer: 'Malarapan ngirim formulir puniki, ragane ngresep mungguing puniki pinunas, nénten pemesenan sané kakonfirmasi. Toko jagi ngubungin ragane buat ngonfirmasi ketersediaan tur muputang pemesenan.',
      selectDatesForPrice: 'Pilih tanggal ragane buat ngecingakin harga',
      depositShort: 'deposit',
      requestToBook: 'Pinunas Mesen',
      continueBtn: 'Lanturang',
      closeBtn: 'Tutup',
      prevPhoto: 'Foto sadurungné',
      nextPhoto: 'Foto salanturné',
      langLabel: 'Basa',
      toastLoadFailed: 'Gagal ngamuat kaca pemesenan',
      toastVersionFailed: 'Gagal ngardi versi kontrak. Ngiring cobain malih.',
      toastPreviewFailed: 'Gagal ngamuat pratinjau kontrak',
      toastEnterName: 'Ngiring ketik wasta jangkep ragane',
      toastEnterEmail: 'Ngiring ketik alamat email ragane',
      toastEnterPhone: 'Ngiring ketik nomor telepon ragane',
      toastSelectStart: 'Ngiring pilih tanggal ngawit',
      toastSelectScooter: 'Ngiring pilih skuter',
      toastSelectPricingGroup: 'Ngiring pilih grup harga',
      toastPricingFailed: 'Nénten prasida ngitung harga',
      toastSubmitFailed: 'Gagal ngirim pinunas pemesenan. Ngiring cobain malih.',
      errFullNameRequired: 'Wasta jangkep wajib kaisi',
      errEmailRequired: 'Email wajib kaisi',
      errEmailInvalid: 'Email nénten valid',
      errPhoneRequired: 'Nomor telepon wajib kaisi',
      mfFullName: 'Wasta Jangkep',
      mfEmail: 'Email',
      mfPhone: 'Nomor Telepon',
      mfStartDate: 'Tanggal Ngawit',
      mfScooter: 'Skuter',
      mfPricingGroup: 'Grup Harga',
      mfPricingInfo: 'Informasi harga'
    },
    theme: {
      dark: 'Peteng',
      light: 'Galang',
      blue: 'Pelung',
      wood: 'Kayu'
    },
    home: {
      quickActions: 'Tindakan Gelis',
      recentActivity: 'Kegiatan Anyar',
      fleetStatusOverview: 'Ringkesan Status Armada',
      welcomeTitle: 'Rahajeng rauh ring OurScooters',
      welcomeSubtitle: 'Dasbor manajemen armada skuter sané jangkep',
      addScooter: 'Skuter Anyar',
      registerScooter: 'Daptarang skuter anyar',
      addClient: 'Klien Anyar',
      registerClient: 'Daptarang pelanggan anyar',
      newContract: 'Kontrak Anyar',
      createAgreement: 'Ngardi perjanjian sewa',
      org: 'Organisasi',
      allOrgs: 'Sami organisasi',
      availableScooters: 'Skuter Sayaga',
      readyForRental: 'Sayaga kasewa',
      rentedScooters: 'Skuter Kasewa',
      currentlyInUse: 'Sedek kaanggén',
      maintenance: 'Pamiara',
      underRepair: 'Sedek kabenahin',
      fleetOverview: 'Ringkesan Armada',
      available: 'sayaga',
      rented: 'kasewa',
      activeRevenue: 'Pikolih Aktif',
      monthlyRecurring: 'Pikolih bulanan',
      totalClients: 'Total Klien',
      activeContracts: 'kontrak aktif',
      avgBattery: 'Rata-rata Baterai',
      fleetBatteryHealth: 'Kesehatan baterai armada',
      avgFuel: 'Rata-rata Bahan Bakar',
      fuel: 'bahan bakar',
      electric: 'listrik',
      fleetROI: 'ROI Armada',
      rev: 'pikolih',
      cost: 'prabéa',
      inMaintenance: 'Sedek pamiara',
      newClientRegistered: 'Klien anyar sampun kadaptar',
      registered: 'kadaptar'
    },
    users: {
      title: 'Pangangge',
      errorLoading: 'Gagal ngamuat pangangge.',
      id: 'ID',
      email: 'Email',
      role: 'Peran',
      username: 'Nama pangangge',
      org: 'Organisasi',
      roleSuperadmin: 'Superadmin',
      roleAdmin: 'Admin',
      roleStaff: 'Staf'
    },
    management: {
      title: 'Manajemen Organisasi',
      errorLoading: 'Iwang ngamuat setélan.',
      timeFormat: 'Format Galah',
      defaultCurrency: 'Mata Uang Baku',
      defaultCountryCode: 'Kode Negara Baku',
      defaultDailyPrice: 'Pangarga Arahina Baku',
      defaultDeposit: 'Deposit Kontrak Baku',
      defaultRentLength: 'Sué Sewa Baku (Rahina)',
      defaultPaymentMethod: 'Cara Pembayaran Baku',
      buttonStyle: 'Gaya Tombol',
      buttonStyleDesc: 'Pilih gaya visual antuk sami tombol ring aplikasi.',
      countryCodeHint: '1 antuk AS, 44 antuk Inggris, msl.',
      countryCodeExplain: 'Wantah angka. Dados awalan nomor telepon ring formulir klien.',
      saving: 'Nyimpen...',
      saveSettings: 'Simpen Setélan',
      saved: 'Sampun kasimpen',
      modernStyle: 'Gaya Modern',
      classicStyle: 'Gaya Klasik',
      originalStyle: 'Gaya Asli',
      btnPrimary: 'Primer',
      btnSecondary: 'Sekunder',
      btnGhost: 'Ghost',
      btnDanger: 'Bahaya',
      modernDesc: 'Latar gradién sareng interaksi alit lan bucu membunder',
      classicDesc: 'Desain resik lan minimal sareng warna manut téma',
      originalDesc: 'Desain tombol asli sané kademenin sadurung refaktor',
      timeFormatExample1: '15 September 2025 6:09 PM',
      timeFormatExample2: 'September 15 2025 6:09 PM',
      timeFormatExample3: '15 September 2025 18:09',
      timeFormatExample4: 'September 15 2025 18:09'
    },
    common: {
      language: 'Basa',
      phoneTooShort: 'Nomor telepon patut minimal 7 digit',
      phoneTooLong: 'Nomor telepon lantang teben (maksimal 15 digit)',
      theme: 'Téma',
      save: 'Simpen',
      cancel: 'Batal',
      logout: 'Medal',
      loading: 'Ngamuat...',
      reset: 'Setél malih',
      close: 'Tutup',
      back: 'Mawali',
      delete: 'Hapus',
      clearAll: 'Hapus Sami',
      upload: 'Unggah',
      uploading: 'Ngunggah...',
      selectPhoto: 'Pilih Foto',
      addNotes: 'Imbuhin cathetan...',
      notes: 'Cathetan',
      optional: 'Opsional',
      retake: 'Jepret malih',
      usePhoto: 'Anggén Foto',
      viewAll: 'Cingak Sami Foto',
      confirmDelete: 'Sampunké yakin jagi ngusap foto puniki?',
      add: 'Imbuhin',
      copied: 'Sampun kasalin',
      expand: 'Buka',
      collapse: 'Tutup',
      updateFailed: 'Pembaruan gagal',
      updateFailedWithMessage: 'Pembaruan gagal: {{message}}',
      failedToSendEmail: 'Gagal ngirim email',
      statusChangeNotAllowed: 'Pauwahan status puniki nénten kadadosang',
      expenseAddedSuccessfully: 'Prabéa sampun kaimbuhin',
      edit: 'Uah'
    },
    toasts: {
      contractCreated: 'Kontrak sampun kakardi',
      contractUpdated: 'Kontrak sampun kaanyarin',
      contractDeleted: 'Kontrak sampun kahapus',
      contractCreateFailed: 'Gagal ngardi kontrak',
      contractUpdateFailed: 'Gagal nganyarin kontrak',
      contractDeleteFailed: 'Gagal ngusap kontrak',
      scooterCreated: 'Skuter sampun kakardi',
      scooterUpdated: 'Skuter sampun kaanyarin',
      scooterDeleted: 'Skuter sampun kahapus',
      scooterStatusUpdated: 'Status skuter sampun kaanyarin',
      scooterStatusUpdateFailed: 'Gagal nganyarin status skuter',
      clientCreated: 'Klien sampun kakardi',
      clientUpdated: 'Klien sampun kaanyarin',
      clientDeleted: 'Klien sampun kahapus',
      settingsUpdated: 'Setélan sampun kaanyarin!',
      scooterStatusChangedAuto: 'Status Skuter Mauwah Otomatis',
      scooterStatusChangeDesc: '{{scooterName}} kasélér saking {{fromStatus}} ka {{toStatus}} {{reason}}.',
      becauseContractActivated: 'santukan kontrak kaaktifang',
      becauseContractDeactivated: 'santukan kontrak katutup/kaarsip',
      automatically: 'otomatis',
      viewScooter: 'Cingak Skuter',
      scooterOpenedFromNotif: 'Mukak détail {{scooterName}}',
      scooterOpenedFromNotifDesc: 'Skuter puniki kabukak otomatis saking notifikasi pauwahan status.',
      closedContractsMoveRestriction: 'Kontrak sané katutup wantah dados kasélér ka Arsip.'
    },
    login: {
      title: 'Ngranjing',
      email: 'Email',
      username: 'Nama pangangge',
      password: 'Sandi',
      signIn: 'Ngranjing',
      signingIn: 'Sedek ngranjing...',
      login: 'Ngranjing',
      loggingIn: 'Sedek ngranjing...',
      loginFailed: 'Gagal ngranjing',
      language: 'Basa'
    },
    sidebar: {
      scooters: 'Skuter',
      clients: 'Klien',
      contracts: 'Kontrak',
      finance: 'Keuangan',
      users: 'Pangangge',
      activity: 'Log Kegiatan',
      management: 'Manajemen',
      howItWorks: 'Carané Makarya',
      signIn: 'Ngranjing',
      expand: 'Buka bilah sisi',
      collapse: 'Tutup bilah sisi',
      logoutConfirm: 'Sampunké yakin jagi medal?'
    },
    toolbar: {
      searchPlaceholder: 'Rereh manut ID, modél, utawi status…',
      clear: 'Hapus',
      listView: 'Tampilan daptar',
      kanbanView: 'Tampilan Kanban',
      calendarView: 'Kalénder',
      gridView: 'Tampilan kisi',
      mapView: 'Tampilan peta',
      filter: 'Filter',
      export: 'Ékspor',
      total: 'Total: {{count}}',
      queryLabel: 'kueri',
      clearQuery: 'Hapus kueri',
      removeFilter: 'Hapus {{filter}}'
    },
    ui: {
      zoom: 'Zoom'
    },
    calendar: {
      week: 'Minggu',
      month: 'Bulan',
      all: 'Sami',
      plusMore: '+{{count}} malih',
      scrollHint: 'Shift+Gulir antuk maosah horizontal',
      hideEmpty: 'Engkebang skuter sané nénten madué kontrak rauh'
    },
    status: {
      available: 'Sayaga',
      rented: 'Kasewa',
      maintenance: 'Pamiara',
      retired: 'Pénsiun',
      draft: 'Draf',
      active: 'Aktif',
      closed: 'Katutup',
      archived: 'Kaarsip'
    },
    clients: {
      id: 'ID Klien',
      name: 'Wasta',
      messenger: 'WhatsApp',
      newClient: 'Klien anyar',
      creating: 'Ngardi klien...',
      fullName: 'Wasta Jangkep',
      fullNamePlaceholder: 'Ketik wasta jangkep',
      email: 'Email',
      emailPlaceholder: 'Ketik alamat email',
      localPhone: 'Nomor Telepon Lokal',
      localPhonePlaceholder: 'Ketik nomor telepon lokal (opsional)',
      messengerNumber: 'Nomor WhatsApp',
      countryCodePlaceholder: '1',
      messengerNumberPlaceholder: 'Ketik nomor WhatsApp (opsional)',
      lineId: 'LINE ID',
      lineIdPlaceholder: 'Ketik LINE ID (opsional)',
      whatsappButton: 'WhatsApp',
      lineButton: 'LINE',
      contactViaWhatsApp: 'Kontak via WhatsApp',
      contactViaLINE: 'Kontak via LINE',
      notes: 'Cathetan',
      notesPlaceholder: 'Cathetan tambahan indik klien puniki (cth. nomor telepon negara lianan)',
      addClient: 'Klien anyar',
      detailsTitle: 'Détail Klien',
      created: 'Kakardi',
      clientId: 'ID Klien',
      edit: 'Uah Klien',
      previousContracts: 'Kontrak sané dumun:',
      noCurrentContracts: 'Nénten wénten kontrak mangkin',
      editClient: 'Uah Klien',
      newContractBtn: 'Kontrak Anyar',
      close: 'Tutup',
      statusHistory: 'Riwayat status',
      unknownClient: 'Klien Nénten Kauningin'
    },
    scooters: {
      id: 'ID Skuter',
      modelRequired: 'Modél patut kaisi',
      batteryRange: 'Baterai patut 0–100',
      odometerNegative: 'Odometer nénten dados negatif',
      fuelRange: 'Bahan bakar % patut 0–100',
      incompatibleEnergy: 'Listrik nénten dados madué bahan bakar; kendaraan bahan bakar nénten dados madué baterai',
      editScooter: 'Uah Skuter',
      newScooter: 'Skuter anyar',
      updating: 'Nganyarin skuter...',
      creating: 'Ngardi skuter...',
      model: 'Modél',
      modelPlaceholder: 'cth. Ninebot Max',
      status: 'Status',
      typeLevel: 'Soroh & Tingkat',
      odometer: 'Odometer',
      qrCode: 'Kode QR',
      qrPlaceholder: 'Kode QR opsional',
      plateNumber: 'Nomor Pelat',
      platePlaceholder: 'Nomor pelat opsional',
      nickname: 'Wasta Paplalian',
      nicknamePlaceholder: 'cth. Honda Barak #1',
      nicknameOptional: 'Wasta paplalian (opsional)',
      brand: 'Mérek',
      brandPlaceholder: 'Mérek opsional',
      vehicleType: 'Soroh kendaraan',
      electric: 'Listrik',
      fuel: 'Bahan bakar',
      batteryPct: 'Baterai %',
      fuelPct: 'Bahan bakar %',
      odometerKm: 'Odometer (km)',
      updateScooter: 'Anyarin skuter',
      addScooter: 'Skuter anyar',
      details: 'Détail',
      edit: 'Uah',
      created: 'Kakardi',
      statusHistory: 'Riwayat status skuter',
      columnScooterId: 'ID Skuter',
      columnBrand: 'Mérek',
      columnModel: 'Modél',
      columnPlateNumber: 'Nomor Pelat',
      columnNickname: 'Wasta Paplalian',
      columnStatus: 'Status',
      columnVehicleStatus: 'Soroh & Tingkat',
      columnOdometer: 'Odometer',
      pictures: 'Gambar',
      noPhotos: 'Durung wénten foto',
      noPhotosYet: 'Durung wénten foto',
      allPhotos: 'Sami Foto',
      viewAllPhotos: 'Cingak Sami Foto',
      damage: 'Karusakan',
      damageOnly: 'Karusakan',
      showingDamage: 'Nyihnayang Karusakan',
      noDamagePhotos: 'Nénten wénten foto karusakan',
      markAsDamage: 'Tandain karusakan',
      markDamage: 'Tandain Karusakan',
      removeDamage: 'Hapus Karusakan',
      markNoDamage: 'Tandain nénten karusakan',
      damageDetected: 'Karusakan kapanggih',
      scooterPhoto: 'Foto skuter',
      selectImageFile: 'Ledang pilih file gambar',
      fileSizeLimit: 'Ageng file patut kirang saking 10MB',
      photoUploaded: 'Foto sampun kaunggah',
      photoDeleted: 'Foto kahapus',
      photoDeleteFailed: 'Gagal ngusap foto',
      photoUpdateFailed: 'Gagal nganyarin foto',
      photoUploadFailed: 'Gagal ngunggah foto',
      photoStorageNotConfigured: 'Panyimpenan foto durung kasetél. Ledang kontak administrator.',
      deletePhoto: 'Hapus foto',
      drawCircle: 'Gambar Bunderan',
      stopDrawing: 'Wusan Nggambar',
      undo: 'Wangsulang',
      redo: 'Ulangin',
      capture: 'Jepret',
      takePhoto: 'Ambil Foto',
      chooseFromGallery: 'Pilih saking Galeri',
      addPhoto: 'Imbuhin Foto',
      photoPreview: 'Pratinjau Foto',
      cameraError: 'Gagal ngakses kamera',
      cameraPermissionDenied: 'Izin kamera katulak'
    },
    pages: {
      manageColumns: 'Atur kolom',
      columns: 'Kolom',
      retiredHidden: 'Skuter pénsiun kaengkebang',
      failedLoadScooters: 'Gagal ngamuat skuter',
      checkConnection: 'Ledang cek koneksi raris coba malih.',
      retry: 'Coba malih',
      clearFilters: 'Hapus filter',
      addScooter: 'Skuter anyar',
      noScootersYet: 'Durung wénten skuter',
      noScootersMatch: 'Nénten wénten skuter sané cocok ring filter',
      addFirstScooter: 'Imbuhin skuter kapertama mangda ngawit.',
      tryAdjusting: 'Coba sesuaikan utawi hapus filter.',
      rowsPerPage: 'Baris saben kaca:',
      pageOf: 'Kaca {{current}} saking {{total}}',
      prevShort: 'Dumun',
      nextShort: 'Salanturnyané',
      prev: 'Kaca dumun',
      next: 'Kaca salanturnyané'
    },
    contractsPage: {
      newContract: 'Kontrak Anyar',
      showArchived: 'Sihnayang kontrak kaarsip',
      newContractSubtitle: 'Gelis ngardi lan ngecék perjanjian sewa anyar',
      createdDate: 'Tanggal Kakardi',
      recentlyUpdated: 'Wawu Kaanyarin',
      recentlyClosed: 'Wawu Katutup',
      allClosed: 'Sami Katutup',
      showAll: 'Sihnayang Sami',
      showRecent: 'Sihnayang Anyar',
      sortBy: 'Urutang manut...',
      archivedOnlyToDraft: 'Kontrak kaarsip wantah dados kasélér ka Draf.'
    },
    activity: {
      title: 'Log Kegiatan',
      subtitle: 'Pantau kegiatan karyawan lan pauwahan sistem. Cingak log sané dangan karesep antuk pemecahan masalah lan audit.',
      filterActivity: 'Filter Kegiatan',
      timePeriod: 'Periode Galah',
      lastHour: 'Jam Pamuput',
      today: 'Dinané Mangkin',
      thisWeek: 'Minggu Puniki',
      thisMonth: 'Bulan Puniki',
      custom: 'Kustom',
      from: 'Saking',
      to: 'Ka',
      employee: 'Karyawan',
      type: 'Soroh Kegiatan',
      allEmployees: 'Sami karyawan',
      allActivities: 'Sami kegiatan',
      scooterMgmt: 'Manajemen Skuter',
      contractMgmt: 'Manajemen Kontrak',
      clientMgmt: 'Manajemen Klien',
      orgSettings: 'Setélan Organisasi',
      searchEverything: 'Rereh Samian',
      searchPlaceholder: 'Rereh wasta, ID, pangarga, status...'
    },
    contracts: {
      filters: 'Filter',
      editContract: 'Uah Kontrak',
      dates: 'Tanggal',
      for: 'antuk',
      scooter: 'Skuter',
      start: 'Ngawit',
      end: 'Wusan',
      duration: 'Sué',
      days: 'rahina',
      weeks: 'minggu',
      months: 'bulan',
      estimatedTotal: 'Perkiraan total',
      fetchingData: 'Ngambil data klien lan skuter',
      scooterCurrentlyRented: 'ℹ️ Skuter puniki sedek kasewa{{until}}.',
      until: 'kantos',
      futureReservationNote: 'Ragané kantun dados ngardi kontrak antuk reservasi benjang - pastikang manten tanggalné nénten tumpang tindih.',
      searchClients: 'Rereh klien manut wasta/email/id',
      noMatches: 'Nénten wénten sané cocok',
      selected: 'Kapilih:',
      scooterQuery: 'Skuter (ID utawi modél madaging)',
      scooterPlaceholder: 'cth. Ninebot utawi UUID',
      dailyRateRange: 'Tarif arahina (min/maks)',
      min: 'Min',
      max: 'Maks',
      minExceedsMax: 'Min nénten dados ngalintangin Maks',
      endAfterStart: 'Galah wusan patut sasampun galah ngawit',
      clientRequired: 'Klien patut kaisi',
      scooterRequired: 'Skuter patut kaisi',
      startTimeRequired: 'Galah ngawit patut kaisi',
      dailyRatePositive: 'Tarif arahina patut > 0',
      addContract: 'Kontrak anyar',
      client: 'Klien',
      selectClient: 'Pilih klien',
      searchScooters: 'Rereh skuter...',
      startTime: 'Galah ngawit',
      status: 'Status',
      dailyRate: 'Tarif arahina',
      deposit: 'Deposit',
      endTime: 'Tanggal Wusan',
      contractId: 'ID Kontrak',
      paymentMethod: 'Cara Pembayaran',
      cash: 'Jinah',
      local_bank_transfer: 'Transfer Bank Lokal',
      international_bank_transfer: 'Transfer Bank Internasional',
      notes: 'Cathetan',
      notesPlaceholder: 'Cathetan opsional indik kontrak puniki',
      createDraftForScooter: 'Ngardi draf kontrak anyar antuk skuter puniki.',
      saveAsDraftHint: 'Simpen dados draf antuk kaaktifang malih',
      startImmediatelyHint: 'Ngawit kontrak gelis',
      creating: 'Ngardi...',
      createDraft: 'Ngardi draf',
      createActive: 'Ngardi aktif',
      day: 'rahina',
      forDays: 'salami {{count}} rahina',
      forDays_plural: 'salami {{count}} rahina',
      total: 'Total:',
      dayOf: 'rahina {{day}} saking {{total}}'
    },
    contractsUi: {
      renting: 'nyewa',
      statusHistory: 'Riwayat status',
      noHistory: 'Nénten wénten riwayat',
      created: 'Kakardi',
      overdue: 'Lambat',
      overdueTitle: 'Kontrak lambat',
      remainingTitle: 'Galah kantun kantos kontrak wusan',
      remaining: 'kantun',
      unitDayShort: 'r',
      unitHourShort: 'j',
      unitMinuteShort: 'm',
      clientDetails: 'Détail Klien',
      scooterDetails: 'Détail Skuter',
      clientNotFound: 'Klien nénten kapanggih',
      scooterNotFound: 'Skuter nénten kapanggih',
      fullName: 'Wasta Jangkep',
      localPhone: 'Telepon Lokal',
      messenger: 'WhatsApp',
      brand: 'Mérek',
      vehicleType: 'Soroh kendaraan',
      battery: 'Baterai',
      qrCode: 'Kode QR',
      scooterStatus: 'Status skuter',
      noStatusChanges: 'Durung wénten pauwahan status',
      loadingHistory: 'Ngamuat riwayat...',
      characters: 'karakter',
      back: 'Mawali',
      startDate: 'Tanggal Ngawit',
      endDate: 'Tanggal Wusan',
      contractId: 'ID Kontrak',
      editContract: 'Uah Kontrak',
      duplicate: 'Duplikat',
      duplicateContract: 'Duplikat kontrak',
      duplicateSubtitle: 'Pilih ngawit, sué, lan wusan antuk draf anyar',
      endTimeMustBeAfter: 'Galah wusan patut sasampun galah ngawit',
      changeStatus: 'Uah status',
      currentStatus: 'mangkin',
      changeScooterStatus: 'Uah status skuter',
      currentScooterStatus: 'mangkin',
      showingRange: 'Nyihnayang {{start}}–{{end}} saking {{total}}',
      contractCreatedTitle: 'Kontrak kakardi',
      contractCreatedMessage: 'Sampunké jagi mukak kontrak sané kakardi?',
      contractLabel: 'Kontrak',
      openContract: 'Bukak Kontrak',
      stayHere: 'Kantun Iriki',
      scooterStatusQuestion: 'Napi status skuter "{{model}}"?',
      currentStatusLabel: 'Status mangkin:',
      statusChangeHelp: 'Puniki jagi ngwantu ragané ngatur kasiagan skuter sasampun pauwahan status kontrak.',
      working: 'Sedek nggarap…',
      moveContractAndUpdate: 'Sélér Kontrak & Anyarin Skuter',
      enterToConfirm: 'Enter antuk konfirmasi · Esc antuk batal',
      statusAvailable: 'Sayaga',
      statusAvailableDesc: 'Skuter sayaga kasewa',
      statusRented: 'Kasewa',
      statusRentedDesc: 'Skuter sedek kasewa',
      statusMaintenance: 'Pamiara',
      statusMaintenanceDesc: 'Skuter merluang pamiara',
      statusRetired: 'Pénsiun',
      statusRetiredDesc: 'Skuter nénten malih beroperasi',
      statusCurrent: 'Mangkin',
      moveContractTitle: 'Sélér Kontrak',
      moveContractMessage: 'Ledang pilih status anyar antuk skuter rikala nyélér kontrak puniki.',
      fromLabel: 'Saking',
      toLabel: 'Ka',
      clientLabel: 'Klien',
      scooterLabel: 'Skuter',
      currentContract: 'Kontrak mangkin:',
      viewContract: 'Cingak Kontrak'
    },
    contractDocument: {
      print: 'Cetak',
      saveAsPDF: 'Simpen dados PDF',
      email: 'Email',
      addSignature: 'Imbuhin Tanda Tangan',
      emailRequired: 'Alamat email patut kaisi',
      emailSent: 'Kontrak sampun kakirim',
      enterSignatureName: 'Ketik wasta sané nandatangin mangda kaimbuhin ring dokumén',
      loading: 'Ngamuat kontrak...',
      preparingPDF: 'Nyiagayang PDF...',
      downloadSuccess: 'PDF sampun kaunduh',
      downloadFailed: 'Gagal ngardi PDF',
      retry: 'Coba malih',
      emailContract: 'Email Kontrak',
      recipientEmail: 'Email Panampi',
      sending: 'Ngirim...',
      send: 'Kirim'
    },
    businessDetails: {
      title: 'Détail Usaha',
      settingsUpdated: 'Détail usaha sampun kaanyarin',
      errorLoading: 'Iwang ngamuat détail usaha.',
      helpText: 'Isinin informasi usaha ragané ring sor. Détail puniki jagi medal ring kontrak sewa. Tinggalang kosong yéning ragané demen ngisinin antuk tangan ring kontrak cetak.',
      businessName: 'Wasta Usaha',
      businessNamePlaceholder: 'Ketik wasta usaha ragané',
      businessAddress: 'Alamat Usaha',
      businessAddressPlaceholder: 'Ketik alamat usaha ragané',
      businessPhone: 'Telepon Usaha',
      businessPhonePlaceholder: '+66 2 123 4567',
      businessEmail: 'Email Usaha',
      businessEmailPlaceholder: 'cth. business@example.com',
      taxId: 'NPWP / Registrasi Usaha',
      taxIdPlaceholder: '1234567890123',
      representativeName: 'Wasta Wakil Resmi',
      representativeNamePlaceholder: 'John Smith',
      representativeIdNumber: 'Nomor ID Wakil',
      representativeIdNumberPlaceholder: '1234567890123',
      saving: 'Nyimpen...',
      saveDetails: 'Simpen Détail Usaha',
      saved: 'Sampun kasimpen'
    },
    messaging: {
      title: 'Kontak Klien',
      scanQR: 'Pindai Kode QR',
      orClickLink: 'Utawi klik tautan ring sor',
      openApp: 'Bukak Aplikasi Pesen',
      selectTemplate: 'Pilih Template Pesen',
      customMessage: 'Pesen Kustom',
      messageEditor: 'Editor Pesen',
      thankYou: 'Suksma',
      reminder: 'Pangéling',
      payment: 'Pangéling Pembayaran',
      templateThankYou: 'Suksma sampun milih {{businessName}}! Dumogi ragané seneng nyewa skuter. Kantos kapanggih malih!',
      templateReminder: 'Om swastiastu {{clientName}}! Puniki {{businessName}}. Pangéling: Masa sewa ragané wusan benjang. Ledang wangsulang skuter.',
      templatePayment: 'Om swastiastu {{clientName}}! Puniki {{businessName}}. Pangéling pembayaran antuk kontrak #{{contractId}}.',
      noWhatsApp: 'Nénten wénten nomor WhatsApp',
      noLINE: 'Nénten wénten LINE ID',
      close: 'Tutup'
    },
    finance: {
      title: 'Ringkesan Keuangan',
      exportCSV: 'Ékspor CSV',
      timeRange: 'Rentang Galah',
      day: 'Rahina',
      week: 'Minggu',
      month: 'Bulan',
      year: 'Warsa',
      all: 'Sami',
      searchPlaceholder: 'Rereh skuter...',
      showSoldOnly: 'Sihnayang sané sampun kaadol manten',
      totalPurchase: 'Total Tumbas',
      totalMaintenance: 'Total Pamiara',
      totalIncome: 'Total Pikolih',
      netProfit: 'Bati Bersih',
      topROIScooter: 'Skuter ROI Pinih Becik',
      bestBrandModel: 'Mérek/Modél Pinih Becik',
      scooters: 'skuter',
      scooter: 'Skuter',
      purchase: 'Tumbas',
      maintenance: 'Pamiara',
      income: 'Pikolih',
      sale: 'Adol',
      net: 'Bersih',
      roi: 'ROI',
      status: 'Status',
      profitable: 'Maguna',
      breakingEven: 'Impas',
      lossMaker: 'Rugi',
      totals: 'Total',
      recentExpenses: 'Prabéa Anyar',
      noExpenses: 'Nénten wénten prabéa kacathet',
      loadingExpenses: 'Ngamuat prabéa...',
      noExpensesInRange: 'Nénten wénten prabéa ring rentang galah sané kapilih',
      expenseTotal: 'Total',
      categoryOilChange: 'Ganti Oli',
      categoryTires: 'Ban',
      categoryBrakes: 'Rem',
      categoryEngine: 'Mesin',
      categoryBattery: 'Baterai',
      categoryBodyParts: 'Bodi',
      categoryCleaning: 'Pereresik',
      categoryRegistration: 'Registrasi',
      categoryInsurance: 'Asuransi',
      categoryMiscParts: 'Suku Cadang Lianan',
      categoryCustom: 'Kustom',
      affectsExpenseDetails: 'mangaruhin détail prabéa',
      tableView: 'Tabel',
      chartsView: 'Grafik',
      toggleKPIs: 'Alihang KPI',
      toggleInsights: 'Alihang Wawasan',
      toggleAdditionalTables: 'Alihang Tabel Tambahan',
      showOperational: 'Sihnayang tampilan operasional',
      showFull: 'Sihnayang tampilan jangkep',
      operationalView: 'Tampilan operasional',
      fullView: 'Tampilan jangkep',
      operationalNet: 'Bersih operasional',
      date: 'Tanggal',
      category: 'Kategori',
      amount: 'Jumlah',
      note: 'Cathetan',
      actions: 'Tindakan',
      edit: 'Uah',
      delete: 'Hapus',
      editExpense: 'Uah Prabéa',
      customLabel: 'Label kustom',
      confirmDeleteExpense: 'Sampunké yakin jagi ngusap prabéa puniki?',
      expenseDeleted: 'Prabéa kahapus',
      deleteFailed: 'Gagal ngusap prabéa',
      expenseUpdated: 'Prabéa kaanyarin',
      updateFailed: 'Gagal nganyarin prabéa',
      expenseCreated: 'Prabéa kakardi',
      addExpense: 'Imbuhin Prabéa',
      brandModelPerformance: 'Kinerja Mérek/Modél',
      brand: 'Mérek',
      model: 'Modél',
      count: 'Cacah',
      avgROI: 'ROI Rata-rata',
      topExpenses: 'Prabéa Pinih Ageng manut Kategori',
      noData: 'Nénten wénten data',
      monthlyTrends: 'Tren Keuangan Bulanan',
      expenseBreakdown: 'Rincian Kategori Prabéa',
      maintenanceEfficiency: 'Efisiensi Pamiara',
      topPerformers: 'Pinih Becik',
      needsAttention: 'Merluang Uratian',
      fleetHealth: 'Ringkesan Kesehatan Armada',
      totalScooters: 'Total Skuter',
      revenueVsMaintenance: 'Pikolih vs Pamiara saben Skuter',
      profitTrend: 'Tren Bati saben Skuter',
      statusDistribution: 'Distribusi Status',
      exportBasic: 'Ékspor Ringkesan Dasar',
      exportDetailed: 'Ékspor Prabéa Détail',
      exportingDetailed: 'Ngékspor prabéa détail...',
      exportCompleted: 'Ékspor wusan',
      exportFailed: 'Gagal ngékspor'
    }
  }
} as const;

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: savedLang,
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
    defaultNS: 'common',
    ns: ['common', 'sidebar', 'toolbar', 'status', 'clients', 'scooters', 'contracts', 'contractsUi', 'pages', 'contractsPage', 'activity', 'home', 'users', 'management', 'login', 'toasts', 'businessDetails', 'messaging', 'finance', 'landing', 'booking', 'howItWorks'],
    returnNull: false,
  });

// Expose i18next on window for components that access via window.i18next
if (typeof window !== 'undefined') {
  (window as any).i18next = i18n;
}

// Expose a helper to switch language and persist
export function setLanguage(lang: LocaleCode) {
  i18n.changeLanguage(lang);
  try { localStorage.setItem(STORAGE_KEY, lang); } catch {}
}

export default i18n;


